import currency from 'currency.js'
import { date, Dialog, Notify } from 'quasar'
import { isAdmin, isConsumer, isKiosk, isPos } from 'src/config'
import Generic from 'src/models/generic/Generic'
const { formatDate, addToDate } = date
import Activity from 'src/models/activity/Activity'
import ActivityBooking, { BOOKING_STATES, formatDateForActivityBookingConfig } from 'src/models/activity/ActivityBooking'
import ActivityTicket from 'src/models/activity-ticket/ActivityTicket'
import { TYPES as BOOKING_FEE_TYPES } from 'src/models/booking-fee-type/BookingFeeType'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import Discount, { LIMIT_BY_QUANTITY_FILTERS, LIMIT_BY_QUANTITY_SCOPES, LIMIT_BY_QUANTITY_TYPES } from 'src/models/discount/Discount'
import GiftCard from 'src/models/gift-card/GiftCard'
import { SALES_ITEM_RESTRICTION_TYPES } from 'src/models/inventory-strategy/InventoryStrategy'
import { MEMBERSHIP_DISCOUNT_CONDITIONS, MEMBERSHIP_DISCOUNT_RENEWAL_TERMS } from 'src/models/membership-type/MembershipDiscount'
import { CONDITION_REQUIREMENTS, MEMBERSHIP_VOUCHER_CONDITIONS } from 'src/models/membership-type/MembershipVoucher'
import Model, { encoders, executeMutation, ID, IsoDateTime, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import { canSelectTicketTypeOrPackage } from 'src/models/order/CheckoutData'
import ItemOrderItemModifier from 'src/models/order-item/ItemOrderItemModifier'
import OrderItem, { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import { getTicketTypesFromItemPackage } from 'src/models/package/ItemPackage'
import Payment, { PAYMENT_STATES, PAYMENT_TYPES } from 'src/models/payment/Payment'
import RegisterStation from 'src/models/register/RegisterStation'
import { APPLICATION_TYPES } from 'src/models/register/RegisterType'
import TimeSlot from 'src/models/schedule/TimeSlot'
import Showing from 'src/models/showing/Showing'
import { COUNTER_PICKUP_IDENTIFIER_OPTIONS } from 'src/models/site/Site'
import Ticket from 'src/models/ticket/Ticket'
import User from 'src/models/user/User'
import Voucher from 'src/models/voucher/Voucher'
import { VOUCHER_TYPE_TYPES } from 'src/models/voucher/VoucherType'
import orderBookingFeeComputer from 'src/services/booking-fees/BookingFeesOrderComputer'
import { isOutOfStock } from 'src/services/inventory/sales-restrictions'
import orderDeliveryOptionsComputer from 'src/services/item-delivery/OrderDeliveryOptionsComputer'
import { validateQuantityLimits } from 'src/utils/redemption-validators'
import { CONSUMER_MESSAGE_TYPES, PRODUCER_MESSAGE_TYPES } from 'src/workers/packageProcessor.worker'
// import { nextTick } from 'vue'

export function getAncestorOrder (model) {
  // Step up through parents until we find a parent that is an order,
  // or we reach the root (parent is nil)
  if (_.isNil(model.options.parent) || model.options?.parent?.typeName === 'Order') {
    return model.options.parent
  } else {
    return getAncestorOrder(model.options.parent)
  }
}

function roundToCents (value) {
  return Math.round(value * 100) / 100
}

const allDeliveryTypes = DELIVERY_TYPE_OPTIONS.COMPLEXITY_ASCENDING

export const EXCHANGE_FEE_TYPES = {
  ORDER: 'order',
  TICKET: 'ticket'
}

export const ORDER_PLACE_TYPES = {
  FULL_ORDER: 'full',
  TAB_ORDER: 'tab',
  TAB_ORDER_AND_PAYMENTS: 'tab_process_payments',
  PREP_ORDER: 'prep'
}

export const SECURE_FIELDS = ['orderItems', 'payments']

export const ORDER_CHECKOUT_DEFAULT_OPTIONS = {
  orderItems: {
    itemOrderItem: {
      item: true,
      itemVariation: true,
      itemModifiers: {
        modifier: true
      }
    },
    ticket: {
      fields: 'all',
      user: {
        membershipTier: 'default',
        hasEffectiveMembership: true
      },
      showing: {
        movie: 'default',
        showingBadges: {
          fields: ['id']
        }
      }
    },
    activityTicket: {
      user: true,
      activitySession: {
        activity: true,
        activitySpace: true,
        activitySchedule: true
      }
    },
    voucherPurchase: {
      user: true,
      voucherType: true,
      card: 'default'
    },
    issuedVoucherPackage: {
      issuedVoucherPackageVouchers: 'default',
      vouchers: {
        voucherType: {
          fields: 'all',
          itemVariation: true
        }
      },
      issuedVoucherPackages: {
        issuedVoucherPackageVouchers: 'default',
        vouchers: {
          voucherType: {
            fields: 'all',
            itemVariation: true
          }
        }
      }
    },
    membershipInterval: 'default',
    tax: true,
    discount: 'default',
    voucher: {
      voucherType: true
    },
    voucherType: true,
    giftCard: 'default',
    bookingFeeType: 'default',

    orderItemBookingFee: {
      bookingFeeType: 'default'
    }
  },
  payments: {
    giftCard: 'default'
  },
  activityBookings: {
    activity: {
      activityCategories: 'default',
      ticketTypes: 'default',
      itemPackages: 'default',
      fromPrices: true,
      maxActivitySpaceCapacity: true
    },
    activitySession: {
      activitySpace: true
    }
  },
  appliedRewards: true,
  user: {
    userClass: true,
    seatPreferences: true,
    giftCards: true,
    userStripeCustomers: 'default',
    membership: {
      members: {
        userClass: true,
        membershipType: {
          membershipVouchers: {
            voucherType: true
          },
          membershipDiscounts: {
            discount: true
          }
        },
        membershipTier: true,
        vouchers: {
          voucherType: true
        },
        redemptionsByShowing: true,
        redemptionsByActivitySession: true
      },
      owner: {
        fields: ['id']
      }
    },
    membershipType: {
      membershipVouchers: {
        voucherType: true
      },
      membershipDiscounts: {
        discount: true
      }
    },
    membershipTier: true,
    vouchers: {
      voucherType: true
    },
    credit: 'default',
    purchasees: {
      userClass: true,
      membershipType: {
        membershipVouchers: {
          voucherType: true
        },
        membershipDiscounts: {
          discount: true
        }
      },
      membershipTier: true,
      membership: {
        id: true,
        state: true
      },
      vouchers: {
        voucherType: true
      },
      redemptionsByShowing: true,
      redemptionsByActivitySession: true
    },
    discountRedemptions: true,
    redemptionsByShowing: true,
    redemptionsByActivitySession: true
  },
  independentVouchers: {
    voucherType: true
  },
  registerStation: {
    registerType: {
      location: {
        locationGroup: true
      }
    }
  },
  stripePaymentIntentClientSecret: true,
  watchQuery: false
}

export const ORDER_ACTIONS = {
  VOID: 'void',
  START_SWAP: 'start_swap',
  COLLECT_ORDER: 'collect_order',
  PREPARE_ORDER: 'prepare_order',
  REVERT_PREPARE_ORDER: 'revert_prepare_order',
  DELIVER_ORDER: 'deliver_order',
  REVERT_DELIVER_ORDER: 'revert_deliver_order',
  ADMIT_TICKETS: 'admit_tickets',
  EXCUSE_TICKETS: 'excuse_tickets',
  PLACE_TAB_ORDER: 'place_tab_order',
  PLACE_TAB_ORDER_PROCESS_PAYMENTS: 'place_tab_order_process_payments',
  PLACE_PREP_ORDER: 'place_prep_order',
  PLACE_ORDER: 'place_order',
  CLAIM_REFUND: 'claim_refund',
  CANCEL_ORDER: 'cancel_order',
  ABANDON_ORDER: 'abandon_order',
  OPEN_TAB: 'open_tab',
  CLOSE_TAB: 'close_tab',
  CREATE_TERMINAL_PAYMENT_INTENT: 'create_terminal_payment_intent',
  CREATE_TERMINAL_PRE_AUTH_PAYMENT_INTENT: 'create_terminal_pre_auth_payment_intent',
  CREATE_PAYMENT_INTENT: 'create_payment_intent',
  RUN_FULL_AUDIT_CHECKS: 'run_full_audit_checks',
  CREATE_TERMINAL_SETUP_INTENT: 'create_terminal_setup_intent',
  CREATE_SETUP_INTENT: 'create_setup_intent',
  UPDATE_SAVED_PAYMENT_METHOD_FROM_STRIPE: 'update_saved_payment_method_from_stripe',
  REMOVE_SAVED_PAYMENT_METHOD: 'remove_saved_payment_method',
  ATTACH_PAYMENT_METHOD: 'attach_payment_method',
  PAYMENT_CONNECTOR_CANCEL_TRANSACTION: 'payment_connector_cancel_transaction',
  PAYMENT_CONNECTOR_START_TRANSACTION: 'payment_connector_start_transaction'
}

export const ORDER_STATES = {
  DRAFT: 'draft',
  OPEN_TAB: 'open_tab',
  PENDING: 'pending',
  COMPLETED: 'completed',
  VOIDED: 'voided',
  PARTIALLY_VOIDED: 'partially_voided',
  CANCELED: 'canceled',
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'partially_refunded'
}

export const TAB_TRANSFER_ACTIONS = {
  TRANSFER: 'transfer',
  REJECT: 'reject',
  ACCEPT: 'accept',
  CANCEL: 'cancel',
  REQUEST: 'request'
}

ORDER_STATES.ALL = _.values(ORDER_STATES)

ORDER_STATES.ALL_VOIDED_STATES = [ORDER_STATES.VOIDED, ORDER_STATES.PARTIALLY_VOIDED, ORDER_STATES.REFUNDED, ORDER_STATES.PARTIALLY_REFUNDED, ORDER_STATES.OPEN_TAB, ORDER_STATES.CANCELED]

export const ORDER_NEEDS = {
  START_OR_ADD: 'Start Or Add',
  SELECT_SEATS: 'Select Seats',
  DELIVERY_INFORMATION: 'Delivery Information',
  TIP: 'Tip',
  PAYMENT: 'Payment',
  TAB_INFO: 'Tab Info',
  WAITLIST_CONFIRMATION: 'Waitlist Confirmation',
  PROMPT_FOR_CREDIT_CARD: 'Prompt for Credit Card',
  QUICKCASH: 'QuickCash',
  CREDIT: 'Credit',
  CASH_SESSION: 'Cash Session',
  MODIFIER_SELECTION: 'Modifier Selection',
  SMS_RECEIPT: 'SMS Receipt',
  EMAIL_RECEIPT: 'Email Receipt'
}
export default class Order extends Model {
  static name = 'Order'

  static versioned = true

  static level = MODEL_LEVEL.SITE

  // Temporary solution to prevent merging changes when fetching an order
  // When fetching a new order we set the ID, so we need to ignore this - as otherwise it will be merged
  // Merging the ID could also have bad side effects, e.g. If we merge a local id, it would then be a new order
  static mergeFieldsToOmit = ['id']

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Customer]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },

      siteId: {
        type: ID,
        output: true
      },

      state: {
        type: String,
        output: true,
        default: 'draft',
        sortable: true,
        options: ['draft', 'open_tab', 'pending', 'completed', 'voided', 'partially_voided', 'canceled']
      },

      hasIssue: {
        type: Boolean,
        output: true,
        sortable: true
      },
      auditChecks: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      },
      availableCorrections: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      },
      comments: {
        type: String,
        default: null
      },
      notes: {
        type: String,
        excludeFromDefault: true
      },

      user: {
        type: User,
        encode: encoders.hasOne,
        reference: true,
        default: null
      },
      userId: {
        type: ID,
        output: true
      },
      orderUserAccountClaimed: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },

      employee: {
        type: User,
        default: null,
        reference: true,
        output: true
      },
      incomingEmployee: {
        type: User,
        default: null,
        reference: true,
        encode: encoders.hasOne
      },

      tabTransferType: {
        type: String,
        default: 'outgoing',
        local: true,
        output: true
      },

      registerStation: {
        type: RegisterStation,
        default: null,
        reference: true,
        output: true
      },

      orderItems: {
        type: [OrderItem],
        default: () => ([]),
        allowDestroy: true
      },
      activityBookings: {
        type: [ActivityBooking],
        default: () => ([]),
        allowDestroy: true
      },
      payments: {
        type: [Payment],
        allowDestroy: true
      },
      appliedRewards: {
        type: [Voucher],
        local: true,
        default: () => ([])
      },
      usualOrderAdded: {
        type: Boolean,
        default: false,
        local: true
      },
      tabInformationConfirmed: {
        type: Boolean,
        default: false
      },
      startOrAddPrompted: {
        type: Boolean,
        default: false,
        local: true
      },
      signatureImage: {
        type: String,
        default: null
      },
      stripePaymentAmount: {
        type: Number,
        encode: encoders.cents,
        input: true,
        default: 0
      },
      paymentConnectorAmount: {
        type: Number,
        encode: encoders.cents,
        input: true,
        default: 0
      },
      paymentConnectorTransactionOptions: {
        type: JsonObject,
        input: true,
        default: () => ({})
      },
      fullAmount: {
        type: Boolean,
        input: true,
        default: true,
        excludeFromDefault: true
      },
      stripePaymentIntentClientSecret: {
        type: String,
        default: null,
        output: true,
        excludeFromDefault: true
      },

      savedPaymentMethodId: {
        type: String,
        output: true
      },
      savedPaymentMethodFingerprint: {
        type: String,
        output: true
      },
      savedPaymentMethodBrand: {
        type: String,
        output: true
      },
      savedPaymentMethodLast4: {
        type: String,
        output: true
      },
      attachPaymentMethodId: {
        type: String,
        input: true
      },
      stripeSetupIntentClientSecret: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      remoteOrder: {
        type: Boolean,
        default: false,
        local: true
      },
      startedViaQrCode: {
        type: Boolean,
        default: false
      },
      deliveryType: {
        type: String,
        default: null
      },
      deliveryTypeSetManually: {
        type: Boolean,
        default: false,
        local: true
      },
      deliverReadyToServeItems: {
        type: Boolean,
        default: false
      },
      deliverFastPrepItems: {
        type: Boolean,
        default: false
      },
      hasDueTime: {
        type: Boolean,
        default: false
      },
      dueTime: {
        type: IsoDateTime,
        default: null
      },

      deliveryEmailAddress: {
        type: String,
        default: null
      },
      deliveryPhone: {
        type: String,
        default: null
      },
      deliveryPhoneCountryCode: {
        type: String,
        default: null
      },
      deliveryFirstName: {
        type: String,
        default: null
      },
      deliveryLastName: {
        type: String,
        default: null
      },
      incompleteSwap: {
        type: Boolean,
        default: false,
        output: true
      },
      identifyWithDeliveryInfo: {
        type: Boolean,
        default: false
      },
      nameForOrder: {
        type: String,
        default: null,
        encode (data) {
          if (this.identifyWithDeliveryInfo) {
            data.nameForOrder = `${this.deliveryLocation ?? ''} - ${this.deliveryPositions?.join(', ') ?? ''}`
            return
          }

          data.nameForOrder = this.data.nameForOrder

          if (!isConsumer) return

          _.find(this.configuration.currentSite.counterPickupIdentifierPriorityForRemoteOrders, identifier => {
            if (identifier === COUNTER_PICKUP_IDENTIFIER_OPTIONS.USER_NAME && this.user?.firstName && this.user?.lastName) {
              data.nameForOrder = `${this.user?.firstName ?? ''} ${this.user?.lastName ?? ''}`.trim()
              return true
            } else if (identifier === COUNTER_PICKUP_IDENTIFIER_OPTIONS.SEAT_NAME && this.deliveryLocation) {
              data.nameForOrder = _.compact([this.deliveryLocation, this.deliveryPositions?.join(', ')]).join(' - ').trim()
              return true
            } else if (identifier === COUNTER_PICKUP_IDENTIFIER_OPTIONS.DELIVERY_NAME && this.deliveryFirstName && this.deliveryLastName) {
              data.nameForOrder = `${this.deliveryFirstName ?? ''} ${this.deliveryLastName ?? ''}`.trim()
              return true
            } else if (identifier === COUNTER_PICKUP_IDENTIFIER_OPTIONS.CUSTOM && !!this.data.nameForOrder) {
              data.nameForOrder = this.data.nameForOrder
              return true
            }
          })
        }
      },
      pagerNumber: {
        type: String,
        default: null
      },
      deliveryDetails: {
        type: String,
        default: null
      },
      deliveryLocation: {
        type: String,
        default: null,
        sortable: true
      },
      deliveryLocationSetManually: {
        type: Boolean,
        default: false,
        local: true
      },
      deliveryPositions: {
        type: JsonArray,
        default: []
      },
      deliveryOwnerPosition: {
        type: String,
        default: null
      },
      deliveryPositionsSetManually: {
        type: Boolean,
        default: false,
        local: true
      },
      tableName: {
        type: String,
        default: null
      },
      numberOfGuests: {
        type: Number,
        default: null
      },
      sendSmsReceipt: {
        type: Boolean,
        default: false
      },
      sendSmsReceiptSkipped: {
        type: Boolean,
        default: false,
        local: true
      },
      sendEmailReceiptSkipped: {
        type: Boolean,
        default: false,
        local: true
      },

      createdAt: {
        type: IsoDateTime,
        output: true,
        sortable: true
      },

      updatedAt: {
        type: IsoDateTime,
        output: true,
        sortable: true
      },

      remoteOrderApplicationName: {
        type: String,
        output: true
      },

      receiptHtml: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      invoiceHtml: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      customTemplateHtml: {
        type: String,
        output: true,
        excludeFromDefault: true
      },

      action: {
        type: String,
        default: null,
        input: true
      },

      voidOrderItemIds: {
        type: [ID],
        input: true
      },

      collectOrderItemIds: {
        type: [ID],
        input: true
      },

      admitTicketIds: {
        type: [ID],
        input: true
      },
      excuseTicketIds: {
        type: [ID],
        input: true
      },

      printReceipt: {
        type: Boolean,
        default: false,
        input: true
      },
      emailReceipt: {
        type: Boolean,
        default: false,
        input: true
      },

      mergeTabOrderId: {
        type: ID,
        default: null,
        input: true
      },

      independentVouchers: {
        type: [Voucher],
        local: true,
        default: () => ([])
      },

      independentDiscounts: {
        type: [Discount],
        local: true,
        default: () => ([])
      },

      giftCards: {
        type: [GiftCard],
        local: true,
        default: () => ([])
      },

      tipCollected: {
        type: Boolean,
        local: true,
        default: false
      },
      lastTipAmount: {
        type: Number,
        default: 0,
        excludeFromDefault: true,
        output: true
      },
      tipAdjustment: {
        type: Number,
        default: 0,
        local: true
      },
      operativeOrderItemTotal: {
        type: Number,
        default: 0,
        ouput: true,
        excludeFromDefault: true
      },

      taxExempt: {
        type: Boolean,
        default: false
      },

      acknowledgedTermsOfService: {
        type: Boolean,
        default: false
      },

      phoneOrder: {
        type: Boolean,
        default: false
      },
      invoicedOrder: {
        type: Boolean,
        default: false
      },
      recaptchaToken: {
        type: String,
        default: null,
        input: true
      },
      groupTicket: {
        type: Boolean,
        default: false,
        input: true
      },

      manualTheaterSeatName: {
        type: String,
        default: null,
        local: true
      },
      computedRemainingOwed: {
        type: Number,
        output: true,
        excludeFromDefault: true
      },
      computedPreAuthAmount: {
        type: Number,
        output: true,
        excludeFromDefault: true
      },

      currentShowing: {
        type: Showing,
        default: null,
        local: true
      },

      currentActivity: {
        type: Activity,
        default: null,
        local: true
      },

      transient: {
        type: Boolean,
        default: false,
        local: true
      },
      availableLimitRestrictedDiscounts: {
        type: [Discount],
        default: () => ([]),
        local: true
      },
      currentMembershipDiscounts: {
        type: [Discount],
        default: () => ([]),
        local: true
      },
      requestingTip: {
        type: Boolean,
        default: false,
        local: true
      },
      fetchedByQrDeliveryDetails: {
        type: Boolean,
        default: false,
        local: true
      },
      previousDraftOrderItemValidationMessages: {
        type: Array,
        default: {},
        local: true
      },
      preventOrderItemValidationsDialog: {
        type: Boolean,
        default: false,
        local: true
      },
      accountingDatesLocked: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      orderUserFirstName: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      orderUserLastName: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      orderUserDefaultPaymentMethodId: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      selected: {
        type: Boolean,
        local: true,
        default: true
      },
      packageCounter: {
        type: Number,
        default: 0,
        local: true
      },
      generatedOrderItems: {
        type: Array,
        default: () => ([]),
        local: true,
        excludeFromClone: true
      },
      concessionOrderTargetShowing: {
        type: Showing,
        default: null,
        local: true
      }
    }

    this.secureFieldsNames = _.compact(_.map(this.fields, (data, key) => {
      if (SECURE_FIELDS.includes(key)) return null
      if (data.reference || data.excludeFromDefault === true) return null
      return key
    }))

    this.computed = {
      anonymous () { return (isConsumer || isKiosk || this.phoneOrder) && !this.user },
      draft () { return this.state === 'draft' },
      openTab () { return this.state === 'open_tab' },
      completed () { return this.state === 'completed' },
      price () {
        let price = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          price = price.add(orderItem.price)
        })
        return price.value
      },
      discount () {
        let discount = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          discount = discount.add(orderItem.discountAmount)
        })
        return discount.value
      },
      taxAmountsByTax () {
        const taxAmountsByTax = {}

        _.each(this.operativeOrderItems, operativeOrderItem => {
          _.each(_.values(operativeOrderItem.simpleOrderItemTaxesById), simpleOrderItemTax => {
            if (taxAmountsByTax[simpleOrderItemTax.tax_id]) {
              taxAmountsByTax[simpleOrderItemTax.tax_id].tax_amount = currency(taxAmountsByTax[simpleOrderItemTax.tax_id].tax_amount).add(simpleOrderItemTax.tax_amount).value
            } else {
              taxAmountsByTax[simpleOrderItemTax.tax_id] = {
                tax_id: simpleOrderItemTax.tax_id,
                tax_name: simpleOrderItemTax.tax_name,
                tax_amount: simpleOrderItemTax.tax_amount
              }
            }
          })
        })

        return _.values(taxAmountsByTax)
      },
      taxAmount () {
        let taxAmount = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          taxAmount = taxAmount.add(orderItem.taxAmount)
        })
        return taxAmount.value
      },
      subtotal () {
        let subtotal = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          subtotal = subtotal.add(orderItem.subtotal)
        })
        return subtotal.value
      },
      total () {
        let total = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          total = total.add(orderItem.total)
        })
        return total.value
      },
      displayPrice () {
        let displayPrice = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          displayPrice = displayPrice.add(orderItem.displayPrice)
        })
        return displayPrice.value
      },
      displaySubtotalPayable () {
        let displaySubtotalPayable = currency(this.subtotal)
        _.each(this.operativeOrderItems, orderItem => {
          if (orderItem.type === ORDER_ITEM_TYPES.BOOKING_FEE) {
            displaySubtotalPayable = displaySubtotalPayable.subtract(orderItem.subtotal)
          } else if (orderItem.type === ORDER_ITEM_TYPES.TICKET && !_.isNil(orderItem.localBookingFeeType)) {
            displaySubtotalPayable = displaySubtotalPayable.subtract(orderItem.bookingFeeAmount)
          }
        })
        return displaySubtotalPayable.value
      },
      displaySubtotal () {
        let displaySubtotal = currency(this.displaySubtotalPayable)
        displaySubtotal = displaySubtotal.add(this.appliedDiscountAmount)
        displaySubtotal = displaySubtotal.add(this.appliedVoucherAmount)
        return displaySubtotal.value
      },
      paymentTotal () {
        let paymentTotal = currency(0)
        _.each(this.validPayments, payment => {
          paymentTotal = paymentTotal.add(payment.amount)
        })
        return paymentTotal.value
      },
      completedPaymentTotal () {
        let completedPaymentTotal = currency(0)
        _.each(this.completedPayments, payment => {
          completedPaymentTotal = completedPaymentTotal.add(payment.amount)
        })
        return completedPaymentTotal.value
      },
      remainingOwed () {
        return currency(this.total).subtract(this.paymentTotal).value
      },
      pendingChangeDuePayments () { return _.filter(this.payments?.data, { forceShowChangeDue: true }) },
      changeDue () {
        let changeDue = currency(0)
        const payments = _(this.validPayments).unionBy(this.pendingChangeDuePayments, 'id').compact().value()
        _.each(payments, payment => {
          changeDue = changeDue.add(payment.changeDue)
        })

        return changeDue.value
      },
      hasGiftCard () { return this.giftCards?.data && this.giftCards.data.length > 0 },
      hasOrderItems () { return this.orderItems?.data?.length > 0 },
      operativeOrderItems () { return _.filter(this.orderItems?.data, { operative: true }) },
      voidedOrderItemsNeedingVoucherTypeSwap () { return _.filter(this.orderItems?.data, orderItem => orderItem.state === 'voided' && !!orderItem.voucherType?.id && !orderItem.voucherTypeSwapped) },
      ticketOrderItems () { return this.orderItemsByType[ORDER_ITEM_TYPES.TICKET] || [] },
      activityTicketOrderItems () { return this.orderItemsByType[ORDER_ITEM_TYPES.ACTIVITY_TICKET] || [] },
      itemPackageActivityTicketOrderItems () { return _.filter(this.activityTicketOrderItems, orderItem => !_.isNil(orderItem.itemPackageId)) },
      ticketOrderItemsPlaced () { return _.filter(this.orderItemsByType[ORDER_ITEM_TYPES.TICKET] || [], orderItem => orderItem.state !== 'draft') },
      dayPassOrderItems () { return this.orderItemsByType[ORDER_ITEM_TYPES.DAY_PASS] || [] },
      itemsOrderItems () { return _.filter(this.orderItemsByType[ORDER_ITEM_TYPES.ITEM] || [], orderItem => _.isNil(orderItem.activityBookingId)) },
      activitySalesItemOrderItems () { return _.filter(this.orderItemsByType[ORDER_ITEM_TYPES.ITEM] || [], orderItem => !_.isNil(orderItem.activityBookingId)) },
      itemPackageItemVariationOrderItems () { return _.filter(this.itemsOrderItems, orderItem => !_.isNil(orderItem.itemPackageId)) },
      membershipsOrderItems () { return this.orderItemsByType[ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL] || [] },
      giftCardOrderItems () { return this.orderItemsByType[ORDER_ITEM_TYPES.GIFT_CARD] || [] },
      vouchersOrderItems () { return _.concat(this.orderItemsByType[ORDER_ITEM_TYPES.VOUCHER_PURCHASE] || [], this.orderItemsByType[ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE] || []) },
      miscOrderItems () { return _.concat(this.orderItemsByType[ORDER_ITEM_TYPES.DONATION] || [], this.orderItemsByType[ORDER_ITEM_TYPES.USER_CREDIT] || [], this.orderItemsByType[ORDER_ITEM_TYPES.MISC_ITEM] || [], this.orderItemsByType[ORDER_ITEM_TYPES.TIP] || []) },
      swappableOrderItems () { return _.filter(this.orderItems?.data, orderItem => orderItem.isSwappable) },
      bookingFeeOrderItems () { return this.orderItemsByType[ORDER_ITEM_TYPES.BOOKING_FEE] || [] },
      totalOrderItemsWithoutBookingFee () { return _.filter(this.orderItems?.data, orderItem => orderItem.type !== ORDER_ITEM_TYPES.BOOKING_FEE) },
      exchangeFees () { return _.filter(this.orderItemsByType[ORDER_ITEM_TYPES.EXCHANGE_FEE] || [], orderItem => orderItem.state === 'draft') },
      displayTotal () {
        let total = currency(0)
        const totals = [
          this.dayPassOrderItemsTotal,
          this.ticketOrderItemsTotal,
          this.activityTicketOrderItemsTotal,
          this.membershipOrderItemsTotal,
          this.giftCardOrderItemsTotal,
          this.voucherOrderItemsTotal,
          this.itemOrderItemsTotal,
          this.miscOrderItemsTotal
        ]

        _.each(totals, itemsTotal => {
          total = total.add(itemsTotal)
        })

        return total.value
      },
      displayBookingFeesTotal () {
        let total = currency(0)
        _.each(this.draftOrderItems, (orderItem) => {
          total = total.add(orderItem.bookingFeeDisplayPrice)
        })
        return total.value
      },
      dayPassOrderItemsTotal () {
        let total = currency(0)
        _.each(this.dayPassOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      ticketOrderItemsTotal () {
        let total = currency(0)
        _.each(this.ticketOrderItems, orderItem => {
          if (this.configuration.currentSite.consumerConfig.showBookingFeeInCart && !orderItem.includeBookingFeeInPrice) {
            total = total.add(orderItem.displayTotal).add(orderItem.bookingFeeDisplayPrice)
          } else {
            total = total.add(orderItem.displayTotal)
          }
        })
        return total.value
      },
      activityTicketOrderItemsTotal () {
        let total = currency(0)
        _.each(this.activityTicketOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      itemOrderItemsTotal () {
        let total = currency(0)
        _.each(this.itemsOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      activitySalesItemOrderItemsTotal () {
        let total = currency(0)
        _.each(this.activitySalesItemOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      membershipOrderItemsTotal () {
        let total = currency(0)
        _.each(this.membershipsOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      giftCardOrderItemsTotal () {
        let total = currency(0)
        _.each(this.giftCardOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      voucherOrderItemsTotal () {
        let total = currency(0)
        _.each(this.vouchersOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      miscOrderItemsTotal () {
        let total = currency(0)
        _.each(this.miscOrderItems, orderItem => {
          total = total.add(orderItem.displayTotal)
        })
        return total.value
      },
      orderItemsByType () {
        return _.groupBy(this.orderItems?.data, 'type')
      },
      unpaidOrderItems () { return _.filter(this.orderItems?.data, { unpaid: true }) },
      validPayments () { return _.filter(this.payments?.data, { operative: true }) },
      completedPayments () { return _.filter(this.payments?.data, { completed: true }) },
      validOrderItems () { return this.orderItems?.data && _.filter(this.orderItems?.data, orderItem => orderItem.state === 'draft' || orderItem.state === 'placed') },
      orderItemsWithoutEmptyDonations () { return this.orderItems?.data && _.filter(this.orderItems?.data, orderItem => orderItem.type !== 'donation' || orderItem.amount > 0) },
      draftOrderItemsCartCount () {
        return _.filter(this.draftOrderItems, orderItem => {
          if (orderItem.type === ORDER_ITEM_TYPES.BOOKING_FEE) return false
          if (_.includes(ORDER_ITEM_TYPES.SET_AMOUNT, orderItem.type) && orderItem.amount === 0) return false
          return true
        }).length
      },
      preAuthPayment () { return _.find(this.payments?.data, { state: 'pre_auth' }) },
      isDisputed () { return this.payments?.data && _.some(this.payments.data, payment => payment.state === 'disputed' || payment.state === 'lost_dispute') },
      surcharges () { return _.filter(this.orderItems?.data, orderItem => orderItem.type === 'surcharge') },
      surchargeAmount () {
        let total = currency(0)
        _.each(this.surcharges, surcharge => {
          total = total.add(surcharge.total)
        })
        return total.value
      },
      draftOrderItems () { return _.filter(this.orderItems?.data, { state: 'draft' }) },
      draftOrderItemValidationMessages () {
        const draftOrderItemValidationMessages = []
        if (!isConsumer) {
          // We return early, as we only enforce/apply this for consumer orders (for now)
          return draftOrderItemValidationMessages
        }
        _.each(this.draftOrderItems, orderItem => {
          _.each(Object.keys(orderItem.validations || {}), key => {
            draftOrderItemValidationMessages.push(orderItem.validations[key]?.message)
          })
        })
        return draftOrderItemValidationMessages
      },
      hasDraftPayments () { return _.some(this.payments?.data, 'draft') },
      numberOfGiftCards () { return this.giftCards.data.length },
      totalGiftCardBalance () {
        let total = currency(0)
        _.each(this.giftCards.data, giftCard => {
          total = total.add(giftCard.balance)
        })
        return total.value
      },
      appliedVoucherAmount () {
        let total = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          total = total.add(orderItem.displayVoucherAmount)
        })
        return total.value
      },
      appliedDiscountAmount () {
        let total = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          total = total.add(orderItem.displayDiscountAmount)
        })
        return total.value
      },
      bookingFeeTotal () {
        let total = currency(0)
        _.each(this.bookingFeeOrderItems, orderItem => {
          total = total.add(orderItem.total)
        })
        return total.value
      },
      bookingFeeSubtotal () {
        let total = currency(0)
        _.each(this.bookingFeeOrderItems, orderItem => {
          total = total.add(orderItem.subtotal)
        })
        return total.value
      },
      exchangeFeeTotal () {
        let total = currency(0)
        _.each(this.exchangeFees, orderItem => {
          total = total.add(orderItem.total)
        })
        return total.value
      },
      exchangeFeeSubtotal () {
        let total = currency(0)
        _.each(this.exchangeFees, orderItem => {
          total = total.add(orderItem.subtotal)
        })
        return total.value
      },
      donation () {
        let total = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          if (orderItem.type === ORDER_ITEM_TYPES.DONATION) {
            total = total.add(orderItem.amount)
          }
        })
        return total.value
      },
      tip () {
        let total = currency(0)
        _.each(this.operativeOrderItems, orderItem => {
          if (orderItem.type === ORDER_ITEM_TYPES.TIP) {
            total = total.add(orderItem.amount)
          }
        })
        return total.value
      },
      totalWithoutTipAndDonation () {
        return currency(this.total).subtract(this.tip).subtract(this.donation).value
      },
      name () {
        return this.userOrDeliveryName || this.nameForOrder
      },
      siteName () {
        if (this.siteId !== this.configuration.currentSite.id) {
          const orderSite = _.find(this.configuration?.sites?.data, site => site.id === this.siteId)
          if (orderSite) return orderSite.name
        }
        return this.configuration.currentSite.name
      },
      userOrDeliveryName () {
        if (this.orderUserFirstName) {
          return `${this.orderUserFirstName} ${this.orderUserLastName}`.trim()
        } else if (this.user) {
          return `${this.user.firstName} ${this.user.lastName}`.trim()
        } else if (this.deliveryFirstName || this.deliveryLastName) {
          return `${this.deliveryFirstName} ${this.deliveryLastName}`.trim()
        } else {
          return null
        }
      },
      averageTicketPrice () {
        const now = new Date()

        const priceCard = this.currentShowing?.computedPriceCard || this.configuration.currentSite?.defaultPriceCard(now.getDay(), TimeSlot.timeSlotForDateTime(now)?.id)

        if (!priceCard?.priceCardItems) return 0

        let totalTicketTypePrice = currency(0)
        let totalTicketTypePricesCount = 0
        _.each(priceCard.priceCardItems.data, priceCardItem => {
          if (priceCardItem.hasCustomPrice && _.isFinite(priceCardItem.customPrice)) {
            totalTicketTypePrice = totalTicketTypePrice.add(priceCardItem.customPrice)
            totalTicketTypePricesCount += 1
          } else if (_.isFinite(priceCardItem.ticketType?.price)) {
            totalTicketTypePrice = totalTicketTypePrice.add(priceCardItem.ticketType?.price)
            totalTicketTypePricesCount += 1
          } else if (!_.isNil(priceCardItem.itemPackage?.itemPackageItems?.data)) {
            _.each(priceCardItem.itemPackage?.itemPackageItems?.data, itemPackageItem => {
              if (_.isFinite(itemPackageItem?.ticketType?.price)) {
                totalTicketTypePrice = totalTicketTypePrice.add(itemPackageItem?.ticketType?.price)
                totalTicketTypePricesCount += 1
              }
            })
          }
        })

        if (totalTicketTypePricesCount < 1) return 0

        return totalTicketTypePrice.divide(totalTicketTypePricesCount).value
      },
      deliveryLocationConfigurationModel () {
        if (_.isNil(this.deliveryLocation) || _.isNil(this.deliveryType)) {
          return null
        }
        return _.find(this.configuration.deliveryLocations.data, { name: this.deliveryLocation, type: this.deliveryType })
      },
      availableRedemptions () {
        if (!this.user) {
          return this.sortRedemptions([...(this.independentVouchers?.data || []), ...(this.independentDiscounts?.data || []), ...this.globalDiscounts])
        }

        const modifyingOrder = _.some(this.orderItems.data, orderItem => orderItem.state === 'draft' || orderItem.isLocal)

        if (this.state !== 'draft' && this.state !== 'open_tab' && !modifyingOrder) return

        // Get all available redemptions
        // Order matters: sort by priority. The first matching redemption gets applied.
        let redemptions = []

        // Handle ticket vouchers from associated members
        // (only ticket vouchers are honored, that member would need to sign in to use a concession voucher)
        const hasMembers = (this.user.membership?.members?.length || 0) + (this.user.purchasees?.length || 0) > 0
        const users = hasMembers ? _.unionBy(this.user.membership.members.data, this.user.purchasees.data, 'id') : [this.user]
        _.each(users, (user) => {
          const isSelf = user.id === this.user.id

          const availableMembershipVouchers = _.filter(user.unlimitedMembershipVouchers, membershipVoucher => {
            let isAvailable = true

            if (isAvailable && membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.SITE] && (!membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] || membershipVoucher.siteIds?.length)) {
              isAvailable = isAvailable && (membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_SITES] !== _.includes(membershipVoucher.siteIds, this.configuration.currentSite.id))
            }

            if (isAvailable && membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.DAY_OF_WEEK]) {
              const dayNumber = _.first(this.futureShowings)?.dayNumber || new Date().getDay()
              const isDayOfWeekMatch = _.includes(membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.DAY_OF_WEEK], dayNumber)
              isAvailable = isAvailable && membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK] !== isDayOfWeekMatch
            }

            if (isAvailable && membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.DATE_RANGE]) {
              const time = _.first(this.futureShowings)?.time ? new Date(_.first(this.futureShowings)?.time).getTime() : new Date().getTime()
              const startDate = new Date(membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_START]).getTime()
              const isAfterStartDate = time > startDate
              isAvailable = isAvailable && membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE] !== isAfterStartDate

              if (membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END]) {
                const endDate = addToDate(new Date(membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END]), { days: 1 }).getTime()
                const isBeforeEndDate = time < endDate
                isAvailable = isAvailable && membershipVoucher.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE] !== isBeforeEndDate
              }
            }

            if (isAvailable && membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.LIFETIME]) {
              const currentTime = new Date().getTime()
              const membershipStartTime = new Date(user.createdAt).getTime()

              if (!membershipStartTime) return false

              const membershipDuration = currentTime - membershipStartTime // Duration in milliseconds

              let lifetimeMinimumMs = null
              let lifetimeMaximumMs = null

              const lifetimeTerm = membershipVoucher.conditionsRequirements.lifetimeTerm
              const lifetimeMin = membershipVoucher.conditionsRequirements.lifetimeMinimum
              const lifetimeMax = membershipVoucher.conditionsRequirements.lifetimeMaximum

              if (lifetimeTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.YEARS) {
                lifetimeMinimumMs = lifetimeMin * 365 * 24 * 60 * 60 * 1000 // Convert years to milliseconds
                if (lifetimeMax !== null) {
                  lifetimeMaximumMs = lifetimeMax * 365 * 24 * 60 * 60 * 1000
                }
              } else if (lifetimeTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.DAYS) {
                lifetimeMinimumMs = lifetimeMin * 24 * 60 * 60 * 1000 // Convert days to milliseconds
                if (lifetimeMax !== null) {
                  lifetimeMaximumMs = lifetimeMax * 24 * 60 * 60 * 1000
                }
                // default to months
              } else {
                lifetimeMinimumMs = lifetimeMin * 30 * 24 * 60 * 60 * 1000 // Approximate months to milliseconds
                if (lifetimeMax !== null) {
                  lifetimeMaximumMs = lifetimeMax * 30 * 24 * 60 * 60 * 1000
                }
              }

              // Check if the membership duration is within the required range
              if (membershipDuration < lifetimeMinimumMs) {
                isAvailable = isAvailable && false
              } else if (lifetimeMaximumMs !== null && membershipDuration > lifetimeMaximumMs) {
                isAvailable = isAvailable && false
              } else {
                // isAvailable = isAvailable
              }
            }

            if (isAvailable && membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.LONGEVITY]) {
              const currentTime = new Date().getTime()
              const membershipStartTime = new Date(user.membershipTypeUpdatedAt).getTime()

              if (!membershipStartTime) return false

              const membershipDuration = currentTime - membershipStartTime // Duration in milliseconds

              let longevityMinimumMs = null
              let longevityMaximumMs = null

              const longevityTerm = membershipVoucher.conditionsRequirements.longevityTerm
              const longevityMin = membershipVoucher.conditionsRequirements.longevityMinimum
              const longevityMax = membershipVoucher.conditionsRequirements.longevityMaximum

              if (longevityTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.YEARS) {
                longevityMinimumMs = longevityMin * 365 * 24 * 60 * 60 * 1000 // Convert years to milliseconds
                if (longevityMax !== null) {
                  longevityMaximumMs = longevityMax * 365 * 24 * 60 * 60 * 1000
                }
              } else if (longevityTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.DAYS) {
                longevityMinimumMs = longevityMin * 24 * 60 * 60 * 1000 // Convert days to milliseconds
                if (longevityMax !== null) {
                  longevityMaximumMs = longevityMax * 24 * 60 * 60 * 1000
                }
                // default to months
              } else {
                longevityMinimumMs = longevityMin * 30 * 24 * 60 * 60 * 1000 // Approximate months to milliseconds
                if (longevityMax !== null) {
                  longevityMaximumMs = longevityMax * 30 * 24 * 60 * 60 * 1000
                }
              }

              // Check if the membership duration is within the required range
              if (membershipDuration < longevityMinimumMs) {
                isAvailable = isAvailable && false
              } else if (longevityMaximumMs !== null && membershipDuration > longevityMaximumMs) {
                isAvailable = isAvailable && false
              } else {
                // isAvailable = isAvailable
              }
            }

            return isAvailable
          })

          const findOrCreateUserVouchers = (user, attributes, count) => {
            const voucherChildren = _.filter([...user.children], child => child.typeName === 'Voucher')
            const existingVouchers = _.filter(voucherChildren, attributes)
            let newlyCreatedVouchers = []

            if (existingVouchers.length < count) {
              let vouchersToCreate = count - existingVouchers.length
              const voucher = new Voucher(attributes)
              vouchersToCreate--
              const vouchers = _.times(vouchersToCreate, () => voucher.copy())
              newlyCreatedVouchers = [voucher, ...vouchers]
              user.addChildren([voucher, ...vouchers])
            }

            return [...existingVouchers, ...newlyCreatedVouchers].slice(0, count)
          }

          _.each(availableMembershipVouchers, membershipVoucher => {
            // Create 5 ticket vouchers for unlimited plan
            // These will only get saved if they are assoicated with a ticket later
            // This limits the number of showings that can be purchased at once to 5)
            const voucherType = membershipVoucher.voucherType
            const voucherAttributes = {
              user,
              membershipVoucher,
              voucherTypeId: voucherType.id,
              isUnlimited: true
            }

            if (voucherType.type === 'ticket') {
              voucherAttributes.forSelf = true
            } else {
              voucherAttributes.isManual = true
            }

            redemptions = [...redemptions, ...findOrCreateUserVouchers(user, voucherAttributes, 5)]
          })
          if (!isSelf) {
            // If the ticket vouchers are limited, they just show up as regular vouchers
            // If this is an associated user, get them here but mark them all as "forSelf" so
            // the selected user can't spend the associated users vouchers, except on the associated user
            _.each(user.vouchers?.data, (voucher) => {
              voucher.user = user
              if (voucher.voucherType.type === 'ticket') {
                voucher.forSelf = true
                redemptions.push(voucher)
              }
            })
          } else {
            // We will add all vouchers for the selected user below, and not limit them to forSelf
            // This allows the selected user to use their vouchers for themselves or a guest
          }

          if (isSelf && (user.dayPassDate || (this.purchasingDayPass && user.id === this.user.id)) && this.configuration.currentSite.dayPassEnabled) {
            // Create 5 ticket vouchers for unlimited plan
            // These will only get saved if they are assoicated with a ticket later
            // This limits the number of showings that can be purchased at once to 5
            let validShowingDate = null
            if (user.dayPassDate && user.dayPassDate.isDate()) {
              validShowingDate = user.dayPassDate
            } else if (this.purchasingDayPass) {
              validShowingDate = this.showing?.time || null
            }

            const voucherAttributes = {
              voucherTypeId: this.configuration.currentSite.dayPassVoucherTypeId,
              user,
              forSelf: true,
              validShowingDate
            }

            redemptions = [...redemptions, ...findOrCreateUserVouchers(user, voucherAttributes, 5)]
          }
        })

        const userVouchers = this.user.vouchers?.data || []
        const nonUserVouchers = _.differenceBy(this.independentVouchers.data, userVouchers, 'id')

        // Add applied rewards, selected user vouchers and discounts (this is from membership or other)
        redemptions = [...redemptions, ...userVouchers, ...nonUserVouchers, ...this.independentDiscounts?.data || [], ...this.appliedRewards?.data || []]

        // Add discounts from membership (either active or default)
        redemptions = [...redemptions, ...(this.currentMembershipDiscounts?.data || [])]

        redemptions = [...redemptions, ...this.globalDiscounts]

        redemptions = this.sortRedemptions(redemptions)

        return redemptions
      },
      globalDiscounts () {
        return _.filter(this.configuration.discounts.data, discount => {
          return discount.appliesToAllOrders &&
            (!isConsumer || discount.availableOnline) &&
            discount.isSiteMatch()
        })
      },
      userDiscountRedemptions () {
        const userDiscountOrderItems = _.filter(this.unpaidOrderItems, orderItem => orderItem.discount && orderItem.discountUserId !== null && !orderItem.transient)
        const discountIds = _.uniq(_.map(userDiscountOrderItems, 'discountId'))
        const userDiscountRedemptions = {}

        if (this.user) {
          _.each(this.user?.membership?.members?.data, member => {
            userDiscountRedemptions[member.id] ||= {}
            _.each(discountIds, discountId => {
              userDiscountRedemptions[member.id][discountId] ||= {}
              userDiscountRedemptions[member.id][discountId].count ||= 0
              userDiscountRedemptions[member.id][discountId].byShowing ||= {}
              userDiscountRedemptions[member.id][discountId].byActivitySession ||= {}
            })
          })
          _.each(this.user?.purchasees?.data, purchasee => {
            userDiscountRedemptions[purchasee.id] = {}
            _.each(discountIds, discountId => {
              userDiscountRedemptions[purchasee.id][discountId] ||= {}
              userDiscountRedemptions[purchasee.id][discountId].count ||= 0
              userDiscountRedemptions[purchasee.id][discountId].byShowing ||= {}
              userDiscountRedemptions[purchasee.id][discountId].byActivitySession ||= {}
            })
          })
          _.each(userDiscountOrderItems, orderItem => {
            const { discountUserId, discountId, localShowing, localActivityTicket } = orderItem
            userDiscountRedemptions[discountUserId][discountId].count++
            if (localShowing) {
              userDiscountRedemptions[discountUserId][discountId].byShowing[localShowing.id] ||= {}
              userDiscountRedemptions[discountUserId][discountId].byShowing[localShowing.id].count ||= 0
              userDiscountRedemptions[discountUserId][discountId].byShowing[localShowing.id].count++
            } else if (localActivityTicket) {
              userDiscountRedemptions[discountUserId][discountId].byActivitySession[localActivityTicket.activitySession.id] ||= []

              const activitySessionObjectForEntryTime = _.find(
                userDiscountRedemptions[discountUserId][discountId].byActivitySession[localActivityTicket.activitySession.id],
                activitySessionObject => activitySessionObject.entryTime === localActivityTicket.entryTime.toISOString()
              )

              if (activitySessionObjectForEntryTime) {
                activitySessionObjectForEntryTime.count++
              } else {
                userDiscountRedemptions[discountUserId][discountId].byActivitySession[localActivityTicket.activitySession.id].push({
                  entryTime: localActivityTicket.entryTime.toISOString(),
                  exitTime: localActivityTicket.exitTime.toISOString(),
                  count: 1
                })
              }
            }
          })
        }

        return userDiscountRedemptions
      },
      userVoucherRedemptions () {
        const userVoucherOrderItems = _.filter(this.orderItems.data, orderItem => orderItem.voucherUserId !== null && orderItem.state === 'draft' && !orderItem.transient)
        const voucherTypeIds = _.uniq(_.map(userVoucherOrderItems, orderItem => orderItem.voucher?.voucherType?.id || orderItem.voucherType?.id))
        const userVoucherRedemptions = {}

        if (this.user) {
          _.each(this.user?.membership?.members?.data, member => {
            userVoucherRedemptions[member.id] ||= {}
            _.each(voucherTypeIds, voucherTypeId => {
              userVoucherRedemptions[member.id][voucherTypeId] ||= {}
              userVoucherRedemptions[member.id][voucherTypeId].count ||= 0
              userVoucherRedemptions[member.id][voucherTypeId].byShowing ||= {}
              userVoucherRedemptions[member.id][voucherTypeId].byActivitySession ||= {}
            })
          })
          _.each(this.user?.purchasees?.data, purchasee => {
            userVoucherRedemptions[purchasee.id] = {}
            _.each(voucherTypeIds, voucherTypeId => {
              userVoucherRedemptions[purchasee.id][voucherTypeId] ||= {}
              userVoucherRedemptions[purchasee.id][voucherTypeId].count ||= 0
              userVoucherRedemptions[purchasee.id][voucherTypeId].byShowing ||= {}
              userVoucherRedemptions[purchasee.id][voucherTypeId].byActivitySession ||= {}
            })
          })
          _.each(userVoucherOrderItems, orderItem => {
            const { voucherUserId, voucher, voucherType, localShowing, localActivityTicket } = orderItem
            const voucherTypeId = voucher?.voucherType?.id || voucherType?.id
            userVoucherRedemptions[voucherUserId][voucherTypeId].count++
            if (localShowing) {
              userVoucherRedemptions[voucherUserId][voucherTypeId].byShowing[localShowing.id] ||= {}
              userVoucherRedemptions[voucherUserId][voucherTypeId].byShowing[localShowing.id].count ||= 0
              userVoucherRedemptions[voucherUserId][voucherTypeId].byShowing[localShowing.id].count++
            } else if (localActivityTicket) {
              userVoucherRedemptions[voucherUserId][voucherTypeId].byActivitySession[localActivityTicket.activitySession.id] ||= []

              const activitySessionObjectForEntryTime = _.find(
                userVoucherRedemptions[voucherUserId][voucherTypeId].byActivitySession[localActivityTicket.activitySession.id],
                activitySessionObject => activitySessionObject.entryTime === localActivityTicket.entryTime.toISOString()
              )

              if (activitySessionObjectForEntryTime) {
                activitySessionObjectForEntryTime.count++
              } else {
                userVoucherRedemptions[voucherUserId][voucherTypeId].byActivitySession[localActivityTicket.activitySession.id].push({
                  entryTime: localActivityTicket.entryTime.toISOString(),
                  exitTime: localActivityTicket.exitTime.toISOString(),
                  count: 1
                })
              }
            }
          })
        }

        return userVoucherRedemptions
      },
      anonymousDiscountRedemptions () {
        const anonymousDiscountOrderItems = _.filter(this.orderItems.data, orderItem => orderItem.discount && !orderItem.discountUserId && orderItem.state === 'draft' && !orderItem.transient)
        const anonymousDiscountRedemptions = {
          byShowing: {}
        }

        _.each(anonymousDiscountOrderItems, orderItem => {
          anonymousDiscountRedemptions[orderItem.discountId] ||= {}
          anonymousDiscountRedemptions[orderItem.discountId].count ||= 0
          anonymousDiscountRedemptions[orderItem.discountId].count++
          if (orderItem.localShowing) {
            anonymousDiscountRedemptions[orderItem.discountId].byShowing ||= {}
            anonymousDiscountRedemptions[orderItem.discountId].byShowing[orderItem.localShowing.id] ||= {}
            anonymousDiscountRedemptions[orderItem.discountId].byShowing[orderItem.localShowing.id].count ||= 0
            anonymousDiscountRedemptions[orderItem.discountId].byShowing[orderItem.localShowing.id].count++
            anonymousDiscountRedemptions[orderItem.discountId].byShowing[orderItem.localShowing.id].showingOverlapStartTime ||= date.addToDate(orderItem.localShowing.time, { minutes: 30 })
            anonymousDiscountRedemptions[orderItem.discountId].byShowing[orderItem.localShowing.id].showingOverlapEndTime ||= date.addToDate(orderItem.localShowing.endTime, { minutes: -30 })
          } else if (orderItem.localActivityTicket) {
            anonymousDiscountRedemptions[orderItem.discountId].byActivitySession ||= {}
            anonymousDiscountRedemptions[orderItem.discountId].byActivitySession[orderItem.localActivityTicket.activitySession.id] ||= []

            const activitySessionObjectForEntryTime = _.find(
              anonymousDiscountRedemptions[orderItem.discountId].byActivitySession[orderItem.localActivityTicket.activitySession.id],
              activitySessionObject => activitySessionObject.entry_time === orderItem.localActivityTicket.entryTime.toISOString()
            )

            if (activitySessionObjectForEntryTime) {
              activitySessionObjectForEntryTime.count++
            } else {
              anonymousDiscountRedemptions[orderItem.discountId].byActivitySession[orderItem.localActivityTicket.activitySession.id].push({
                entry_time: orderItem.localActivityTicket.entryTime.toISOString(),
                exit_time: orderItem.localActivityTicket.exitTime.toISOString(),
                count: 1
              })
            }
          }
        })

        return anonymousDiscountRedemptions
      },
      anonymousVoucherRedemptions () {
        const anonymousVoucherOrderItems = _.filter(this.orderItems.data, orderItem => orderItem.voucher && !orderItem.voucher?.user && orderItem.state === 'draft' && !orderItem.transient)
        const anonymousVoucherRedemptions = {
          byShowing: {}
        }

        _.each(anonymousVoucherOrderItems, orderItem => {
          anonymousVoucherRedemptions[orderItem.voucher.voucherType.id] ||= {}
          anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].count ||= 0
          anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].count++
          if (orderItem.localShowing) {
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byShowing ||= {}
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byShowing[orderItem.localShowing.id] ||= {}
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byShowing[orderItem.localShowing.id].count ||= 0
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byShowing[orderItem.localShowing.id].count++
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byShowing[orderItem.localShowing.id].showingOverlapStartTime ||= date.addToDate(orderItem.localShowing.time, { minutes: 30 })
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byShowing[orderItem.localShowing.id].showingOverlapEndTime ||= date.addToDate(orderItem.localShowing.endTime, { minutes: -30 })
          } else if (orderItem.localActivityTicket) {
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byActivitySession ||= {}
            anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byActivitySession[orderItem.localActivityTicket.activitySession.id] ||= []

            const activitySessionObjectForEntryTime = _.find(
              anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byActivitySession[orderItem.localActivityTicket.activitySession.id],
              activitySessionObject => activitySessionObject.entry_time === orderItem.localActivityTicket.entryTime.toISOString()
            )

            if (activitySessionObjectForEntryTime) {
              activitySessionObjectForEntryTime.count++
            } else {
              anonymousVoucherRedemptions[orderItem.voucher.voucherType.id].byActivitySession[orderItem.localActivityTicket.activitySession.id].push({
                entry_time: orderItem.localActivityTicket.entryTime.toISOString(),
                exit_time: orderItem.localActivityTicket.exitTime.toISOString(),
                count: 1
              })
            }
          }
        })

        return anonymousVoucherRedemptions
      },
      appliedPromotions () {
        return _(this.unpaidOrderItems)
          .filter({ transient: false })
          .map(orderItem => [orderItem.localVoucher, orderItem.discount])
          .flatten()
          .compact()
          .uniqBy(promotion => `${promotion.typeName}-${promotion.id}`)
          .sortBy(promotion => -(promotion.voucherType?.id || promotion.id))
          .sortBy(promotion => promotion.typeName)
          .reverse()
          .value()
      },
      appliedVouchers () {
        return _.filter(this.appliedPromotions, { typeName: 'Voucher' })
      },
      uniqueAppliedVouchers () {
        return _.unionBy(this.appliedVouchers, voucher => `${voucher.voucherType.id},${voucher.isReward}`)
      },
      hasUnappliedCeaVoucher () {
        return _.some(this.allUnappliedVouchers, voucher => voucher.voucherType.isCeaCard)
      },
      ceaCardApplied () {
        // Cannot use appliedVouchers directly - as reactivity doesn't work
        return _.some(this.appliedPromotions, promotion => promotion.typeName === 'Voucher' && promotion.voucherType.isCeaCard)
      },
      allAppliedDiscounts () {
        return _(this.unpaidOrderItems)
          .filter({ transient: false })
          .map(orderItem => orderItem.discount)
          .compact()
          .sortBy(promotion => promotion.id)
          .reverse()
          .value()
      },
      appliedDiscountCount () {
        const discountGroups = _.groupBy(this.allAppliedDiscounts, 'id')
        const discountCount = {}
        for (const discountId in discountGroups) {
          discountCount[discountId] = discountGroups[discountId].length
        }

        return discountCount
      },
      appliedDiscounts () {
        return _.filter(this.appliedPromotions, { typeName: 'Discount' })
      },
      unappliedVouchers () {
        const appliedIds = _.map(this.appliedVouchers, 'id')
        return _(this.availableRedemptions).filter(
          redemption =>
            redemption.typeName === 'Voucher' &&
            (redemption.isRemote || redemption.isManual) &&
            !redemption.forSelf &&
            !_.includes(appliedIds, redemption.id)
        ).groupBy(voucher => `${voucher.voucherType.id},${voucher.isReward}`)
          .values()
          .map(vouchers => {
            return { firstVoucher: vouchers[0], count: vouchers.length }
          })
          .value()
      },
      allUnappliedVouchers () {
        const appliedIds = _.map(this.appliedVouchers, 'id')
        return _.filter(this.availableRedemptions, redemption => redemption.typeName === 'Voucher' && !_.includes(appliedIds, redemption.id))
      },
      unappliedDiscounts () {
        const appliedIds = _.map(this.appliedDiscounts, 'id')
        return _(this.availableRedemptions).filter({ typeName: 'Discount', isRemote: true }).filter(redemption => !_.includes(appliedIds, redemption.id)).value()
      },
      availableDiscounts () {
        return _.uniq([..._.filter(this.availableRedemptions, redemption => {
          return redemption.typeName === 'Discount' &&
            (!redemption.appliesToAllOrders || _.includes(this.appliedDiscounts, redemption))
        }), ...(this.availableLimitRestrictedDiscounts?.data || [])])
      },
      remainingVouchers () {
        const remainingVouchers = {}

        _.each(this.unappliedVouchers, voucherType => {
          remainingVouchers[voucherType.firstVoucher.voucherType.id] = voucherType
        })

        return remainingVouchers
      },
      remainingDiscounts () {
        const remainingDiscounts = {}

        _.each(this.availableDiscounts, discount => {
          let discountRedemptionLimit = null
          if (discount.membershipDiscount && discount.membershipDiscount.limitRedemptions) {
            discountRedemptionLimit = discount.membershipDiscount.redemptionsLimit - (discount.user.discountRedemptions[discount.id] ?? 0) - (this.userDiscountRedemptions[discount.user.id]?.[discount.id]?.count ?? 0)
          }
          if (discount.limitRedemptionsByOrderEnabled) {
            const remainingDiscounts = discount.orderRedemptionsLimit - this.appliedDiscountCount[discount.id]
            discountRedemptionLimit = Math.min(remainingDiscounts, discountRedemptionLimit)
          }
          if (discount.limitRedemptionsByShowingEnabled && this.currentShowing) {
            if (discount.user) {
              const discountsUsed = this.userDiscountRedemptions[discount.user.id]?.[discount.id]?.byShowing[this.currentShowing.id]?.count ?? 0
              const discountsRemaining = discount.showingRedemptionsLimit - discountsUsed
              discountRedemptionLimit = !discountRedemptionLimit ? discountsRemaining : Math.min(discountRedemptionLimit, discountsRemaining)
            } else {
              const discountUsed = this.anonymousDiscountRedemptions[discount.id]?.byShowing[this.currentShowing.id]?.count ?? 0
              const discountsRemaining = discount.showingRedemptionsLimit - discountUsed
              discountRedemptionLimit = !discountRedemptionLimit ? discountsRemaining : Math.min(discountRedemptionLimit, discountsRemaining)
            }
          }

          remainingDiscounts[discount.id] = { discount, count: (discountRedemptionLimit ?? 0) }
        })

        return remainingDiscounts
      },
      totalPoints () {
        return (this.user?.points || 0)
      },
      availablePoints () {
        return (this.user?.points || 0) - _(this.unpaidOrderItems).map('pointsUsed').sum()
      },
      pointsToUse () {
        return _(this.unpaidOrderItems).map('pointsUsed').sum()
      },
      validatedRewards () {
        const rewards = this.configuration.currentCircuit.rewards
        return _.map(rewards, reward => {
          if (reward.membershipTypeRestrictionsEnabled && reward.membershipTypeRestrictionsExclude && this.user) {
            if (_.some(reward.membershipTypeIds, (voucherTypeMembershipTypeId) => voucherTypeMembershipTypeId === this.user.effectiveMembershipType?.id)) {
              return {
                reward,
                valid: false,
                isVisible: false,
                reason: 'Reward not available for current membership type'
              }
            }
          }

          if (reward.membershipTypeRestrictionsEnabled && !reward.membershipTypeRestrictionsExclude && this.user) {
            if (!_.some(reward.membershipTypeIds, (voucherTypeMembershipTypeId) => voucherTypeMembershipTypeId === this.user.effectiveMembershipType?.id)) {
              return {
                reward,
                valid: false,
                isVisible: false,
                reason: 'Reward not available for current membership type'
              }
            }
          }

          if (reward.pointsCost > this.availablePoints) {
            return {
              reward,
              valid: false,
              isVisible: true,
              reason: 'Not enough points to redeem this item'
            }
          }

          if (reward.type === VOUCHER_TYPE_TYPES.SPECIFIC_ITEM) {
            const { currentSite } = this.configuration

            if (isConsumer) {
              if (!currentSite.hasOnlineDelivery) {
                return {
                  reward,
                  valid: false,
                  isVisible: false,
                  reason: 'No delivery types available, item is only available for purchase at the cinema'
                }
              }

              // TODO OOS INDY-6757 Update availability logic
              if (!reward.itemVariation?.item?.category?.availableOnline) {
                return {
                  reward,
                  valid: false,
                  isVisible: false,
                  reason: 'This item category is not available online, item is only available for purchase at the cinema'
                }
              }

              // TODO OOS INDY-6757 Update availability logic
              if (!reward.itemVariation.item.availableOnline) {
                return {
                  reward,
                  valid: false,
                  isVisible: false,
                  reason: 'This item is not available online, item is only available for purchase at the cinema'
                }
              }
            }

            return {
              reward,
              valid: true
            }
          }

          if (!_.some(this.orderItems.data, orderItem => reward.validateVoucher(orderItem))) {
            return {
              reward,
              valid: false,
              isVisible: true,
              reason: 'No items in the cart qualify for this reward'
            }
          }

          return {
            reward,
            valid: true
          }
        })
      },
      availableRewards () {
        const validRewards = _.filter(this.validatedRewards, 'valid')
        return _.map(validRewards, 'reward')
      },
      unavailableRewards () {
        const rewards = this.configuration.currentCircuit.rewards

        return _.differenceBy(rewards, this.availableRewards, 'id')
      },
      unavailableRewardsWithValidation () {
        return _.filter(this.validatedRewards, reward => !reward.valid)
      },
      shownUnavailableRewardsWithValidation () {
        return _.filter(this.unavailableRewardsWithValidation, reward => reward.isVisible)
      },
      uncollectedItems () {
        return _.filter(this.orderItems.data, orderItem => {
          if (orderItem.state !== 'placed' && orderItem.state !== 'paid') return false

          if (!_.isNil(orderItem.collectedAt)) return false

          if (orderItem.type === ORDER_ITEM_TYPES.TICKET) return true

          if (
            orderItem.type === ORDER_ITEM_TYPES.ITEM &&
            orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.COLLECT_FROM_REGISTER
          ) return true

          return false
        })
      },
      collectableTickets () {
        return _.filter(this.uncollectedItems, orderItem => orderItem.type === ORDER_ITEM_TYPES.TICKET)
      },
      collectableItems () {
        return _.filter(this.uncollectedItems, orderItem => {
          return orderItem.type === ORDER_ITEM_TYPES.TICKET || (
            orderItem.type === ORDER_ITEM_TYPES.ITEM &&
            this.currentRegisterStation?.registerType?.applicationType === APPLICATION_TYPES.REGISTER &&
            this.currentRegisterStation?.registerType?.location?.id === orderItem.itemSourceLocationId
          )
        })
      },
      collectableItemsForFutureShowings () {
        return _.filter(this.uncollectedItems, orderItem => {
          return (orderItem.type === ORDER_ITEM_TYPES.TICKET && !orderItem.ticket?.showing?.isPast) || (
            orderItem.type === ORDER_ITEM_TYPES.ITEM &&
            this.currentRegisterStation?.registerType?.applicationType === APPLICATION_TYPES.REGISTER &&
            this.currentRegisterStation?.registerType?.location?.id === orderItem.itemSourceLocationId
          )
        })
      },
      hasUncollectedItems () {
        return !_.isEmpty(this.uncollectedItems)
      },
      hasCollectableTickets () {
        return !_.isEmpty(this.collectableTickets)
      },
      hasCollectableItems () {
        return !_.isEmpty(this.collectableItems)
      },
      customDeliveryType () {
        if (!Number.isInteger(Number.parseInt(this.deliveryType))) return null

        return _.find(this.deliveryTypeOptions, deliveryTypeOption => deliveryTypeOption.id === Number.parseInt(this.deliveryType))
      },
      deliveryTypeName () {
        return this.customDeliveryType?.name || this.deliveryType
      },
      defaultDeliveryType () {
        // Get the simplest delivery type available to all items (complexity goes up with the index of allDeliveryTypes)
        return (_(this.operativeOrderItems)
          .filter(orderItem => orderItem.deliveryType !== DELIVERY_TYPE_OPTIONS.VIRTUAL)
          .map('defaultDeliveryType')
          .maxBy(deliveryType => Number.isInteger(Number.parseInt(deliveryType))
            ? _.indexOf(allDeliveryTypes, 'custom')
            : _.indexOf(allDeliveryTypes, deliveryType))) || _.first(this.deliveryTypeOptions)?.type || null
      },
      defaultDeliveryLocation () {
        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE) {
          return _.first(_(this.operativeOrderItems).filter(orderItem => orderItem.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE).countBy('deliveryLocation').entries().maxBy(entry => _.last(entry)))
        } else if (this.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT && !_.isEmpty(this.futureShowings)) {
          return _.first(this.deliveryLocationOptions)?.name
        }

        return null
      },
      defaultDeliveryPositions () {
        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE) return []

        if (_.isEmpty(this.futureShowings)) return []

        if (_.isNil(this.deliveryLocation)) return []

        return _.compact([_.first(this.deliveryPositionOptions)])
      },
      deliveryOptions () {
        return orderDeliveryOptionsComputer.compute(this)
      },
      deliveryTypeOptions () {
        return this.deliveryOptions.deliveryTypeOptions
      },
      deliveryLocationOptions () {
        return this.deliveryOptions.deliveryLocationOptions
      },
      deliveryPositionOptions () {
        return this.deliveryOptions.deliveryPositionOptions
      },
      defaultHasDueTime () {
        if (this.currentRegisterStation) return false // Only default to future for online orders
        if (!this.purchasingItems) return false
        if (!this.showing) return false

        const now = new Date()
        const minutesUntilStart = date.getDateDiff(this.showing.time, now, 'minutes')

        // If showing is far enough in the future, default to being due
        return minutesUntilStart > 60 // TODO Make configurable
      },
      defaultDueTime () {
        if (!this.purchasingItems) return null
        if (!this.showing) return null

        // TODO Make configurable
        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
          return this.showing.time
        } else {
          return date.addToDate(this.showing.time, { minutes: -10 })
        }
      },
      defaultRemoteOrder () {
        if (!this.currentRegisterStation) return true

        if (this.hasDueTime) return true

        // This one is really an N/A as all prep items are submitted as remote
        if (!this.hasNonprepItems) return true

        return this.currentRegisterStation.registerType?.submitAsRemoteOrder || false
      },
      deliveryInformationProvided () {
        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT && !(this.deliveryLocation && this.deliveryPositions?.length)) return false

        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE && !this.deliveryLocation) return false

        if (this.customDeliveryType && this.customDeliveryType.requireDetails && !this.deliveryDetails) return false

        if (!this.collectPagerNumber && !this.collectNameForOrder) return true

        if (this.collectPagerNumber && !this.pagerNumber) return false

        return !!this.pagerNumber || !!this.user || !!this.nameForOrder
      },
      tabInformationProvided () {
        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE) return true

        if (!this.user && !this.nameForOrder && !this.identifyWithDeliveryInfo) return false

        if (this.hasSavedPaymentMethod) return true

        if (this.configuration.currentSite.requireCreditCardForTabOrders) return false

        return this.tabInformationConfirmed
      },
      purchasingMembership () {
        return _.some(this.operativeOrderItems, { type: ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL })
      },
      purchasingItems () {
        return _.some(this.operativeOrderItems, { type: ORDER_ITEM_TYPES.ITEM })
      },
      purchasingTickets () {
        return _.some(this.operativeOrderItems, { type: ORDER_ITEM_TYPES.TICKET })
      },
      purchasingGiftCards () {
        return _.some(this.operativeOrderItems, { type: ORDER_ITEM_TYPES.GIFT_CARD })
      },
      purchasingDonation () {
        return _.some(this.operativeOrderItems, { type: ORDER_ITEM_TYPES.DONATION })
      },
      purchasingVouchers () {
        return _.some(this.operativeOrderItems, { type: ORDER_ITEM_TYPES.VOUCHER_PURCHASE }) ||
          _.some(this.operativeOrderItems, { type: ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE })
      },
      purchasingMultiMonthMembership () {
        const orderItem = _.find(this.operativeOrderItems, { type: 'membership-interval', subtype: 'new' })
        return (orderItem?.membershipInterval?.term || 1) > 1
      },
      purchasingDayPass () {
        return _.some(this.operativeOrderItems, { type: 'day-pass' })
      },
      dayPassTicketPricePerPerson () {
        return _(this.operativeOrderItems)
          .filter(orderItem => orderItem.type === 'day-pass' || orderItem.type === 'ticket')
          .map('total')
          .sum() /
          _.filter(this.operativeOrderItems, { type: 'ticket' }).length
      },
      showing () {
        return _.find(this.operativeOrderItems, { type: 'ticket' })?.ticket?.showing || this.concessionOrderTargetShowing
      },
      activityTicket () {
        return _.find(this.operativeOrderItems, { type: 'activity-ticket' })?.activityTicket
      },
      showingIncludingVoided () {
        if (this.showing) return this.showing

        const ticketOrderItem = _.find(this.orderItems.data, { type: 'ticket' })

        if (!ticketOrderItem) return

        if (!ticketOrderItem.ticket) return

        return ticketOrderItem.ticket.showing
      },
      purchasingShowing () {
        const ticketOrderItem = _.find(this.orderItems.data, { state: 'draft', type: 'ticket' })

        if (!ticketOrderItem) return

        if (!ticketOrderItem.ticket) return

        return ticketOrderItem.ticket.showing
      },
      allowNonRecurringPayment () {
        if (!this.purchasingMembership) return true

        if (this.configuration.currentSite.allowMonthlyCashMembership) return true

        if (this.configuration.currentSite.allowMultiMonthCashMembership && this.purchasingMultiMonthMembership) return true

        return false
      },
      allShowings () {
        return _(this.operativeOrderItems).filter({ type: 'ticket' }).map('ticket.showing').uniqBy('id').compact().value()
      },
      allMovies () {
        return _(this.operativeOrderItems).filter({ type: 'ticket' }).map('ticket.showing.movie').uniqBy('id').value()
      },
      currentShowings () {
        const currentTime = new Date().getTime()
        return _(this.allShowings).filter(showing => {
          if (showing.timeMs > currentTime) {
            const NinetyMinutes = 90 * 60 * 1000
            return showing.timeMs < (currentTime + NinetyMinutes)
          } else {
            return showing.endTimeMs > currentTime
          }
        }).sortBy('time').value()
      },
      futureShowings () {
        const currentTime = new Date().getTime()
        return _(this.allShowings).filter(showing => { return showing.endTimeMs > currentTime }).sortBy('time').value()
      },
      futureActivityTickets () {
        const currentTime = new Date().getTime()
        return _(this.operativeOrderItems)
          .filter({ type: 'activity-ticket' })
          .map('activityTicket')
          .filter(activityTicket => activityTicket.exitTime.getTime() > currentTime)
          .sortBy('entryTime')
          .value()
      },
      nearestShowing () {
        const currentTime = new Date().getTime()
        return _(this.allShowings).sortBy(showing => Math.abs(showing.timeMs - currentTime)).first()
      },
      nextShowing () {
        return _.first(this.futureShowings)
      },
      nextActivityTicket () {
        return _.first(this.futureActivityTickets)
      },
      theaterSeatName () {
        if (this.manualTheaterSeatName) return this.manualTheaterSeatName

        if (!this.showing) return

        return `${this.showing.screen.name} - Seat ${_.find(this.operativeOrderItems, (orderItem) => orderItem.type === 'ticket').ticket.seatName}`
      },
      seatSelectionRequired () {
        return _.some(
          this.operativeOrderItems,
          orderItem =>
            orderItem.type === 'ticket' &&
            orderItem.ticket.showing.reservedSeating &&
            !orderItem.ticket.seatName
        )
      },
      hasNonprepItems () {
        if (!this.purchasingItems) return false

        return !_.every(this.operativeOrderItems, 'orderItem.itemOrderItem.item.requiresPreparation')
      },
      formattedDueTime () {
        return this.formatDueTime(this.dueTime)
      },
      collectNameForOrder () {
        return _.includes([DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP, DELIVERY_TYPE_OPTIONS.EXPRESS_PICKUP], this.deliveryType) &&
          (this.configuration.currentSite.useNameForCounterPickup ||
            (isConsumer && this.configuration.currentSite.counterPickupAvailableOnline && _.includes(this.configuration.currentSite.counterPickupIdentifierPriorityForRemoteOrders, 'custom')))
      },
      collectPagerNumber () {
        return this.currentRegisterStation && this.configuration.currentSite.hasCounterPager && this.deliveryType === DELIVERY_TYPE_OPTIONS.COUNTER_PICKUP && !this.hasDueTime
      },
      isOpen () {
        return this.state === 'draft' || this.state === 'open_tab' || this.state === 'pending'
      },
      currentRegisterStation () {
        return this.context.currentRegisterClient?.registerStation
      },
      canVoid () {
        return _.some(this.orderItems?.data, 'canVoid') && !this.isDisputed
      },
      canCancel () {
        return this.state === 'pending'
      },
      isEmptyOpenTab () {
        return this.state === 'open_tab' && !this.hasOrderItems
      },
      consumerRefundable () {
        if (!this.configuration.currentSite.allowConsumerRefunds) {
          return { isRefundable: false, reason: 'Consumer refunds are not allowed at this site' }
        }

        const hasRefundableItems = _.some(this.operativeOrderItems, orderItem => orderItem.consumerRefundable.isRefundable)

        if (hasRefundableItems) {
          return { isRefundable: true, reason: null }
        }

        const nonRefundableItem = _.find(this.operativeOrderItems, orderItem => !orderItem.consumerRefundable.isRefundable)
        if (nonRefundableItem) {
          return { isRefundable: false, reason: nonRefundableItem.consumerRefundable.reason }
        }

        return { isRefundable: false, reason: 'No refundable items' }
      },
      consumerRefundableAmount () {
        return _.sumBy(this.operativeOrderItems, orderItem => orderItem.consumerRefundable.isRefundable ? orderItem.total : 0)
      },
      waitingForPreparationPrinting () {
        return _.some(this.orderItems?.data, { pickPrepPrintingStatus: 'waiting' })
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      },
      hasTicketPackageWithRequiredModifierSets () {
        return _.some(this.ticketPackageWithRequiredModifierSets)
      },
      ticketPackageWithRequiredModifierSets () {
        return _.filter(this.allPackagesWithRequiredModifierSets, group => !_.isEmpty(group.ticketOrderItems))
      },
      hasAnyPackagesWithRequiredModifierSetsUnfulfilled () {
        return _.some(this.allPackagesWithRequiredModifierSets, group => {
          return _.some(group.itemOrderItems, itemOrderItem => {
            return itemOrderItem.requiredItemModifierSets.length - itemOrderItem.selectedRequiredItemModifierSets.length > 0
          })
        })
      },
      allPackagesWithRequiredModifierSets () {
        const groups = {}

        for (const orderItem of this.draftOrderItems) {
          if (!orderItem.packageGroupIdentifier) continue

          if (orderItem.type === ORDER_ITEM_TYPES.TICKET) {
            groups[orderItem.packageGroupIdentifier] ||= {}
            groups[orderItem.packageGroupIdentifier].ticketOrderItems ||= []
            groups[orderItem.packageGroupIdentifier].ticketOrderItems.push(orderItem)
          } else if (orderItem.type === ORDER_ITEM_TYPES.ITEM && !_.isEmpty(orderItem.requiredItemModifierSets)) {
            groups[orderItem.packageGroupIdentifier] ||= {}
            groups[orderItem.packageGroupIdentifier].itemOrderItems ||= []
            groups[orderItem.packageGroupIdentifier].itemOrderItems.push(orderItem)
          }
        }

        return _.filter(groups, group => !_.isEmpty(group.itemOrderItems))
      },
      errors () {
        let errors = []

        if (this.action === ORDER_ACTIONS.VOID || this.action === ORDER_ACTIONS.START_SWAP) {
          if (!this.canVoid) {
            return ['Cannot void, no items in voidable state']
          }

          return
        }

        if (this.action === ORDER_ACTIONS.CLAIM_REFUND) {
          let completedUnclaimedRefundTotal = currency(0)
          let draftTotal = currency(0)
          _.each(this.payments.data, payment => {
            if (payment.state === 'completed' && payment.type === 'unclaimed-refund') {
              completedUnclaimedRefundTotal = completedUnclaimedRefundTotal.add(payment.amount)
            } else if (payment.state === 'draft') {
              draftTotal = draftTotal.add(payment.amount)
            }
          })

          if (completedUnclaimedRefundTotal.value >= 0) {
            return ['No refund to claim']
          }
          if (draftTotal.value !== 0) {
            return ['Refund amount does not match unclaimed refund amount']
          }
        }

        if (this.purchasingShowing && !this.allowPastShows && this.purchasingShowing.localPast && this.state !== 'open_tab' && (!this.purchasingShowing.movie.allowPastSales || this.purchasingShowing.showingFinished)) {
          return ['Cannot place order, show time is in the past']
        }

        if (this.showing) {
          const ticketErrors = [...this.ticketCountErrors, ...this.duplicateSeatTicketErrors]
          if (ticketErrors.length) {
            return ticketErrors
          }
        }
        if (this.seatChartErrors.length) return this.seatChartErrors
        errors = errors.concat(_(this.operativeOrderItems).filter({ state: 'draft' }).map('errors').compact().value())

        if (errors.length === 0) {
          return null
        } else {
          return errors
        }
      },
      errorsByShowing () {
        return _(this.allShowings)
          .keyBy('id')
          .mapValues(showing => {
            const errors = _(this.showingTicketOrderItems(showing)).map('errors').compact().value()

            if (errors.length === 0) {
              return null
            } else {
              return errors
            }
          })
          .value()
      },
      seatChartErrors () {
        const errors = []
        const partialCouchSaleError = _.map(this.allShowings, showing => {
          return showing.screen?.seatChart?.hasPartialCouchSaleError(this.allSelectedSeats(showing))
        })
        if (_.includes(partialCouchSaleError, true)) {
          errors.push('Partial couch sale error. Please re-select seats.')
        }
        return errors
      },
      deliveryLocationLabel () {
        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE) {
          return 'Table'
        } else if (this.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
          return 'Screen'
        } else {
          return this.i18n.tc('siteLocation.location', 1)
        }
      },
      deliveryPositionLabel () {
        if (this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE) {
          return 'Position'
        } else if (this.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
          return 'Seat'
        } else {
          return 'Position'
        }
      },
      lastTimePlaced () {
        const lastPlacedAt = _.last(
          _(this.orderItems.data)
            .sortBy('placedAt')
            .map(({ placedAt }) => placedAt ? new IsoDateTime(placedAt).inTimeZone(this.configuration.currentSite.timeZone) : null)
            .compact().value()
        )

        if (!lastPlacedAt) return null

        const currentTime = this.configuration.currentSite.siteTime
        const showDate = this.i18n.d(currentTime, 'shortDate') !== this.i18n.d(lastPlacedAt, 'shortDate')

        if (showDate) {
          return `${this.i18n.d(lastPlacedAt, 'shortDate')} ${this.i18n.d(lastPlacedAt, 'shortTime')}`
        } else {
          const lastPlacedAtWasMoreThanAnHourAgo = ((currentTime - lastPlacedAt) / (1000 * 60)) > 60
          return lastPlacedAtWasMoreThanAnHourAgo ? this.i18n.d(lastPlacedAt, 'shortTime') : lastPlacedAt.relativeTime({ relativeTo: currentTime, compact: true })
        }
      },
      unclaimedRefund () {
        let completedUnclaimedRefundTotal = currency(0)
        _.each(this.payments.data, payment => {
          if (payment.state === 'completed' && payment.type === 'unclaimed-refund') {
            completedUnclaimedRefundTotal = completedUnclaimedRefundTotal.add(payment.amount)
          }
        })

        const unclaimedRefundValue = completedUnclaimedRefundTotal.value
        if (unclaimedRefundValue < 0) {
          return `Unclaimed Refund: ${this.i18n.n(Math.abs(unclaimedRefundValue), 'currency')}`
        }

        return null
      },
      allowConsumerPlaceOpenTab () {
        if (_.isNull(this.deliveryLocation)) return false

        if (!this.fetchedByQrDeliveryDetails && !this.startedViaQrCode) return false

        const resolvedLocation = _.find(this.configuration.deliveryLocations.data, { name: this.deliveryLocation })

        if (!resolvedLocation) return false

        if (!resolvedLocation.allowOpenTabWithQrCode) return false

        return _.every(this.orderItems.data, 'allowedInConsumerTab')
      },
      hasMultipleTicketsForShowing () {
        return _.some(Object.values(_.groupBy(this.ticketOrderItems, orderItem => orderItem.ticket?.showing?.id)), itemsForShowing => itemsForShowing.length > 1)
      },
      hasCardableMissingRequiredCard () {
        const isAnyCardableMissingRequiredCard = _.some(this.orderItems.data, orderItem => {
          let cardable
          if (orderItem.type === ORDER_ITEM_TYPES.VOUCHER_PURCHASE || orderItem.type === ORDER_ITEM_TYPES.VOUCHER_SERVICE_ISSUE) {
            cardable = orderItem.voucherPurchase
          } else if (orderItem.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE) {
            cardable = orderItem.issuedVoucherPackage
          } else if (orderItem.type === ORDER_ITEM_TYPES.GIFT_CARD) {
            cardable = orderItem.giftCard
          } else {
            return false
          }

          return cardable.requiresCard
        })

        return isAnyCardableMissingRequiredCard
      },
      allowPlaceOrder () {
        return !this.hasCardableMissingRequiredCard && this.orderItems.data.length !== 0
      },
      canTransferTab () {
        // open tab orders can be transferred only by their employee or an admin
        if (!this.openTab) return false
        if (!this.context.currentUser) return false
        if (this.employee?.id !== this.context.currentUser?.id && !this.context.currentUser?.admin) return false
        return true
      },
      meerkatMovieOrderItems () {
        return _.filter(this.orderItems.data, orderItem => orderItem.isMeerkatMovieTicket)
      },
      meerkatMovieVouchers () {
        return _.filter(this.independentVouchers?.data, voucher => voucher.voucherType.isMeerkatMovieValue)
      },
      hasMeerkatMovieVoucher () {
        return !_.isEmpty(this.meerkatMovieVouchers)
      },
      hasMeerkatMovieVoucherApplied () {
        const meerkatMovieVoucherIds = _.map(this.meerkatMovieVouchers, 'id')
        return _.some(this.orderItems.data, orderItem => _.includes(meerkatMovieVoucherIds, orderItem.voucher?.id))
      },
      isValidMeerkatMovieOrder () {
        return this.hasMeerkatMovieVoucher && !_.isEmpty(this.meerkatMovieOrderItems)
      },
      isValidCeaOrder () {
        // For CEA cards, we need at least 1 CEA ticket and 1 other ticket to the same showing
        const ceaTicketForShowing = _.find(this.orderItems?.data, orderItem =>
          orderItem.type === ORDER_ITEM_TYPES.TICKET &&
          orderItem.ticket?.ticketType?.isCeaCard
        ) || null
        if (_.isNil(ceaTicketForShowing)) return false

        const otherTicketForShowing = _.find(this.orderItems?.data, orderItem =>
          orderItem.type === ORDER_ITEM_TYPES.TICKET &&
          orderItem.ticket?.showing?.id === ceaTicketForShowing.ticket?.showing?.id &&
          orderItem.id !== ceaTicketForShowing?.id
        ) || null
        if (_.isNil(otherTicketForShowing)) return false

        return true
      },
      hasIncompleteActivityBookings () {
        return _.some(this.activityBookings.data, { state: BOOKING_STATES.DRAFT, canBePlaced: false })
      }
    }

    this.queries = {
      orderForCustomTemplate: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          printTemplateId: {
            type: ID,
            required: true
          }
        },
        results: {
          order: Order
        }
      },
      setupIntentForOrderLookup: {
        arguments: {
        },
        results: {
          value: Generic
        }
      },
      findOrderByToken: {
        arguments: {
          token: {
            type: String,
            required: true
          }
        },
        results: {
          order: Order
        }
      },
      findShowingByStripePaymentMethod: {
        arguments: {
          stripePaymentMethodId: {
            type: String,
            required: true
          }
        },
        results: {
          value: Generic
        }
      },
      findShowingByOrderId: {
        arguments: {
          orderId: {
            type: ID,
            required: true
          }
        },
        results: {
          value: Generic
        }
      },
      findOrderByQrDelivery: {
        arguments: {
          deliveryType: {
            type: String,
            required: true
          },
          deliveryLocation: {
            type: String,
            required: true
          },
          deliveryPosition: {
            type: String,
            required: true
          }
        },
        results: {
          value: Generic
        }
      },
      findOrderByTicket: {
        arguments: {
          ticketNumber: {
            type: String,
            required: true
          }
        },
        results: {
          order: Order
        }
      },
      pendingTabTransfers: {
        arguments: {
          incomingEmployeeId: {
            type: ID,
            required: true
          },
          rangeStart: {
            type: String,
            required: true
          }
        },
        results: {
          count: Generic
        }
      }
    }

    this.watchers = [
      [
        function () {
          return [...(this.orderItems?.data || [])]
        },
        function (newOrderItems, oldOrderItems) {
          if (this.transient || this.destroyed || this.destroyRequested || !isPos) return

          const isEnabled = this.context.isFeatureEnabled(this.$featureFlags.automatic_package_assignment)

          if (!isEnabled) return

          newOrderItems = _.first(newOrderItems)
          oldOrderItems = _.first(oldOrderItems)

          const itemTypesToProcess = [ORDER_ITEM_TYPES.TICKET, ORDER_ITEM_TYPES.ITEM]

          const orderItemsToAdd = _.filter(_.differenceBy(newOrderItems, oldOrderItems, 'id'), orderItem => itemTypesToProcess.includes(orderItem.type) && !orderItem.paid && !orderItem.voided)
          const orderItemsToRemove = _.filter(_.differenceBy(oldOrderItems, newOrderItems, 'id'), orderItem => itemTypesToProcess.includes(orderItem.type))

          if (_.isNil(this.packageWorker)) {
            try {
              this.packageWorker = new Worker(new URL('../../workers/packageProcessor.worker.js', import.meta.url))
            } catch (error) {
              console.error(`[PACKAGE_PROCESSOR] Error initializing order package worker: ${error.message}`)
              this.$rollbar.error('Error initializing order package worker', error)
              return
            }

            this.packageWorker.onmessage = (event) => {
              this.processPackageWorkerMessage(event.data)
            }

            this.packageWorker.onerror = (error) => {
              console.error(`[PACKAGE_PROCESSOR] Error processing order package worker: ${error.message}`)
              this.$rollbar.error('package worker error', error)
            }

            this.initializePackageProcessor()
          }

          this.packageWorker.postMessage({
            messageType: CONSUMER_MESSAGE_TYPES.PROCESS_ITEMS,
            orderItemsToAdd: _.map(orderItemsToAdd, orderItem => ({ id: orderItem.id, type: orderItem.type, typeId: orderItem.type === ORDER_ITEM_TYPES.TICKET ? orderItem.ticket.ticketType.id : orderItem.itemOrderItem.itemVariation.id, excludedPackageIds: [...orderItem.excludedItemPackageIds], assignedPackageId: orderItem.itemPackageId })),
            orderItemsToRemove: _.map(orderItemsToRemove, orderItem => ({ id: orderItem.id, type: orderItem.type, typeId: orderItem.type === ORDER_ITEM_TYPES.TICKET ? orderItem.ticket.ticketType.id : orderItem.itemOrderItem.itemVariation.id, excludedPackageIds: [...orderItem.excludedItemPackageIds], assignedPackageId: orderItem.itemPackageId }))
          })
        }
      ],
      [
        function () { return this.id },
        async function () {
          // Temp fix for notes persisting when the order is reset
          if (this.notes) this.notes = null
        }
      ],
      [
        function () { return this.draftOrderItemValidationMessages },
        async function () {
          const newValidations = _.difference(this.draftOrderItemValidationMessages, this.previousDraftOrderItemValidationMessages)
          if (newValidations.length && !this.preventOrderItemValidationsDialog) {
            this.previousDraftOrderItemValidationMessages = this.draftOrderItemValidationMessages
            this.$mh.showErrors(newValidations)
          }
          this.preventOrderItemValidationsDialog = false
        }
      ],
      [
        function () { return this?.user?.id },
        async function () {
          if (this.currentMembershipDiscounts?.data) this.currentMembershipDiscounts.data = []
          this.setupCurrentMemberDiscounts()
          this.applyRedemptions()
        }
      ],
      [
        function () { return [this?.orderItems?.data, this.user?.loading] },
        async function () {
          this.setupCurrentMemberDiscounts()
          this.applyRedemptions()
        }
      ],
      [
        function () { return this.deliveryType },
        function () {
          this.deliveryTypeSetManually = (
            !!this.deliveryType &&
            /**
             * Virtual is always set automatically.
             */
            this.deliveryType !== DELIVERY_TYPE_OPTIONS.VIRTUAL &&
            this.deliveryType !== this.defaultDeliveryType
          )

          if (_.isNil(this.deliveryLocation)) return

          if (!_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, this.deliveryType)) {
            this.deliveryLocation = null
          } else {
            const deliveryLocation = _.find(this.configuration.deliveryLocations.data, { name: this.deliveryLocation })
            if (deliveryLocation && this.deliveryType !== deliveryLocation.type) {
              this.deliveryLocation = null
            }
          }
        }
      ],
      [
        function () { return this.defaultDeliveryType },
        async function () {
          // Wait for deliveryType watcher to run
          // There is a race condition where the defaultDeliveryType is computed before the deliveryType watcher runs
          // This skips the deliveryTypeSetManually check and overrides the deliveryType when inflating the order
          // await nextTick()
          // Reverting this for now as has caused issues where the delivery type for order items is not being set

          if (this.deliveryTypeSetManually) return

          this.deliveryType = this.defaultDeliveryType
        },
        { immediate: true }
      ],
      [
        function () { return this.deliveryLocation },
        function () {
          this.deliveryLocationSetManually = (
            !!this.deliveryLocation &&
            this.deliveryLocation !== this.defaultDeliveryLocation &&
            (_.isEmpty(this.deliveryLocationOptions) || _.isNil(_.find(this.deliveryLocationOptions, { name: this.deliveryLocation })))
          )

          if (_.isEmpty(this.deliveryPositions)) return

          if (_.isNil(this.deliveryLocation)) {
            this.deliveryPositions = []
            return
          }

          const deliveryLocation = _.find(this.configuration.deliveryLocations.data, { name: this.deliveryLocation })
          if (_.isNil(deliveryLocation)) {
            this.deliveryPositions = []
            return
          }

          const hasInvalidDeliveryPositions = !_.isEmpty(_.without(this.deliveryPositions, ...deliveryLocation.positions))
          if (hasInvalidDeliveryPositions) {
            this.deliveryPositions = []
          }
        }
      ],
      [
        function () { return this.defaultDeliveryLocation },
        function () {
          if (this.deliveryLocationSetManually) return

          this.deliveryLocation = this.defaultDeliveryLocation
        },
        { immediate: true }
      ],
      [
        function () { return this.defaultDeliveryPositions },
        function () {
          if (this.deliveryPositionsSetManually || (!this.isLocal && (this.state === ORDER_STATES.OPEN_TAB || this.startedViaQrCode) && !_.isEmpty(this.deliveryPositions))) return

          if (_.isEqual(this.deliveryPositions, this.defaultDeliveryPositions)) return

          this.deliveryPositions = [...this.defaultDeliveryPositions]
        },
        { immediate: true }
      ],
      [
        function () { return this.deliveryPositions },
        function () {
          if (_.isEmpty(this.deliveryPositions)) {
            this.deliveryPositionsSetManually = false
          } else {
            this.deliveryPositionsSetManually = !_.isEqual(this.deliveryPositions || [], this.defaultDeliveryPositions || [])
          }
        }
      ],
      [
        function () { return this.defaultHasDueTime },
        function () { this.hasDueTime = this.defaultHasDueTime }
      ],
      [
        function () {
          return this.defaultRemoteOrder
        },
        function () { this.remoteOrder = this.defaultRemoteOrder },
        {
          immediate: true
        }
      ],
      [
        function () { return this.defaultDueTime },
        function (defaultDueTime, previousDefaultDueTime) {
          if (!this.dueTime || !_.isEqual(defaultDueTime, previousDefaultDueTime)
          ) {
            this.dueTime = this.defaultDueTime
          }
        }
      ],
      [
        function () { return [this?.orderItems?.data] },
        function () {
          if (!this.orderItems) return

          const isMembershipOrder = _.some(this.orderItems.data, { type: ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL })

          if (!isMembershipOrder) return

          const isOrderItemAllowedInMembershipOrder = (type) => !_.includes(ORDER_ITEM_TYPES.ALLOWED_IN_MEMBERSHIP_ORDER, type)
          const anyRestrictedOrderItems = _.some(this.orderItemsWithoutEmptyDonations, orderItem => isOrderItemAllowedInMembershipOrder(orderItem.type))

          if (anyRestrictedOrderItems) {
            Dialog.create({
              message: 'Complete membership order first'
            })
            this.orderItems.filter(orderItem => _.includes(ORDER_ITEM_TYPES.ALLOWED_IN_MEMBERSHIP_ORDER, orderItem.type))
          }
        }
      ],
      [
        function () { return [this.subtotal, this.orderItems?.data, this.configuration.bookingFeeTypes?.data] },
        function () {
          if (!this.orderItems) return

          const orderBookingFeeOrderItem = _.find(this.orderItems.data, { type: ORDER_ITEM_TYPES.BOOKING_FEE, subtype: BOOKING_FEE_TYPES.ORDER })
          if (!_.isNil(orderBookingFeeOrderItem) && !orderBookingFeeOrderItem.draft) return

          const computedBookingFeeType = orderBookingFeeComputer.compute(this)
          if (_.isNil(computedBookingFeeType) && !_.isNil(orderBookingFeeOrderItem)) {
            this.orderItems.remove(orderBookingFeeOrderItem)
          } else if (!_.isNil(computedBookingFeeType) && !_.isNil(orderBookingFeeOrderItem) && computedBookingFeeType.id !== orderBookingFeeOrderItem.bookingFeeTypeId) {
            orderBookingFeeOrderItem.bookingFeeTypeId = computedBookingFeeType.id
            orderBookingFeeOrderItem.bookingFeeType = computedBookingFeeType
          } else if (!_.isNil(computedBookingFeeType) && _.isNil(orderBookingFeeOrderItem)) {
            this.orderItems.add({
              type: ORDER_ITEM_TYPES.BOOKING_FEE,
              subtype: BOOKING_FEE_TYPES.ORDER,
              bookingFeeTypeId: computedBookingFeeType.id,
              bookingFeeType: computedBookingFeeType,
              transient: this.transient,
              order: this
            })
          }
        }
      ],
      [
        function () { return [this.voidedOrderItemsNeedingVoucherTypeSwap, this.independentVouchers?.data] },
        function () {
          _(this.voidedOrderItemsNeedingVoucherTypeSwap)
            // IGNORE ORDER ITEMS WHERE WE'VE ALREADY STARTED SWAPPING
            .filter(orderItem => !_.find(this.independentVouchers?.data, voucher => voucher.swapFromOrderItemId === orderItem.id))
            .groupBy('voucherType.id')
            .entries()
            .each(([voucherTypeId, voidedOrderItems]) => {
              _.each(voidedOrderItems, voidedOrderItem => {
                const voucher = new Voucher({
                  voucherTypeId,
                  isManual: true,
                  autoCreate: true,
                  autoApply: true,
                  swapFromOrderItemId: voidedOrderItem.id
                }, { voucherType: true })
                this.addIndependentVoucher(voucher)
              })

              this.applyRedemptions()
            })
        }
      ],
      [
        function () { return this.user },
        function () {
          if (_.isNil(this.user)) {
            _.each(this.activityTicketOrderItems, orderItem => {
              if (!_.isNil(orderItem.activityTicket.user)) {
                orderItem.activityTicket.user = null
              }
            })
          }
        }
      ]
    ]

    this.mutations = {
      printOrderReceipt: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      },
      printBulkOrdersReceipt: {
        arguments: {
          ids: {
            type: [ID],
            required: true
          },
          printMerchantCopy: {
            type: Boolean,
            required: true
          }
        },
        results: {
          success: Boolean,
          preAuthErrors: [String]
        }
      },
      saveTipAdjustments: {
        arguments: {
          tipByOrderId: {
            type: JsonObject,
            required: true
          }
        },
        results: {
          success: Boolean,
          errorsByOrderId: JsonObject
        }
      },
      printOrderInvoice: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      },
      printKioskOrder: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      },
      splitTabOrder: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          orderItemIds: {
            type: [ID],
            required: true
          },
          splitByPosition: {
            type: Boolean,
            required: false
          }
        },
        results: {
          success: Boolean,
          id: ID,
          error: String
        }
      },
      printOrderCustomTemplate: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          printTemplateId: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      },
      emailOrderReceipt: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          email: {
            type: String,
            required: false
          }
        },
        results: {
          success: Boolean
        }
      },
      emailOrderInvoice: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          email: {
            type: String,
            required: false
          }
        },
        results: {
          success: Boolean
        }
      },
      emailOrderCustomTemplate: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          printTemplateId: {
            type: ID,
            required: true
          },
          email: {
            type: String,
            required: false
          }
        },
        results: {
          success: Boolean
        }
      },
      printOrderItems: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          orderItemIds: {
            type: [ID],
            required: false
          },
          groupTicket: {
            type: Boolean,
            required: false
          }
        },
        results: {
          success: Boolean
        }
      },
      emailOrderItems: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          email: {
            type: String,
            required: false
          },
          orderItemIds: {
            type: [ID],
            required: false
          }
        },
        results: {
          success: Boolean
        }
      },
      tabTransferUpdate: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          employeeId: {
            type: ID,
            required: false
          },
          action: {
            type: String,
            required: true
          }
        },
        results: {
          success: Boolean,
          employeeId: ID,
          incomingEmployeeId: ID
        }
      },
      addInteracRefundToOrder: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          stripeRefundId: {
            type: String,
            required: true
          }
        },
        results: {
          success: Boolean,
          error: String
        }
      },
      sendSmsOrderReceipt: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          deliveryPhone: {
            type: String,
            required: true
          },
          deliveryPhoneCountryCode: {
            type: String,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      },
      updatePreAuthAmounts: {
        arguments: {
          ids: {
            type: [ID],
            required: true
          }
        },
        results: {
          success: Boolean,
          errorsByOrderId: JsonObject
        }
      },
      placeOrdersAndProcessPayments: {
        arguments: {
          ids: {
            type: [ID],
            required: true
          }
        },
        results: {
          success: Boolean,
          errorsByOrderId: JsonObject
        }
      },
      claimQrOrder: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean
        }
      }
    }

    this.fragments = {
      handheldTabSearch: ['id', 'orderUserFirstName', 'orderUserLastName', 'orderUserDefaultPaymentMethodId', 'deliveryType', 'deliveryLocation', 'deliveryPositions', 'deliveryFirstName', 'deliveryLastName', 'identifyWithDeliveryInfo', 'computedRemainingOwed', 'savedPaymentMethodId', 'userId']
    }

    this.paginate = true
  }

  get specificMembershipVoucher () {
    return _.find(this.appliedVouchers.data, voucher => voucher.voucherType?.type === VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP) ||
      _.find(this.independentVouchers.data, voucher => voucher.voucherType?.type === VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP)
  }

  beforeDestroy () {
    this.destroyRequested = true
    _.each(this.generatedOrderItems, orderItem => orderItem.destroy())
    if (this.packageWorker) {
      try {
        this.packageWorker.terminate()
      } catch (e) {
        this.$rollbar.error('Error terminating order package worker', e)
      } finally {
        this.packageWorker = null
      }
    }
  }

  sortRedemptions (redemptions) {
    // Sort according to application priority, item specificity, and estimated redemption value
    const typeNamePriority = ['Voucher', 'VoucherType', 'Discount']

    return _.sortBy(
      redemptions,
      redemption => {
        const typeNamePriorityValue = typeNamePriority.indexOf(redemption.typeName)

        // Prioritize item package vouchers above all other vouchers
        // This is because this voucher may apply to multiple items - we don't want some items to use this voucher and others not
        const itemPackagePriorityModifier = redemption.typeName === 'Voucher' && redemption.voucherType?.itemPackage ? 0 : 1

        // Prioritize specific item vouchers above all remaining vouchers
        // The item specificity is required for the auto apply that adds a specific items if it's not in the cart to work properly
        const specificItemPriorityModifier = redemption.typeName === 'Voucher' && redemption.voucherType.type === VOUCHER_TYPE_TYPES.SPECIFIC_ITEM ? 0 : 1

        // Naively estimate the quality of ticket redemptions based on average ticket price
        let estimatedRedemptionScore = 0
        if ((redemption.type === 'ticket' || redemption.voucherType?.type === 'ticket') && this.averageTicketPrice > 0) {
          if (redemption.typeName === 'Voucher' && redemption.voucherType.type === 'ticket') {
            // Estimate the price to be paid when redeeming this redemption
            const estimatedRedemptionPrice = Math.max(redemption.voucherType.hasMinPrice ? redemption.voucherType.minPrice : 0, redemption.voucherType.hasMaxPrice ? this.averageTicketPrice - redemption.voucherType.maxPrice : 0)
            // Estimate the percentage of ticket price to be paid when redeeming this redemption
            // A higher percentage of the ticket price paid = a worse redemption
            estimatedRedemptionScore = 100 - Math.round(estimatedRedemptionPrice / this.averageTicketPrice * 100)
          } else if (redemption.typeName === 'VoucherType' && redemption.type === 'ticket') {
            const estimatedRedemptionPrice = Math.max(redemption.hasMinPrice ? redemption.minPrice : 0, redemption.hasMaxPrice ? this.averageTicketPrice - redemption.maxPrice : 0)

            estimatedRedemptionScore = 100 - Math.round(estimatedRedemptionPrice / this.averageTicketPrice * 100)
          } else if (redemption.typeName === 'Discount' && redemption.type === 'ticket' && redemption.amountType === 'percent') {
            estimatedRedemptionScore = 100 - Math.round(redemption.percent)
          } else if (redemption.typeName === 'Discount' && redemption.type === 'ticket' && redemption.amountType === 'fixed_amount') {
            const estimatedRedemptionPrice = Math.max(this.averageTicketPrice - redemption.amount, 0)

            estimatedRedemptionScore = Math.round(estimatedRedemptionPrice / this.averageTicketPrice * 100)
          } else if (redemption.typeName === 'Discount' && redemption.type === 'ticket' && redemption.amountType === 'target_amount') {
            estimatedRedemptionScore = Math.round(redemption.amount / this.averageTicketPrice * 100)
          }
        } else {
          if (redemption.typeName === 'Discount' && redemption.amountType === 'percent') {
            estimatedRedemptionScore = 100 - Math.round(redemption.percent)
          } else if (redemption.typeName === 'Discount' && redemption.amountType === 'fixed_amount') {
            estimatedRedemptionScore = Math.max(50 - redemption.amount, 0)
          }
        }

        return `${typeNamePriorityValue}${itemPackagePriorityModifier}${specificItemPriorityModifier}${estimatedRedemptionScore}`
      }
    )
  }

  async reset () {
    _.each(this.generatedOrderItem, orderItem => orderItem.destroy())
    this.generatedOrderItems = []
    return super.reset()
  }

  isVoucherApplied (voucher) {
    return !!_.find(this.independentVouchers.data, appliedVoucher => appliedVoucher.id === voucher.id)
  }

  addCashOutItems () {
    const cashOutOrderItems = _.filter(this.orderItems.data, orderItem => orderItem.isCashOut && orderItem.description === 'Charge for Cash Withdrawal' && orderItem.state === 'draft')
    let existingCashOutItem = _.find(this.orderItems.data, orderItem => orderItem.isCashOut && orderItem.description === 'Credit for Cash Withdrawal' && orderItem.state === 'draft')
    let existingCashoutPayment = _.find(this.payments.data, payment => payment.subtype === 'cashout' && payment.state === 'draft')
    const cashOutTotal = _.sum(_.map(cashOutOrderItems, 'amount')) || 0

    if (cashOutTotal === 0) return

    if (!existingCashOutItem) {
      existingCashOutItem = new OrderItem({
        type: 'misc-item',
        description: 'Credit for Cash Withdrawal',
        voucher: null,
        voucherType: null,
        discount: null,
        isCashOut: true
      })
      this.orderItems.add(existingCashOutItem)
    }
    if (!existingCashoutPayment) {
      existingCashoutPayment = new Payment({
        type: 'cash',
        subtype: 'cashout'
      })
      this.payments.add(existingCashoutPayment)
    }

    existingCashoutPayment.amount = cashOutTotal * -1
    existingCashOutItem.amount = cashOutTotal * -1
  }

  applyReward (reward, skipApplyRedemptions = false) {
    const previouslyAppliedReward = _.find(this.appliedRewards?.data || [], { voucherTypeId: reward.id, isReward: true })
    if (previouslyAppliedReward && this.applyVoucher(previouslyAppliedReward, skipApplyRedemptions)) {
      // Applied reward, no further action needed
    } else {
      this.appliedRewards.add({
        voucherTypeId: reward.id,
        isReward: true
      })
      if (!skipApplyRedemptions) {
        this.applyRedemptions()
      }
    }
  }

  setRecapchaToken (token) {
    this.recaptchaToken = token
    const truncatedToken = token ? _.truncate(token, { length: 15 }) : token
    if (this.context?.isFeatureEnabled(this.$featureFlags.extra_recaptcha_info_logs)) {
      this.$rollbar.info('Recaptcha token set', { token: truncatedToken })
    }
  }

  applyVoucher (voucher, skipApplyRedemptions = false) {
    if (_.includes(this.appliedVouchers, voucher)) return false

    // Meerkat Movies vouchers should be applied to the cheapest ticket first
    const sortOrder = voucher.voucherType.isMeerkatMovieValue ? ['desc', 'asc'] : ['desc', 'desc']
    const sortedOrderItems = _.orderBy(this.unpaidOrderItems, ['skipVoucher', 'price'], sortOrder)

    const orderItem = _.find(sortedOrderItems, orderItem => {
      // Only support one voucher
      if (orderItem.voucher || orderItem.voucherType) return false

      return voucher.voucherType.validateVoucher(orderItem, voucher)
    })

    if (orderItem) {
      orderItem.skipVoucher = false
      voucher.autoApply = true
      if (!skipApplyRedemptions) {
        this.applyRedemptions()
      }
      return true
    }
    return false
  }

  resetChangeDuePayments () {
    _.each(this.payments.data, payment => {
      payment.forceShowChangeDue = false
    })
  }

  applyDiscount (discount) {
    const sortedOrderItems = _.sortBy(this.unpaidOrderItems, orderItem => -orderItem.price)

    const allOrderItems = _.filter(sortedOrderItems, orderItem => {
      if (orderItem.discountId !== null) return false

      if (orderItem.manualDiscount && !_.isEmpty(orderItem.discountGroupKey)) return false

      if (discount.orderItemPackageOnly && !(orderItem.itemPackageId && _.includes(_.map(orderItem.itemPackage.discounts.data, 'id'), discount.id))) return false

      const discountValidation = discount.validateDiscount(orderItem, discount.orderItemPackageOnly, !this.isIndependentDiscount(discount), true, false)
      return discountValidation
    })

    const availableOrderItems = _.filter(allOrderItems, orderItem => !orderItem.manualDiscount && !discount.limitByQuantityEnabled)

    if (discount.limitByQuantityEnabled) {
      if (discount.type === ORDER_ITEM_TYPES.TICKET && _.includes([LIMIT_BY_QUANTITY_FILTERS.SHOWING, LIMIT_BY_QUANTITY_FILTERS.MOVIE], discount.limitByQuantityFilter)) {
        const distinctShowingOrMovieCount = _(allOrderItems).map(orderItem => discount.limitByQuantityFilter === LIMIT_BY_QUANTITY_FILTERS.SHOWING ? orderItem?.ticket?.showing?.id : orderItem?.ticket?.showing?.movie?.id).uniq().compact().value().length
        if (distinctShowingOrMovieCount < discount.numberOfMatchingItemsRequired) return false
      }

      if (discount.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET) {
        if (_.includes([LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_SESSION, LIMIT_BY_QUANTITY_FILTERS.ACTIVITY], discount.limitByQuantityFilter)) {
          const distinctActivitySessionOrActivityCount = _(allOrderItems).map(oi => {
            switch (discount.limitByQuantityFilter) {
              case LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_SESSION:
                return oi?.activityTicket?.activitySession?.id
              case LIMIT_BY_QUANTITY_FILTERS.ACTIVITY:
                return oi?.activityTicket?.activitySession?.activity?.id
              default:
                return null
            }
          }).uniq().compact().value().length

          /**
           * If we don't have enough matching items, return
           */
          if (distinctActivitySessionOrActivityCount < discount.numberOfMatchingItemsRequired) return
        } else if (discount.limitByQuantityFilter === LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_TICKET) {
          const distinctActivityTicketCounts = _(allOrderItems).map(orderItem => orderItem.activityTicket?.id).groupBy(oi => {
            switch (discount.limitByQuantityScope) {
              case LIMIT_BY_QUANTITY_SCOPES.ACTIVITY_SESSION:
                return oi?.activityTicket?.activitySession?.id
              case LIMIT_BY_QUANTITY_SCOPES.ACTIVITY:
                return oi?.activityTicket?.activitySession?.activity?.id
              default:
                /**
                 * Unsupported limit by quantity scope
                 */
                return null
            }
          }).values().map(orderItems => {
            return _(orderItems).map(oi => oi.activityTicket?.id).uniq().compact().value().length
          }).value()

          /**
           * If we don't have enough matching items, return
           */
          if (_.every(distinctActivityTicketCounts, (distinctActivityTicketCount) => distinctActivityTicketCount < discount.numberOfMatchingItemsRequired)) return
        } else {
          /**
           * Unsupported limit by quantity filter
           */
          return
        }
      }

      const currentAvailableItems = _.filter(availableOrderItems, oi => (!oi.forceApplyDiscountId || oi.forceApplyDiscountId === discount.id) && !oi.discountGroupKey && !_.includes(oi.explicitDiscountRemovalIds, discount.id)).length
      const itemsToUpdate = _.filter(availableOrderItems, oi => !oi.discountGroupKey && _.includes(oi.explicitDiscountRemovalIds, discount.id)).slice(0, discount.numberOfMatchingItemsRequired - currentAvailableItems)
      _.each(itemsToUpdate, oi => {
        oi.explicitDiscountRemovalIds = _.filter(oi.explicitDiscountRemovalIds, id => id !== discount.id)
        oi.forceApplyDiscountId = null
        oi.manualDiscount = false
      })
      if (currentAvailableItems + itemsToUpdate.length < discount.numberOfMatchingItemsRequired) {
        let remainingItemsToUpdate = _.filter(this.unpaidOrderItems, orderItem => _.includes(orderItem.explicitDiscountRemovalIds, discount.id)).slice(0, discount.numberOfMatchingItemsRequired - (currentAvailableItems + itemsToUpdate.length))
        remainingItemsToUpdate = _.filter([...remainingItemsToUpdate, ...this.unpaidOrderItems], oi => oi.forceApplyDiscountId !== discount.id).slice(0, discount.numberOfMatchingItemsRequired - (currentAvailableItems + itemsToUpdate.length))
        _.each(remainingItemsToUpdate, oi => {
          oi.forceApplyDiscountId = discount.id
          oi.explicitDiscountRemovalIds = _.filter(oi.explicitDiscountRemovalIds, id => id !== discount.id)
          oi.manualDiscount = false
        })
      }
    } else if (allOrderItems.length) {
      allOrderItems[0].manualDiscount = false
      allOrderItems[0].forceApplyDiscountId = discount.id
    }

    this.applyRedemptions()
  }

  addIndependentVoucher (voucher) {
    if (_.some(this.appliedVouchers, { id: voucher.id }) || _.some(this.allUnappliedVouchers, { id: voucher.id })) return
    this.independentVouchers.data = _.unionBy(this.independentVouchers.data, [voucher], 'id')
    this.applyRedemptions()
  }

  addVoucherPackage (voucherPackage, apply = true) {
    let count = 0
    _.each(voucherPackage.vouchers.data, voucher => {
      if (voucher.state !== 'valid') return

      if (voucher.redeemedAt) return

      if (this.isVoucherApplied(voucher)) return

      if (apply) {
        this.addIndependentVoucher(voucher)
      }
      ++count
    })

    if (apply) {
      Notify.create({
        message: count + ' Vouchers Applied to Order',
        type: 'positive',
        position: 'top'
      })
      return
    }

    return count
  }

  applyIndependentVoucher (voucherToApply) {
    const voucher = _.find(this.independentVouchers.data, ['uid', voucherToApply.uid])
    if (voucher) {
      voucher.autoApply = true
      this.applyRedemptions()
    } else {
      this.addIndependentVoucher(voucherToApply)
    }
  }

  addIndependentDiscount (discount) {
    discount.independent = true
    discount.orderItemPackageOnly = false
    this.independentDiscounts.data = _.unionBy(this.independentDiscounts.data, [discount], 'id')
    this.applyRedemptions()
  }

  removeIndependentDiscount (discount) {
    this.independentDiscounts.remove(discount)
    this.applyRedemptions()
  }

  addNote (note) {
    this.notes ||= ''

    const newNote = [
      this.configuration.currentSite.siteTime.format('YYYY-MM-DD HH:mm:ss'),
      this.context?.currentUser?.name,
      note
    ].join(' - ')

    this.notes = `${this.notes}\n\n${newNote}`
  }

  excludePackageFromOrderItem (orderItem) {
    orderItem.excludedItemPackageIds.push(orderItem.itemPackageId)
    this.packageWorker.postMessage({
      messageType: CONSUMER_MESSAGE_TYPES.EXCLUDE_PACKAGE,
      orderItemId: orderItem.id,
      packageId: orderItem.itemPackageId
    })
  }

  initializePackageProcessor () {
    const { packageToItemMap, ticketTypeToPackagesMap, itemVariationToPackagesMap } = this.configuration.currentSite.packageReferenceMap

    this.packageWorker.postMessage({
      messageType: CONSUMER_MESSAGE_TYPES.INITIALIZE,
      packageToItemMap: _.cloneDeep(packageToItemMap),
      ticketTypeToPackagesMap: _.cloneDeep(ticketTypeToPackagesMap),
      itemVariationToPackagesMap: _.cloneDeep(itemVariationToPackagesMap)
    })
  }

  addGiftCard (giftCard) {
    const distinctGiftCards = _.unionBy(this.giftCards.data, [giftCard], 'id')
    this.giftCards.setData(distinctGiftCards)
  }

  canAddItemPackage (itemPackage, showing) {
    let totalTicketsInPackage = 0
    const ticketTypesInPackage = _(itemPackage.itemPackageItems.data).map('ticketType').compact().groupBy('id').value()
    const itemPackageTicketRemainingValidation = _.map(Object.keys(ticketTypesInPackage), ticketTypeId => {
      // Grouped by ticket type, so first ticketType in array is the same as the others
      const ticketType = ticketTypesInPackage[ticketTypeId][0]
      const numberOfTickets = ticketTypesInPackage[ticketTypeId].length
      totalTicketsInPackage += numberOfTickets
      return (this.ticketsRemaining(showing, ticketType) >= numberOfTickets)
    })
    const packageTicketsLessOrEqualTicketsRemaining = !(_.includes(itemPackageTicketRemainingValidation, false))
    return packageTicketsLessOrEqualTicketsRemaining && this.ticketsRemaining(showing) >= totalTicketsInPackage
  }

  addItemPackage (itemPackage, showing, user = null, transient = false) {
    if (showing) {
      const ticketTypesInPackage = _(itemPackage.itemPackageItems.data).map('ticketType').compact().groupBy('id').value()
      const itemPackageTicketRemainingValidation = _.map(Object.keys(ticketTypesInPackage), ticketTypeId => {
        // Grouped by ticket type, so first ticketType in array is the same as the others
        const ticketType = ticketTypesInPackage[ticketTypeId][0]
        const numberOfTickets = ticketTypesInPackage[ticketTypeId].length
        return (this.ticketsRemaining(showing, ticketType) >= numberOfTickets)
      })
      if (!transient && _.includes(itemPackageTicketRemainingValidation, false)) return
    }
    const items = this.createItemPackages(itemPackage, showing, user, transient)

    const isPackageExisting = this.findPackageTicketOrderItem(showing, itemPackage)

    const packageIdentifier = `${itemPackage.id}-${this.packageCounter++}`

    _.each(items, (item) => {
      item.packageGroupIdentifier = packageIdentifier
      this.orderItems.add(item)
    })

    if (!isPackageExisting) {
      this.independentDiscounts.data = [...this.independentDiscounts.data, ...itemPackage.discountsForOrder]
    }

    return items
  }

  defaultItemModifiersForItem (item) {
    if (_.isNil(item)) return []

    return _(item.itemModifierSets.data).flatMap(itemModifierSet =>
      _.map(itemModifierSet.defaultModifiers.data, defaultModifier => {
        return new ItemOrderItemModifier({
          modifierId: defaultModifier.id,
          modifierSetId: itemModifierSet.modifierSetId
        })
      })
    ).value()
  }

  createItemPackageItemVariationOrderItem (itemVariation, itemPackage, { activityBookingId = null, transient = false, user = null } = {}) {
    return new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: itemVariation.itemId,
        itemVariationId: itemVariation.id,
        itemModifiers: this.defaultItemModifiersForItem(itemVariation.item),
        user,
        activityBookingId
      },
      itemPackageId: itemPackage.id,
      transient
    })
  }

  createItemPackages (itemPackage, showing, user = null, transient = false) {
    // Sort by ticketTypeId so the first entry here is always a ticket type (expected by SelectUsers.vue)
    return _(itemPackage.itemPackageItems.data).sortBy('ticketTypeId').map((packageItem) => {
      if (packageItem.itemVariation) {
        return this.createItemPackageItemVariationOrderItem(
          packageItem.itemVariation,
          itemPackage,
          {
            transient,
            user
          }
        )
      } else if (packageItem.ticketType) {
        const marathonTickets = []
        if (showing.isMarathon) {
          _.each(showing.showings.data, marathonShowing => {
            marathonTickets.push(new Ticket({
              showing: marathonShowing,
              ticketTypeId: packageItem.ticketType.id,
              user
            }))
          })
        } else if (showing.hasMarathon) {
          marathonTickets.push(new Ticket({
            showing: showing.showing,
            ticketTypeId: packageItem.ticketType.id,
            user
          }))
        }

        return new OrderItem({
          member: { name: 'Your Guest' },
          type: 'ticket',
          ticket: {
            showing,
            ticketTypeId: packageItem.ticketType.id,
            user,
            tickets: marathonTickets
          },
          itemPackageId: itemPackage.id,
          transient
        })
      }
    }).compact().value()
  }

  createItemPackageOrderItemsForActivity (itemPackage, activityBookingId, { user = null }) {
    return _(itemPackage.itemPackageItems.data)
      .map(itemPackageItem => {
        if (itemPackageItem.ticketType) {
          return this.createActivityTicketOrderItem(
            activityBookingId,
            itemPackageItem.ticketType.id,
            {
              user,
              itemPackageId: itemPackage.id
            }
          )
        } else if (itemPackageItem.itemVariation) {
          return this.createItemPackageItemVariationOrderItem(
            itemPackageItem.itemVariation,
            itemPackage,
            {
              activityBookingId,
              user
            }
          )
        } else {
          return null
        }
      })
      .compact()
      .value()
  }

  createItemPackageOrderItemsForActivityAndAddToOrder (itemPackage, activityBookingId, { user = null }) {
    _.each(
      this.createItemPackageOrderItemsForActivity(itemPackage, activityBookingId, { user }),
      orderItem => {
        this.orderItems.add(orderItem)
      }
    )
  }

  removeItemPackageOrderItemsForActivity (itemPackage, activityBookingId, { user = null }) {
    const itemPackageTicketTypeOrderItems = []
    const itemPackageItemVariationOrderItems = []

    _(itemPackage.itemPackageItems.data)
      .each(itemPackageItem => {
        if (itemPackageItem.ticketType) {
          const ticketTypeOrderItem = _(this.itemPackageActivityTicketOrderItems).reverse().find(orderItem => {
            // Exclude items that have already been added
            if (_.some(itemPackageTicketTypeOrderItems, { id: orderItem.id })) return false

            // Exclude items that don't match the user, if provided
            if (
              (_.isNil(user) && !_.isNil(orderItem.activityTicket.user)) ||
              (!_.isNil(user) && orderItem.activityTicket.user?.id !== user.id)
            ) return false

            return (
              orderItem.itemPackageId === itemPackage.id &&
              orderItem.activityTicket.ticketTypeId === itemPackageItem.ticketType.id &&
              orderItem.activityTicket.activityBookingId === activityBookingId
            )
          })

          if (ticketTypeOrderItem) {
            itemPackageTicketTypeOrderItems.push(ticketTypeOrderItem)
          }
        } else if (itemPackageItem.itemVariation) {
          const itemVariationOrderItem = _(this.itemPackageItemVariationOrderItems).reverse().find(orderItem => {
            if (_.some(itemPackageItemVariationOrderItems, { id: orderItem.id })) return false

            // Exclude items that don't match the user, if provided
            if (
              (_.isNil(user) && !_.isNil(orderItem.itemOrderItem.user)) ||
              (!_.isNil(user) && orderItem.itemOrderItem.user?.id !== user.id)
            ) return false

            return (
              orderItem.itemPackageId === itemPackage.id &&
              orderItem.itemOrderItem.itemVariationId === itemPackageItem.itemVariation.id &&
              orderItem.itemOrderItem.activityBookingId === activityBookingId
            )
          })

          if (itemVariationOrderItem) {
            itemPackageItemVariationOrderItems.push(itemVariationOrderItem)
          }
        }
      })

    this.orderItems.removeAll(itemPackageTicketTypeOrderItems.concat(itemPackageItemVariationOrderItems))
  }

  addDeliveryPosition (position) {
    if (!this.deliveryPositions.includes(position)) {
      this.deliveryPositionsSetManually = true
      this.deliveryPositions.push(position)
      this.deliveryPositions.sort()
    }
  }

  removeDeliveryPosition (index) {
    this.deliveryPositions.splice(index, 1)
    this.deliveryPositions.sort()

    if (this.deliveryPositions.length === 0) {
      this.deliveryPositionsSetManually = false
    }
  }

  resetDeliveryPositions () {
    if (!this.deliveryPositions?.length) return

    this.deliveryPositions = []
  }

  resetRedemptions () {
    _.each(this.unpaidOrderItems, orderItem => {
      // We need to ensure any remote discounts added are not removed when fetching a remote order
      if (isPos && orderItem.discountId && orderItem.isRemote && !orderItem.manualDiscount && !orderItem.discount.appliesToAllOrders && !orderItem.discount.orderItemPackageOnly) {
        if (!_.some(this.independentDiscounts.data, { id: orderItem.discountId })) {
          this.independentDiscounts.data = [...this.independentDiscounts.data, orderItem.discount]
        }
      }

      if (!orderItem.manualDiscount) {
        orderItem.discountId = null
        orderItem.discountGroupKey = null
      }

      const previouslyAppliedVoucherType = orderItem.voucherType
      const previouslyAppliedVoucherIsReward = !_.isNil(previouslyAppliedVoucherType) && previouslyAppliedVoucherType.isReward && orderItem.pointsUsed === previouslyAppliedVoucherType.pointsCost
      const isRemoteAndNoLocalVoucher = _.isNil(orderItem.localVoucher) && orderItem.isRemote
      const reApplyReward = isPos && !_.isNil(this.user) && previouslyAppliedVoucherIsReward && isRemoteAndNoLocalVoucher

      orderItem.voucher = null
      orderItem.localVoucher = null
      orderItem.voucherType = null
      orderItem.pointsUsed = 0
      this.availableLimitRestrictedDiscounts.data = []

      // Reapply reward if it was previously applied and is a remote reward
      // This is to handle adding vouchers then saving the order, so the reward is not lost when reselecting the order
      if (reApplyReward) {
        this.applyReward(previouslyAppliedVoucherType, true)
      }
    })
  }

  resetTransientRedemptions (orderItem) {
    if (!orderItem.transient) return
    orderItem.voucher = null
    orderItem.localVoucher = null
    orderItem.voucherType = null
    orderItem.pointsUsed = 0
    orderItem.discountGroupKey = null
  }

  removeDiscount (orderItem) {
    if (orderItem.discountGroupKey) {
      const orderItemsDiscountGroup = _.filter(this.orderItems.data, { discountGroupKey: orderItem.discountGroupKey })
      if (orderItem.discount.limitByQuantityType === LIMIT_BY_QUANTITY_TYPES.ALL_ITEMS.value) {
        _(orderItemsDiscountGroup).take(orderItem.discount.numberOfMatchingItemsRequired).each(discountGroupOrderItem => {
          discountGroupOrderItem.explicitDiscountRemovalIds.push(orderItem.discountId)
          discountGroupOrderItem.manualDiscount = false
          discountGroupOrderItem.discountId = null
          discountGroupOrderItem.discountGroupKey = null
          discountGroupOrderItem.forceApplyDiscountId = null
        })
      } else {
        _.each(orderItemsDiscountGroup, discountGroupOrderItem => {
          discountGroupOrderItem.explicitDiscountRemovalIds.push(orderItem.discountId)
          discountGroupOrderItem.manualDiscount = false
          discountGroupOrderItem.discountId = null
          discountGroupOrderItem.discountGroupKey = null
          discountGroupOrderItem.forceApplyDiscountId = null
        })
      }
    } else {
      orderItem.manualDiscount = true
      orderItem.discountGroupKey = null
      orderItem.discountId = null
    }

    this.applyRedemptions()
  }

  // When changing this function ensure changes are not also required in applyTransientRedemptions
  applyRedemptions () {
    if (isAdmin) return

    if (this.transient) return

    if (!this.orderItems) return

    // Clear all redemptions
    this.resetRedemptions()

    let discountsToRevalidate = []

    // Ensure vouchers are applied to highest price items first
    const sortedOrderItems = _.sortBy(this.unpaidOrderItems, orderItem => -orderItem.price)

    // Meerkat Movies vouchers should be applied to the cheapest ticket first
    let reverseSortedOrderItems = sortedOrderItems
    if (this.configuration.currentSite.meerkatMovieEnabledValue) {
      reverseSortedOrderItems = _.reverse(_.sortBy(this.unpaidOrderItems, orderItem => -orderItem.price))
    }

    // Apply redemptions
    _.each(this.availableRedemptions, redemption => {
      // See if there is an applicable order item to add voucher to
      if (redemption.typeName === 'Voucher') {
        const voucher = redemption

        if (voucher.autoApply === false) return

        const availableOrderItems = voucher?.voucherType?.isMeerkatMovieValue ? reverseSortedOrderItems : sortedOrderItems

        // Find the first matching order item for this voucher
        const orderItemMatches = _.filter(availableOrderItems, orderItem => {
          if (orderItem.voucher || orderItem.voucherType) return false

          if (orderItem.skipVoucher) return false

          return voucher.voucherType.validateVoucher(orderItem, voucher)
        })

        let orderItem = orderItemMatches.length ? orderItemMatches[0] : null

        if (voucher.voucherType.limitByQuantityEnabled) {
          const nonVoucheredItems = _.filter(orderItemMatches, oi => !oi.voucher && !oi.voucherType && !oi.skipVoucher && !oi.discountGroupKey)
          orderItem = _.first(orderItemMatches) || null

          const isValid = validateQuantityLimits({
            redemption: voucher,
            orderItem,
            nonDiscountedItems: nonVoucheredItems,
            orderItemMatches,
            unpaidOrderItems: this.unpaidOrderItems
          })

          if (!isValid) return
        }

        let itemsToApply = []

        if (!orderItem && (!voucher.isManual || voucher.autoCreate) && voucher.voucherType.type === VOUCHER_TYPE_TYPES.SPECIFIC_ITEM && voucher.voucherType.itemVariation && !this.purchasingMembership) {
          // If this is a specific order item and no matching one is found, add it now
          orderItem = new OrderItem({
            type: 'item',
            itemOrderItem: {
              itemVariationId: voucher.voucherType.itemVariationId,
              item: voucher.voucherType.itemVariation.itemId
            }
          })

          const itemVariation = voucher.voucherType.itemVariation
          const item = voucher.voucherType.itemVariation.item

          // TODO OOS INDY-6757 Update availability logic
          // Don't auto-apply items that aren't available online for consumer sites
          if (isConsumer && !item.availableOnline) return

          // At the kiosk, if the item is "direct to customer" (doesn't require prep)
          // then don't auto-apply it to the order
          if (isKiosk && !item.requiresPreparation) return

          itemsToApply[0] = new OrderItem({
            type: 'item',
            itemOrderItem: {
              itemId: item.id,
              itemVariationId: itemVariation.id || _.filter(item.itemVariations.data, 'isUnarchived')[0].id,
              itemModifiers: this.defaultItemModifiersForItem(item)
            },
            voucher: null,
            voucherType: null,
            discount: null
          })

          this.orderItems.add(itemsToApply[0])
        } else if (orderItem && voucher.voucherType.type === VOUCHER_TYPE_TYPES.ITEM_PACKAGE && voucher.voucherType.itemPackage) {
          itemsToApply = this.findAllItemsForPackage(voucher.voucherType.itemPackage, true)
          if (_.compact(itemsToApply).length !== itemsToApply.length) {
            itemsToApply = []
          }
        } else if (!orderItem && (!voucher.isManual || voucher.autoCreate) && voucher.voucherType.type === VOUCHER_TYPE_TYPES.ITEM_PACKAGE && voucher.voucherType.itemPackage?.itemOnlyPackage) {
          itemsToApply = this.addItemPackage(voucher.voucherType.itemPackage, null)
        } else if (orderItem) {
          itemsToApply = [orderItem]
        }

        if (itemsToApply.length) {
          _.each(itemsToApply, itemToApply => {
            // Apply voucher
            if (voucher.isRemote) {
              // This is a server side voucher that needs to be redeemed
              itemToApply.voucher = voucher
              itemToApply.localVoucher = voucher
            } else if (voucher.isReward) {
              // This is a client side generated voucher for points redemption
              itemToApply.voucherType = voucher.voucherType
              itemToApply.localVoucher = voucher
              itemToApply.pointsUsed = voucher.voucherType.pointsCost
            } else if (voucher.isManual) {
              // This is a client side generated voucher added from a button press
              itemToApply.voucherType = voucher.voucherType
              itemToApply.localVoucher = voucher
              if (voucher.swapFromOrderItemId) {
                const voidedOrderItem = _.find(this.voidedOrderItemsNeedingVoucherTypeSwap, orderItem => orderItem.id === voucher.swapFromOrderItemId)
                if (voidedOrderItem) voidedOrderItem.voucherTypeSwapped = true
              }
            } else {
              // This is a client side generated voucher for unlimited tickets
              itemToApply.voucherType = voucher.voucherType
              itemToApply.localVoucher = voucher
            }

            itemToApply.voucherUserId = voucher.user?.id || this.user?.id
          })
        }
      } else if (redemption.typeName === 'Discount') {
        // Apply discount to highest price matching order item
        const discount = redemption

        if (discount.limitByQuantityEnabled) {
          discountsToRevalidate = _.uniq([...discountsToRevalidate, ...[discount]])
        }

        let orderItemMatches = _.filter(sortedOrderItems, orderItem => {
          if (discount.orderItemPackageOnly && !(orderItem.itemPackageId && _.includes(_.map(orderItem.itemPackage.discounts.data, 'id'), discount.id))) return

          return discount.validateDiscount(orderItem, discount.orderItemPackageOnly, !this.isIndependentDiscount(discount), false, false)
        })

        if (orderItemMatches.length) {
          // Limit the number of order items the discount applies to based on:
          // 1. How many times the given discount can be applied per showing
          // 2. How many times the given discount can be applied per user (based on membership benefits)
          let discountRedemptionLimit = null

          if (discount.membershipDiscount && discount.membershipDiscount.limitRedemptions) {
            discountRedemptionLimit = discount.membershipDiscount.redemptionsLimit - (discount.user.discountRedemptions[discount.id] ?? 0) - (this.userDiscountRedemptions[discount.user.id]?.[discount.id]?.count ?? 0)

            if (discountRedemptionLimit <= 0) return
          }

          if (discountRedemptionLimit) {
            orderItemMatches = _.slice(orderItemMatches, 0, discountRedemptionLimit)
          }

          _.each(orderItemMatches, orderItem => {
            let discountUser = discount.user

            if (discount.limitRedemptionsByOrderEnabled && (this.appliedDiscountCount[discount.id] || 0) >= discount.orderRedemptionsLimit) return

            if (orderItem.forceApplyDiscountId && orderItem.forceApplyDiscountId !== discount.id) return

            if (discount.limitRedemptionsByShowingEnabled) {
              const ticketUser = orderItem.ticket?.user
              if (!discountUser && ticketUser) {
                discountUser = ticketUser
              } else if (
                discountUser &&
                ticketUser &&
                ticketUser.id !== discountUser.id &&
                ticketUser.membershipType?.id === discountUser.membershipType?.id &&
                ticketUser.membershipTier?.id === discountUser.membershipTier?.id
              ) {
                const associates = _.concat(discountUser.purchasees?.data, discountUser.membership?.members?.data)
                const associatedUser = _.find(associates, user => user.id === ticketUser.id)
                discountUser = associatedUser || discountUser
              }

              if (discountUser) {
                const orderItemShowing = orderItem.localShowing
                if (orderItemShowing) {
                  const discountsUsed = this.userDiscountRedemptions[discountUser.id]?.[discount.id]?.byShowing[orderItemShowing.id]?.count ?? 0

                  discountRedemptionLimit = discount.showingRedemptionsLimit - discountsUsed
                  const discountRedemptionsByShowing = discountUser.redemptionsByShowing?.discounts[discount.id]
                  if (discountRedemptionsByShowing) {
                    const showingDiscountRedemptionsCount = discountRedemptionsByShowing[orderItemShowing.id]?.count ?? 0
                    if ((discountRedemptionLimit - showingDiscountRedemptionsCount) <= 0) return

                    if (discount.limitRedemptionsByShowingIncludesOverlapsEnabled) {
                      const overlappingDiscountRedemptions = _.filter(_.values(discountRedemptionsByShowing), showing => {
                        const showingStartTime = new Date(showing.showing_overlap_start_time)
                        const showingEndTime = new Date(showing.showing_overlap_end_time)

                        // Overlaps if local showing start time is within redemption showing time
                        if (showingStartTime <= orderItemShowing.time && showingEndTime >= orderItemShowing.time) return true
                        // Overlaps if local showing end time is within redemption showing time
                        if (showingStartTime <= orderItemShowing.endTime && showingEndTime >= orderItemShowing.endTime) return true
                        // Overlaps if local showing start time is before redemption showing start time and local showing end time is after redemption showing end time
                        if (showingStartTime > orderItemShowing.time && showingEndTime < orderItemShowing.endTime) return true

                        return false
                      })
                      const overlappingDiscountRedemptionsCount = _.sumBy(overlappingDiscountRedemptions, 'count') ?? 0

                      discountRedemptionLimit -= overlappingDiscountRedemptionsCount
                      if (discountRedemptionLimit <= 0) return
                    } else {
                      discountRedemptionLimit -= showingDiscountRedemptionsCount
                    }
                  }
                }
              } else {
                const orderItemShowing = orderItem.localShowing
                if (orderItemShowing) {
                  const discountUsed = this.anonymousDiscountRedemptions[discount.id]?.byShowing[orderItemShowing.id]?.count ?? 0
                  const discountRedemptionsByShowing = this.anonymousDiscountRedemptions[discount.id]?.byShowing
                  discountRedemptionLimit = discountRedemptionsByShowing ? discount.showingRedemptionsLimit : discount.showingRedemptionsLimit - discountUsed
                  if (discountRedemptionsByShowing) {
                    const showingDiscountRedemptionsCount = discountRedemptionsByShowing[orderItemShowing.id]?.count ?? 0
                    if ((discountRedemptionLimit - showingDiscountRedemptionsCount) <= 0) return

                    if (discount.limitRedemptionsByShowingIncludesOverlapsEnabled) {
                      const overlappingDiscountRedemptions = _.filter(discountRedemptionsByShowing[orderItemShowing.id], showingData => {
                        const showingStartTime = new Date(showingData.showingOverlapStartTime)
                        const showingEndTime = new Date(showingData.showingOverlapEndTime)

                        // Overlaps if local showing start time is within redemption showing time
                        if (showingStartTime <= orderItemShowing.time && showingEndTime >= orderItemShowing.time) return true
                        // Overlaps if local showing end time is within redemption showing time
                        if (showingStartTime <= orderItemShowing.endTime && showingEndTime >= orderItemShowing.endTime) return true
                        // Overlaps if local showing start time is before redemption showing start time and local showing end time is after redemption showing end time
                        if (showingStartTime > orderItemShowing.time && showingEndTime < orderItemShowing.endTime) return true

                        return false
                      })
                      const overlappingDiscountRedemptionsCount = _.sumBy(overlappingDiscountRedemptions, 'count') ?? 0

                      discountRedemptionLimit -= overlappingDiscountRedemptionsCount
                      if (discountRedemptionLimit <= 0) return
                    } else {
                      discountRedemptionLimit -= showingDiscountRedemptionsCount
                    }
                  }
                }
              }

              if (discountRedemptionLimit !== null && discountRedemptionLimit <= 0) return
            }
            if (discount.limitRedemptionsByActivitySessionEnabled) {
              const activityTicket = orderItem.localActivityTicket
              if (activityTicket) {
                discountRedemptionLimit = discount.activitySessionRedemptionsLimit

                const existingDiscountRedemptionsForActivitySession = this.user?.redemptionsByActivitySession.discounts[discount.id]?.[activityTicket.activitySession.id]
                // eslint-disable-next-line camelcase
                const existingDiscountRedemptionsForActivitySessionCount = _(existingDiscountRedemptionsForActivitySession).map(({ entry_time, exit_time, count }) => {
                  if (!discount.limitRedemptionsByActivitySessionAdmissionTimes) {
                    return count
                  }

                  const activitySessionEntryTime = new Date(entry_time)
                  const activitySessionExitTime = new Date(exit_time)

                  // Overlaps if local activity ticket start time is within redemption activity ticket time
                  if (activitySessionEntryTime <= activityTicket.entryTime && activitySessionExitTime >= activityTicket.entryTime) return count
                  // Overlaps if local activity ticket end time is within redemption activity ticket time
                  if (activitySessionEntryTime <= activityTicket.exitTime && activitySessionExitTime >= activityTicket.exitTime) return count
                  // Overlaps if local activity ticket start time is before redemption activity ticket start time and local activity ticket end time is after redemption activity ticket end time
                  if (activitySessionEntryTime > activityTicket.entryTime && activitySessionExitTime < activityTicket.exitTime) return count

                  return null
                }).compact().sum()

                const currentDiscountRedemptionsForActivitySession = this.user
                  ? orderItem.order.userDiscountRedemptions[this.user.id][discount.id].byActivitySession[activityTicket.activitySession.id]
                  : orderItem.order.anonymousDiscountRedemptions[discount.id].byActivitySession[activityTicket.activitySession.id]
                // eslint-disable-next-line camelcase
                const currentDiscountRedemptionsForActivitySessionCount = _(currentDiscountRedemptionsForActivitySession).map(({ entry_time, exit_time, count }) => {
                  if (!discount.limitRedemptionsByActivitySessionAdmissionTimes) {
                    return count
                  }

                  const activitySessionEntryTime = new Date(entry_time)
                  const activitySessionExitTime = new Date(exit_time)

                  // Overlaps if local activity ticket start time is within redemption activity ticket time
                  if (activitySessionEntryTime <= activityTicket.entryTime && activitySessionExitTime >= activityTicket.entryTime) return count
                  // Overlaps if local activity ticket end time is within redemption activity ticket time
                  if (activitySessionEntryTime <= activityTicket.exitTime && activitySessionExitTime >= activityTicket.exitTime) return count
                  // Overlaps if local activity ticket start time is before redemption activity ticket start time and local activity ticket end time is after redemption activity ticket end time
                  if (activitySessionEntryTime > activityTicket.entryTime && activitySessionExitTime < activityTicket.exitTime) return count

                  return null
                }).compact().sum()

                const allDiscountRedemptionsForActivitySessionCount = existingDiscountRedemptionsForActivitySessionCount + currentDiscountRedemptionsForActivitySessionCount
                discountRedemptionLimit -= allDiscountRedemptionsForActivitySessionCount

                if (discountRedemptionLimit !== null && discountRedemptionLimit <= 0) return
              }
            }

            const nonDiscountedItems = _.filter(orderItemMatches, oi => (!oi.forceApplyDiscountId || oi.forceApplyDiscountId === discount.id) && !oi.discountGroupKey && !_.includes(oi.explicitDiscountRemovalIds, discount.id))

            if (discount.limitByQuantityEnabled) {
              const isValid = validateQuantityLimits({
                redemption: discount,
                orderItem,
                nonDiscountedItems,
                orderItemMatches,
                unpaidOrderItems: this.unpaidOrderItems
              })

              if (!isValid) return
            }

            orderItem.discountId = discount.id
            if (discount.membershipDiscount) {
              orderItem.discountUserId = discountUser.id
            } else {
              orderItem.discountUserId = this.user?.id || null
            }
          })
        }
      }
    })

    _.each(discountsToRevalidate, discount => {
      const orderItemMatches = _.filter(sortedOrderItems, orderItem => {
        if (discount.orderItemPackageOnly && !(orderItem.itemPackageId && _.includes(_.map(orderItem.itemPackage.discounts.data, 'id'), discount.id))) return

        return discount.validateDiscount(orderItem, discount.orderItemPackageOnly, !this.isIndependentDiscount(discount), true, true)
      })

      const nonDiscountedMatches = _.filter(orderItemMatches, { discountGroupKey: null })

      const itemsWithDiscount = _.filter(this.unpaidOrderItems, oi => oi.discountId === discount.id).length
      const itemsWithoutDiscount = _.filter(this.unpaidOrderItems, oi => oi.discountId !== discount.id).length

      if (discount.type === ORDER_ITEM_TYPES.TICKET && _.includes([LIMIT_BY_QUANTITY_FILTERS.SHOWING, LIMIT_BY_QUANTITY_FILTERS.MOVIE], discount.limitByQuantityFilter)) {
        const distinctShowingOrMovieCount = _(orderItemMatches).map(orderItem => discount.limitByQuantityFilter === LIMIT_BY_QUANTITY_FILTERS.SHOWING ? orderItem?.ticket?.showing?.id : orderItem?.ticket?.showing?.movie?.id).uniq().compact().value().length
        if (distinctShowingOrMovieCount < discount.numberOfMatchingItemsRequired) return
      }

      if (discount.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET) {
        if (_.includes([LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_SESSION, LIMIT_BY_QUANTITY_FILTERS.ACTIVITY], discount.limitByQuantityFilter)) {
          const distinctActivitySessionOrActivityCount = _(orderItemMatches).map(oi => {
            switch (discount.limitByQuantityFilter) {
              case LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_SESSION:
                return oi?.activityTicket?.activitySession?.id
              case LIMIT_BY_QUANTITY_FILTERS.ACTIVITY:
                return oi?.activityTicket?.activitySession?.activity?.id
              default:
                return null
            }
          }).uniq().compact().value().length

          /**
           * If we don't have enough matching items, return
           */
          if (distinctActivitySessionOrActivityCount < discount.numberOfMatchingItemsRequired) return
        } else if (discount.limitByQuantityFilter === LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_TICKET) {
          const distinctActivityTicketCounts = _(orderItemMatches).map(orderItem => orderItem.activityTicket?.id).groupBy(oi => {
            switch (discount.limitByQuantityScope) {
              case LIMIT_BY_QUANTITY_SCOPES.ACTIVITY_SESSION:
                return oi?.activityTicket?.activitySession?.id
              case LIMIT_BY_QUANTITY_SCOPES.ACTIVITY:
                return oi?.activityTicket?.activitySession?.activity?.id
              default:
                /**
                 * Unsupported limit by quantity scope
                 */
                return null
            }
          }).values().map(orderItems => {
            return _(orderItems).map(oi => oi.activityTicket?.id).uniq().compact().value().length
          }).value()

          /**
           * If we don't have enough matching items, return
           */
          if (_.every(distinctActivityTicketCounts, (distinctActivityTicketCount) => distinctActivityTicketCount < discount.numberOfMatchingItemsRequired)) return
        } else {
          /**
           * Unsupported limit by quantity filter
           */
          return
        }
      }

      if (discount.limitByQuantityType === LIMIT_BY_QUANTITY_TYPES.ALL_ITEMS.value && _.find(this.unpaidOrderItems, oi => oi.discount === discount) && nonDiscountedMatches.length >= 1) {
        this.availableLimitRestrictedDiscounts.push(discount)
      } else if (discount.limitByQuantityType === LIMIT_BY_QUANTITY_TYPES.ALL_ITEMS.value && orderItemMatches.length + itemsWithDiscount >= discount.numberOfMatchingItemsRequired && itemsWithoutDiscount >= 1) {
        this.availableLimitRestrictedDiscounts.push(discount)
      } else if (discount.limitByQuantityType === LIMIT_BY_QUANTITY_TYPES.LAST_ITEM.value && orderItemMatches.length - (itemsWithDiscount * discount.numberOfMatchingItemsRequired) >= discount.numberOfMatchingItemsRequired) {
        this.availableLimitRestrictedDiscounts.push(discount)
      }
    })
  }

  // When changing this function ensure changes are not also required in applyRedemptions
  applyTransientRedemptions () {
    if (!this.transient) return

    if (!this.orderItems) return

    // Redemptions aren't allowed for membership purchases
    if (this.purchasingMembership) return

    // Reset discount limits
    this.availableLimitRestrictedDiscounts = []

    // Only apply redemptions to transient order items (non-transient are real items in the cart)
    const transientOrderItems = _.filter(this.unpaidOrderItems, 'transient')

    const vouchersByPackage = {}
    const discountsByPackage = {}

    // Apply redemptions
    _.each(transientOrderItems, orderItem => {
      this.resetTransientRedemptions(orderItem)

      const memberId = orderItem.itemOrderItem?.user?.id || orderItem.ticket?.user?.id || 'anonymous'

      if (orderItem.itemPackageId) {
        vouchersByPackage[memberId] ||= {}
        vouchersByPackage[memberId][orderItem.itemPackageId] ||= []
        discountsByPackage[memberId] ||= {}
        discountsByPackage[memberId][orderItem.itemPackageId] ||= {}
      }

      const voucherRedemption = _.find(this.allUnappliedVouchers, voucher => {
        // Do not apply the same voucher twice to items in the same package
        if (orderItem.itemPackageId && _.includes(vouchersByPackage[memberId][orderItem.itemPackageId], voucher.id)) return

        return voucher.voucherType.validateVoucher(orderItem, voucher)
      })

      if (voucherRedemption) {
        if (orderItem.itemPackageId) {
          vouchersByPackage[memberId][orderItem.itemPackageId].push(voucherRedemption.id)
        }

        if (voucherRedemption.isRemote) {
          // This is a server side voucher that needs to be redeemed
          orderItem.voucher = voucherRedemption
          orderItem.localVoucher = voucherRedemption
        } else if (voucherRedemption.isReward) {
          // This is a client side generated voucher for points redemption
          orderItem.voucherType = voucherRedemption.voucherType
          orderItem.localVoucher = voucherRedemption
          orderItem.pointsUsed = voucherRedemption.voucherType.pointsCost
        } else if (voucherRedemption.isManual) {
          // This is a client side generated voucher added from a button press
          orderItem.voucherType = voucherRedemption.voucherType
          orderItem.localVoucher = voucherRedemption
        } else {
          // This is a client side generated voucher for unlimited tickets
          orderItem.voucherType = voucherRedemption.voucherType
          orderItem.localVoucher = voucherRedemption
        }
      }

      const sortedRedemptions = _.filter(this.sortRedemptions([...this.availableDiscounts, ..._.filter(this.availableRedemptions, redemption => redemption.typeName === 'Discount' && redemption.appliesToAllOrders)]))

      const discountRedemption = _.find(sortedRedemptions, discount => {
        let discountRedemptionLimit = null

        if (discount.limitRedemptionsByOrderEnabled && (this.appliedDiscountCount[discount.id] || 0) >= discount.orderRedemptionsLimit) return

        if (discount.membershipDiscount && discount.membershipDiscount.limitRedemptions) {
          const transientPackageDiscountsUsed = (orderItem.itemPackageId ? discountsByPackage[memberId][orderItem.itemPackageId][discount.id] : 0) ?? 0
          discountRedemptionLimit = discount.membershipDiscount.redemptionsLimit - (discount.user.discountRedemptions[discount.id] ?? 0) - (this.userDiscountRedemptions[discount.user.id]?.[discount.id]?.count ?? 0) - transientPackageDiscountsUsed
        }

        if (discount.limitRedemptionsByShowingEnabled && this.currentShowing) {
          let discountUser = discount.user
          const ticketUser = orderItem.ticket?.user

          if (
            !discountUser &&
            ticketUser
          ) {
            discountUser = ticketUser
          } else if (
            discountUser &&
            ticketUser &&
            ticketUser.id !== discountUser.id &&
            ticketUser.membershipType?.id === discountUser.membershipType?.id &&
            ticketUser.membershipTier?.id === discountUser.membershipTier?.id
          ) {
            const associates = _.concat(discountUser.purchasees?.data, discountUser.membership?.members?.data)
            const associatedUser = _.find(associates, user => user.id === ticketUser.id)
            discountUser = associatedUser || discountUser
          }
          const discountsUsed = discountUser ? this.userDiscountRedemptions[discountUser.id]?.[discount.id]?.byShowing[this.currentShowing.id]?.count : 0
          const transientPackageDiscountsUsed = (orderItem.itemPackageId ? discountsByPackage[memberId][orderItem.itemPackageId][discount.id] : 0) ?? 0
          const discountsRemaining = discount.showingRedemptionsLimit - discountsUsed - transientPackageDiscountsUsed

          discountRedemptionLimit = discountRedemptionLimit === null ? discountsRemaining : Math.min(discountRedemptionLimit, discountsRemaining)

          if (discountUser) {
            const orderItemShowing = orderItem.localShowing
            if (orderItemShowing) {
              const discountRedemptionsByShowing = discountUser.redemptionsByShowing?.discounts[discount.id]
              if (discountRedemptionsByShowing) {
                const showingDiscountRedemptionsCount = discountRedemptionsByShowing[orderItemShowing.id]?.count ?? 0

                if ((discountRedemptionLimit - showingDiscountRedemptionsCount) <= 0) {
                  return
                }
                if (discount.limitRedemptionsByShowingIncludesOverlapsEnabled) {
                  const overlappingDiscountRedemptions = _.filter(_.values(discountRedemptionsByShowing), showing => {
                    const showingStartTime = new Date(showing.showing_overlap_start_time)
                    const showingEndTime = new Date(showing.showing_overlap_end_time)

                    // Overlaps if local showing start time is within redemption showing time
                    if (showingStartTime <= orderItemShowing.time && showingEndTime >= orderItemShowing.time) return true
                    // Overlaps if local showing end time is within redemption showing time
                    if (showingStartTime <= orderItemShowing.endTime && showingEndTime >= orderItemShowing.endTime) return true
                    // Overlaps if local showing start time is before redemption showing start time and local showing end time is after redemption showing end time
                    if (showingStartTime > orderItemShowing.time && showingEndTime < orderItemShowing.endTime) return true

                    return false
                  })
                  const overlappingDiscountRedemptionsCount = _.sumBy(overlappingDiscountRedemptions, 'count') ?? 0

                  discountRedemptionLimit -= overlappingDiscountRedemptionsCount
                  if (discountRedemptionLimit <= 0) return
                } else {
                  discountRedemptionLimit -= showingDiscountRedemptionsCount
                }
              }
            }
          } else {
            const orderItemShowing = orderItem.localShowing
            if (orderItemShowing) {
              const discountUsed = this.anonymousDiscountRedemptions[discount.id]?.byShowing[orderItemShowing.id]?.count ?? 0
              const discountRedemptionsByShowing = this.anonymousDiscountRedemptions[discount.id]?.byShowing
              discountRedemptionLimit = discountRedemptionsByShowing ? discount.showingRedemptionsLimit : discount.showingRedemptionsLimit - discountUsed

              if (discountRedemptionsByShowing) {
                const showingDiscountRedemptionsCount = discountRedemptionsByShowing[orderItemShowing.id]?.count ?? 0
                if ((discountRedemptionLimit - showingDiscountRedemptionsCount) <= 0) return

                if (discount.limitRedemptionsByShowingIncludesOverlapsEnabled) {
                  const overlappingDiscountRedemptions = _.filter(discountRedemptionsByShowing[orderItemShowing.id], showingData => {
                    const showingStartTime = new Date(showingData.showingOverlapStartTime)
                    const showingEndTime = new Date(showingData.showingOverlapEndTime)

                    // Overlaps if local showing start time is within redemption showing time
                    if (showingStartTime <= orderItemShowing.time && showingEndTime >= orderItemShowing.time) return true
                    // Overlaps if local showing end time is within redemption showing time
                    if (showingStartTime <= orderItemShowing.endTime && showingEndTime >= orderItemShowing.endTime) return true
                    // Overlaps if local showing start time is before redemption showing start time and local showing end time is after redemption showing end time
                    if (showingStartTime > orderItemShowing.time && showingEndTime < orderItemShowing.endTime) return true

                    return false
                  })
                  const overlappingDiscountRedemptionsCount = _.sumBy(overlappingDiscountRedemptions, 'count') ?? 0

                  discountRedemptionLimit -= overlappingDiscountRedemptionsCount
                  if (discountRedemptionLimit <= 0) return
                } else {
                  discountRedemptionLimit -= showingDiscountRedemptionsCount
                }
              }
            }
          }
        }

        const orderItemMatches = _.filter(this.orderItems.data, oi => {
          if (discount.orderItemPackageOnly && !(oi.itemPackageId && _.includes(_.map(orderItem.itemPackage?.discounts?.data, 'id'), discount.id))) return

          if (oi.transient && oi !== orderItem) return

          return discount.validateDiscount(oi, discount.orderItemPackageOnly, !this.isIndependentDiscount(discount), false, true)
        })

        const nonDiscountedItems = _.filter(orderItemMatches, oi => (!oi.discountGroupKey || (oi.discountGroupKey && _.findIndex(sortedRedemptions, discount) <= _.findIndex(sortedRedemptions, oi.discount))) && !_.includes(oi.explicitDiscountRemovalIds, discount.id))

        if (discount.limitByQuantityEnabled) {
          if (_.includes(orderItem.explicitDiscountRemovalIds, discount.id)) return

          if (discount.type === ORDER_ITEM_TYPES.TICKET && _.includes([LIMIT_BY_QUANTITY_FILTERS.SHOWING, LIMIT_BY_QUANTITY_FILTERS.MOVIE], discount.limitByQuantityFilter)) {
            const distinctShowingOrMovieCount = _(orderItemMatches).map(orderItem => discount.limitByQuantityFilter === LIMIT_BY_QUANTITY_FILTERS.SHOWING ? orderItem?.ticket?.showing?.id : orderItem?.ticket?.showing?.movie?.id).uniq().compact().value().length
            if (distinctShowingOrMovieCount < discount.numberOfMatchingItemsRequired) return
          }

          if (discount.limitByQuantityType === LIMIT_BY_QUANTITY_TYPES.ALL_ITEMS.value) {
            if (orderItem.discountGroupKey && _.find(this.unpaidOrderItems, oi => oi !== orderItem && oi.discountGroupKey === orderItem.discountGroupKey && oi.discountId)) {
              // Do nothing, discount applies
            } else if (!orderItem.discountGroupKey && _.find(this.unpaidOrderItems, oi => oi.discount === discount)) {
              // An item already has this discount so this can apply
            } else if (nonDiscountedItems.length < discount.numberOfMatchingItemsRequired) {
              // Have not meet number of matching items required for discount to apply
              return
            } else {
              // Passed
            }
          } else {
            if (orderItem.discountGroupKey && _.some(transientOrderItems, orderItemMatch => orderItemMatch.discountGroupKey === orderItem.discountGroupKey && orderItemMatch.discount)) {
              // An order item in the same group that has a discount applied already exists.
              return
            }

            const matchLength = _.filter(this.unpaidOrderItems, oi => oi.discountGroupKey === orderItem.discountGroupKey).length

            if (orderItem.discountGroupKey && matchLength === discount.numberOfMatchingItemsRequired) {
              // We have a group but no item that holds discount, check if we are at the correct place in the array to consume discount
              if (_.findLast(transientOrderItems, oi => oi.discountGroupKey === orderItem.discountGroupKey) !== orderItem) return
            } else if (orderItem.discountGroupKey && matchLength < discount.numberOfMatchingItemsRequired && nonDiscountedItems.length + matchLength >= discount.numberOfMatchingItemsRequired) {
              // Some items that were previously in the group have been removed, we have enough matching items that are not in a group to fill that gap
            } else if (orderItem.discountGroupKey) {
              // Have a group with no discout assigned
              return
            } else if (!orderItem.discountGroupKey && nonDiscountedItems.length >= discount.numberOfMatchingItemsRequired) {
              // Assign group since we have enough matching items to group as discount
            } else {
              // Discount cannot apply, doesn't meet requirements
              return
            }
          }
        }

        if (discountRedemptionLimit !== null && discountRedemptionLimit <= 0) return

        if (discount.orderItemPackageOnly && !(orderItem.itemPackageId && _.includes(_.map(orderItem.itemPackage.discounts.data, 'id'), discount.id))) return

        return discount.validateDiscount(orderItem, discount.orderItemPackageOnly, !this.isIndependentDiscount(discount), false, false)
      })

      if (discountRedemption) {
        if (orderItem.itemPackageId) {
          discountsByPackage[memberId][orderItem.itemPackageId][discountRedemption.id] ||= 0
          discountsByPackage[memberId][orderItem.itemPackageId][discountRedemption.id]++
        }

        orderItem.discountId = discountRedemption.id
      }
    })
  }

  isIndependentDiscount (discount) {
    return _.includes(_.map(this.independentDiscounts.data, 'id'), discount.id)
  }

  getTicketTypeForMember (ticketTypes, member, fallbackTicketType, activityTicketOrderItems = []) {
    const ticketTypesGroupedByMatchSpecificity = {}

    _.each(ticketTypes, ticketType => {
      let matchSpecificity = 0

      if (
        /**
         * The member has already selected a ticket type
         */
        _.some(activityTicketOrderItems, orderItem => orderItem.activityTicket.ticketType.id === ticketType.id && orderItem.activityTicket.user.id === member.id)
      ) {
        matchSpecificity = 4
      } else if (
        /**
         * Membership type and user class both match.
         */
        ticketType.membershipType && ticketType.userClass &&
        ticketType.isMembershipTypeMatch(member.effectiveMembershipType?.id) &&
        ticketType.isUserClassMatch(member.userClass?.id)
      ) {
        matchSpecificity = 3
      } else if (
        /**
         * Just membership type matches.
         */
        ticketType.membershipType && !ticketType.userClass &&
        ticketType.isMembershipTypeMatch(member.effectiveMembershipType?.id)
      ) {
        matchSpecificity = 2
      } else if (
        /**
         * Just user class matches.
         */
        !ticketType.membershipType && ticketType.userClass &&
        ticketType.isUserClassMatch(member.userClass?.id)
      ) {
        matchSpecificity = 1
      }

      ticketTypesGroupedByMatchSpecificity[matchSpecificity] ||= []
      ticketTypesGroupedByMatchSpecificity[matchSpecificity].push(ticketType)
    })

    const bestMatchSpecificity = _(ticketTypesGroupedByMatchSpecificity).keys().max() || 0
    const bestMatchTicketType = _.first(ticketTypesGroupedByMatchSpecificity[bestMatchSpecificity]) || fallbackTicketType

    return [bestMatchTicketType, bestMatchSpecificity]
  }

  getItemPackageForMember (itemPackages, member, fallbackItemPackage, activityTicketOrderItems = []) {
    const itemPackagesGroupedByMatchSpecificity = {}

    _.each(itemPackages, itemPackage => {
      let matchSpecificity = 0
      const itemPackageTicketTypes = getTicketTypesFromItemPackage(itemPackage)

      if (
        /**
         * The member has already selected an item package
         */
        _.some(activityTicketOrderItems, orderItem => orderItem.itemPackage.id === itemPackage.id && orderItem.activityTicket.user.id === member.id)
      ) {
        matchSpecificity = 4
      } else if (
        /**
         * Membership type and user class both match.
         */
        itemPackageTicketTypes.every(ticketType => (
          ticketType.membershipType && ticketType.userClass &&
          ticketType.isMembershipTypeMatch(member.effectiveMembershipType?.id) &&
          ticketType.isUserClassMatch(member.userClass?.id)
        ))
      ) {
        matchSpecificity = 3
      } else if (
        /**
         * Just membership type matches.
         */
        itemPackageTicketTypes.every(ticketType => (
          ticketType.membershipType && !ticketType.userClass &&
          ticketType.isMembershipTypeMatch(member.effectiveMembershipType?.id)
        ))
      ) {
        matchSpecificity = 2
      } else if (
        /**
         * Just user class matches.
         */
        itemPackageTicketTypes.every(ticketType => (
          !ticketType.membershipType && ticketType.userClass &&
          ticketType.isUserClassMatch(member.userClass?.id)
        ))
      ) {
        matchSpecificity = 1
      }

      itemPackagesGroupedByMatchSpecificity[matchSpecificity] ||= []
      itemPackagesGroupedByMatchSpecificity[matchSpecificity].push(itemPackage)
    })

    const bestMatchSpecificity = _(itemPackagesGroupedByMatchSpecificity).keys().max() || 0
    const bestMatchItemPackage = _.first(itemPackagesGroupedByMatchSpecificity[bestMatchSpecificity]) || fallbackItemPackage

    return [bestMatchItemPackage, bestMatchSpecificity]
  }

  // TODO OOS INDY-6757 Move all of this logic out of the Model
  initializeDataOrderForShowing (user, showing, checkoutData = null) {
    // Looks for the best guess for what ticket a member would select for a showing and returns an array containing that ticket or package
    if (!user || (user.isLocal && !user.dayPassUser) || !showing) return

    const hasMembers = (user.membership?.members?.length || 0) + (user.purchasees?.length || 0) > 0

    let orderItemsData = []

    const fallbackTicketType = _.find(
      showing.ticketTypes(),
      ticketType => !ticketType.membershipType && ticketType.channels.website && (
        _.isNil(checkoutData) ||
        canSelectTicketTypeOrPackage(this, showing, ticketType, checkoutData.selectedSeatsForShowingId(showing.id), this.configuration?.currentSite?.consumerConfig?.showTicketFirstCheckoutFlow)
      )
    )
    const fallbackPackage = _.find(
      showing.packages('guest'),
      pack => pack.availableOnline && (
        _.isNil(checkoutData) ||
        canSelectTicketTypeOrPackage(this, showing, pack, checkoutData.selectedSeatsForShowingId(showing.id), this.configuration?.currentSite?.consumerConfig?.showTicketFirstCheckoutFlow)
      )
    )

    if (hasMembers) {
      const purchasableMembers = _.unionBy(user.membership.members.data, user.purchasees.data, 'id')
      orderItemsData = _(purchasableMembers).map(member => {
        const ticketTypes = showing.memberTicketTypes(member, false)
        const packageItems = showing.memberPackages(member)

        // Filter ticket types that are available for selection
        const availableTicketTypes = _.filter(ticketTypes, ticketType => {
          // Skip seat validation if no checkout data exists
          if (_.isNil(checkoutData)) {
            return true
          }

          // Get selected seats for this showing
          const selectedSeats = checkoutData.selectedSeatsForShowingId(showing.id)

          // Check if this ticket type can be selected with the current seats
          return canSelectTicketTypeOrPackage(this, showing, ticketType, selectedSeats, this.configuration?.currentSite?.consumerConfig?.showTicketFirstCheckoutFlow)
        })

        // Get the best matching ticket type for this member
        const [ticketTypeForUser] = this.getTicketTypeForMember(
          availableTicketTypes,
          member,
          fallbackTicketType
        )

        const itemPackageForUser = _.find(packageItems, pack => {
          return (
            _.isNil(checkoutData) ||
            canSelectTicketTypeOrPackage(this, showing, pack, checkoutData.selectedSeatsForShowingId(showing.id), this.configuration?.currentSite?.consumerConfig?.showTicketFirstCheckoutFlow)
          )
        })

        // Check if we have a member package that is more specific than the current ticket type
        if (ticketTypeForUser && !ticketTypeForUser.membershipType && itemPackageForUser) {
          const packageTicketTypes = _.compact(_.map(itemPackageForUser.itemPackageItems.data, packItem => packItem.ticketType))
          const isMemberPackage = _.every(packageTicketTypes, ticketType =>
            (ticketType.membershipType?.id === member.effectiveMembershipType?.id) &&
            (ticketType.userClass?.id === member.userClass?.id)
          )
          if (isMemberPackage) return this.createItemPackages(itemPackageForUser, showing, member)
        }

        // Handle case where we have no ticket types and only packages exist
        if (!ticketTypeForUser && itemPackageForUser) {
          return this.createItemPackages(itemPackageForUser, showing, member)
        }

        // If no ticket types use fallback package
        if (!ticketTypeForUser && fallbackPackage) return this.createItemPackages(fallbackPackage, showing, member)

        // No valid tickets exist
        if (!ticketTypeForUser) return null

        const marathonTickets = this.getMarathonTicketsForShowing(showing, ticketTypeForUser, member)

        const ticket = new Ticket({
          user: member,
          showing,
          ticketTypeId: ticketTypeForUser.id,
          tickets: marathonTickets
        })

        return [new OrderItem({
          type: 'ticket',
          ticket
        })]
      }).compact().value()
    } else if (fallbackTicketType) {
      const marathonTickets = this.getMarathonTicketsForShowing(showing, fallbackTicketType, user)

      const ticket = new Ticket({
        user,
        showing,
        ticketTypeId: fallbackTicketType.id,
        tickets: marathonTickets
      })
      orderItemsData = [[new OrderItem({
        type: 'ticket',
        ticket
      })]]
    } else if (fallbackPackage) {
      orderItemsData = [this.createItemPackages(fallbackPackage, showing, user)]
    }

    return orderItemsData
  }

  getTicketTypesAndItemPackagesForUserAndPurchasableMembers (user, activity, activityTicketOrderItems = []) {
    const data = {
      user: {},
      members: {}
    }

    if (!user) return data

    const hasMembers = (user.membership?.members?.length || 0) + (user.purchasees?.length || 0) > 0

    const fallbackTicketType = _.first(activity.guestTicketTypes)
    const fallbackItemPackage = _.first(activity.guestItemPackages)

    if (hasMembers) {
      const purchasableMembers = _.unionBy(user.membership.members.data, user.purchasees.data, 'id')

      _.each(purchasableMembers, member => {
        const memberTicketTypes = activity.memberTicketTypes(member)
        const memberItemPackages = activity.memberItemPackages(member)

        const [ticketTypeForMember, ticketTypeMatchSpecificity] = this.getTicketTypeForMember(
          memberTicketTypes,
          member,
          fallbackTicketType,
          _.filter(activityTicketOrderItems, orderItem => !_.isNil(orderItem.activityTicket.user))
        )
        const [itemPackageForMember, itemPackageMatchSpecificity] = this.getItemPackageForMember(
          memberItemPackages,
          member,
          fallbackItemPackage,
          _.filter(activityTicketOrderItems, orderItem => !_.isNil(orderItem.activityTicket.user))
        )

        const ticketTypeAndItemPackageData = {
          user: member,
          ticketTypeOrItemPackage:
            // Item package is more specific than ticket type
            itemPackageMatchSpecificity > ticketTypeMatchSpecificity ||
              // Only item package exists
              (!!itemPackageForMember && !ticketTypeForMember)
              ? itemPackageForMember
              : ticketTypeForMember,
          memberTicketTypes,
          memberItemPackages
        }

        if (member.id === user.id) {
          data.user = ticketTypeAndItemPackageData
        } else {
          data.members[member.id] = ticketTypeAndItemPackageData
        }
      })
    } else {
      const memberTicketTypes = activity.memberTicketTypes(user)
      const memberItemPackages = activity.memberItemPackages(user)

      const [ticketTypeForMember, ticketTypeMatchSpecificity] = this.getTicketTypeForMember(
        memberTicketTypes,
        user,
        fallbackTicketType,
        _.filter(activityTicketOrderItems, orderItem => !_.isNil(orderItem.activityTicket.user))
      )
      const [itemPackageForMember, itemPackageMatchSpecificity] = this.getItemPackageForMember(
        memberItemPackages,
        user,
        fallbackItemPackage,
        _.filter(activityTicketOrderItems, orderItem => !_.isNil(orderItem.activityTicket.user))
      )

      data.user = {
        user,
        ticketTypeOrItemPackage:
          // Item package is more specific than ticket type
          itemPackageMatchSpecificity > ticketTypeMatchSpecificity ||
            // Only item package exists
            (!!itemPackageForMember && !ticketTypeForMember)
            ? itemPackageForMember
            : ticketTypeForMember,
        memberTicketTypes,
        memberItemPackages
      }
    }

    return data
  }

  getMarathonTicketsForShowing (showing, ticketType, user = null) {
    const marathonTickets = []
    if (showing.isMarathon && !!ticketType) {
      _.each(showing.showings.data, marathonShowing => {
        marathonTickets.push(new Ticket({
          showing: marathonShowing,
          ticketTypeId: ticketType.id,
          user
        }))
      })
    } else if (showing.hasMarathon && !!ticketType) {
      marathonTickets.push(new Ticket({
        showing: showing.showing,
        ticketTypeId: ticketType.id,
        user
      }))
    }

    return marathonTickets
  }

  addGuestTicket (showing, passedTicketType, itemPackage = null) {
    if (!_.isNil(itemPackage)) {
      return this.addItemPackage(itemPackage, showing)
    }

    if (this.ticketsRemaining(showing, passedTicketType) > 0 && !this.isMaxTicketLimitReached(showing)) {
      const ticketType = passedTicketType || showing.ticketTypes().find(ticketType => ticketType.displayName === 'Adult') || showing.ticketTypes()[0]
      if (!ticketType) throw new Error('No ticket types on showing')

      const marathonTickets = this.getMarathonTicketsForShowing(showing, ticketType)
      this.orderItems.add({
        member: { name: 'Your Guest' },
        type: 'ticket',
        ticket: {
          showing,
          tickets: marathonTickets,
          ticketTypeId: ticketType.id
        },
        // For some reason, without this, each order item will be pointed at a
        // different copy of the order, breaking reactivity
        order: this
      })
    }
  }

  getActivityBookingForActivity (activity, date = null) {
    if (_.isNil(activity)) return null

    if (!_.isNil(date)) {
      return _.find(this.activityBookings.data, { activity: { id: activity.id }, config: { selectedActivitySessionDate: formatDateForActivityBookingConfig(date) } })
    } else {
      return _.find(this.activityBookings.data, { activity: { id: activity.id } })
    }
  }

  getActivityBookingByActivityBookingId (activity, activityBookingId) {
    if (_.isNil(activity) || _.isNil(activityBookingId)) return null

    return _.find(this.activityBookings.data, { activity: { id: activity.id }, id: Number(activityBookingId) })
  }

  async createNewActivityBooking (date) {
    /**
     * We need a unique key so that we can find the saved activity booking.
     */
    const createKey = `create-key-${crypto.getRandomValues(new Uint32Array(1))[0]}`

    const activityBooking = new ActivityBooking({
      activityId: this.currentActivity.id,
      order: this
    })

    if (!_.isNil(date)) {
      activityBooking.selectActivitySessionDate(date)
    }

    activityBooking.config.createKey = createKey
    this.activityBookings.add(activityBooking)

    /**
     * We should always save the activity booking first so that it can be
     * referenced by the activity tikets that belong to it.
     */
    await this.save()

    return _.find(this.activityBookings.data, { config: { createKey } })
  }

  cleanupDraftActivityBookings () {
    _(this.activityBookings.data).each(activityBooking => {
      if (activityBooking.allowDraftCleanup) {
        this.orderItems.removeAll(activityBooking.activityTicketOrderItems)
        this.orderItems.removeAll(activityBooking.activitySalesItemOrderItems)
        this.activityBookings.remove(activityBooking)
      }
    })
  }

  createActivityTicketOrderItem (activityBookingId, ticketTypeId, { itemPackageId = null, user = null }) {
    const activityTicket = new ActivityTicket({
      activityBookingId,
      ticketTypeId,
      user
    })

    return new OrderItem({
      type: 'activity-ticket',
      activityTicket,
      itemPackageId
    })
  }

  createActivityTicketOrderItemAndAddToOrder (activityBookingId, ticketTypeId, { itemPackageId = null, user = null }) {
    const orderItem = this.createActivityTicketOrderItem(activityBookingId, ticketTypeId, { itemPackageId, user })

    this.orderItems.add(orderItem)

    return orderItem
  }

  // Determines the number of tickets remaining for a showing
  ticketsRemaining (showing, ticketType = null) {
    if (ticketType && showing.seatChart) {
      return this.ticketsRemainingByTicketType(showing, ticketType)
    }

    return this.ticketsRemainingOverall(showing)
  }

  // Determines the overall number of tickets remaining for a showing
  ticketsRemainingOverall (showing) {
    const totalSeatsAvailable = showing.screen.seatCount
    const numHeldSeats = showing.reservedSeating ? 0 : showing.heldSeatCount
    const numTicketsSold = showing.ticketsSold
    const numTicketsInCart = this.showingTicketOrderItems(showing).length

    let numMarathonTicketsRemaining = 0
    if (showing.hasMarathon) {
      numMarathonTicketsRemaining = Math.max(showing.screen.marathonSeatCount - showing.marathonTicketsSold, 0)
    }

    return totalSeatsAvailable - numHeldSeats - numTicketsSold - numMarathonTicketsRemaining - numTicketsInCart
  }

  // Determines the number of tickets remaining for a showing by ticket type
  ticketsRemainingByTicketType (showing, ticketType) {
    // We are unable to calculate tickets remaining by ticket type without
    // the 'seats remaining by ticket type id' data from the backend
    if (showing.seatsRemainingByTicketTypeId === undefined) {
      console.error('Seats remaining by ticket type data is missing. We are unable to calculate the number of seats available per ticket type.')
      return 0
    }

    // Get the count of available seats for this ticket type
    const numAvailableSeatsForTypeId = showing.seatsRemainingByTicketTypeId[ticketType.id] || 0
    if (!numAvailableSeatsForTypeId) {
      return 0
    }

    // Count the number of seats reserved in the cart for this ticket type
    const ticketsInCart = this.showingTicketOrderItems(showing)
    const ticketsInCartByTypeId = _.groupBy(ticketsInCart, 'ticket.ticketTypeId')
    const numTicketsInCartForTypeId = ticketsInCartByTypeId[ticketType.id] ? ticketsInCartByTypeId[ticketType.id].length : 0

    // Determine the number of tickets in the cart with types IDs that apply to the
    // same seats as the target type ID.
    const numOverlappingTicketsInCart = this.getNumOverlappingTicketsInCartForTicketType(showing, ticketType, ticketsInCartByTypeId)

    return (numAvailableSeatsForTypeId - numTicketsInCartForTypeId - numOverlappingTicketsInCart)
  }

  // Determines the number of tickets in the cart with types IDs that apply to the
  // same seats as the target type ID.
  //
  // Example: there may be a seat that can be reserved by both a 'Premium Adult'
  // and 'Premium Child' ticket type. If that seat is reserved, we need to
  // decrement the count of available seats for both ticket types.
  getNumOverlappingTicketsInCartForTicketType (showing, targetTicketType, ticketsInCartByTypeId) {
    let overlappingTicketsInCart = 0

    const seatChart = showing.seatChart.seatChart
    const seats = _(seatChart).flatten()
    const availableSeats = seats.filter(seat => seat.available && (!showing.hasMarathon || !seat.reserveDuringMarathons))

    _.each(ticketsInCartByTypeId, (tickets, ticketTypeId) => {
      // Don't double-count the target ticket type
      if (Number(ticketTypeId) === Number(targetTicketType.id)) {
        return 0
      }

      const ticketType = this.findTicketTypeById(ticketTypeId)
      if (!ticketType) {
        return 0
      }

      // Count tickets in the cart for each ticket type
      const numSeatsSelectedForTicketTypeId = tickets.length

      // Count the number of tickets with type IDs that share the same seat as the target type ID
      const numSeatsAvailableForBothTicketTypes = availableSeats.filter(seat => (
        this.ticketTypeCanApplyToSeat(ticketType, seat) && this.ticketTypeCanApplyToSeat(targetTicketType, seat)
      )).value().length

      // Determine the number of reserved, overlapping tickets
      overlappingTicketsInCart += Math.min(numSeatsSelectedForTicketTypeId, numSeatsAvailableForBothTicketTypes)
    })

    return overlappingTicketsInCart
  }

  // Finds the ticket type matching the given ID
  findTicketTypeById (id) {
    if (!this.configuration.ticketTypes) {
      return null
    }

    return this.configuration.ticketTypes.data.find((ticketType) => Number(ticketType.id) === Number(id))
  }

  // Determines if a specific ticket type can apply to a specific seat
  ticketTypeCanApplyToSeat (ticketType, seat) {
    if (ticketType.appliesToSpecificSeats && seat.ticketTypeIds && seat.ticketTypeIds.includes(ticketType.id)) {
      return true
    }

    if (!ticketType.appliesToSpecificSeats && !seat.ticketTypeIds) {
      return true
    }

    return false
  }

  isMaxTicketLimitReached (showing) {
    if (showing.ticketLimit === null) {
      return false
    }
    return this.showingTicketOrderItems(showing).length >= showing.ticketLimit
  }

  get ticketCountErrors () {
    return _(this.allShowings).map(showing => {
      // IGNORE RESERVED SEATING AVAILABILITY TO AVOID RE-FETCHING THE SEAT CHART
      if (showing.reservedSeating) return null

      const remainingTickets = this.ticketsRemaining(showing)
      if (remainingTickets >= 0) return null

      return `You have selected too many tickets for ${showing.movie.name} showing ${showing.formattedTime(true, false)} please remove ${remainingTickets * -1} tickets and try again`
    }).compact().value()
  }

  get duplicateSeatTicketErrors () {
    return _(this.allShowings).map(showing => {
      if (!showing.reservedSeating) return null

      const ticketOrderItems = this.allShowingTicketOrderItems(showing)
      if (ticketOrderItems.length === 0) return null

      const allSeatNames = _(ticketOrderItems).map('ticket.seatName').compact().value()
      const uniqueSeatNames = _.uniq(allSeatNames)
      if (allSeatNames.length !== uniqueSeatNames.length) {
        return `The same seat has been selected multiple times for ${showing.movie.name} showing ${showing.formattedTime(true, false)} please reselect seats`
      }
      return null
    }).compact().value()
  }

  // CHANGED FROM COMPUTED PROPERTY TO GETTER TO FIX REACTIVITY ISSUES (PAY BUTTON WASN'T CHANGING WHEN ORDER ITEM TOTALS CHANGED)
  get totalOwed () {
    return roundToCents(this.total - this.completedPaymentTotal)
  }

  findGuestTicketOrderItem (showing, ticketType) {
    return _.findLast(this.orderItems.data, (oi) => oi.state === 'draft' && oi.ticket && !oi.ticket.user && oi.ticket.showing.id === showing?.id && oi.ticket.ticketTypeId === ticketType.id)
  }

  findPackageTicketOrderItem (showing, itemPackage, user = null) {
    return _.findLast(this.orderItems.data, (oi) => oi.state === 'draft' && oi.ticket && (user ? oi.ticket.user?.id === user.id : !oi.ticket.user) && oi.ticket.showing.id === showing?.id && oi.itemPackage?.id === itemPackage.id)
  }

  canRemoveGuestTicket (showing, ticketType) {
    return !!this.findGuestTicketOrderItem(showing, ticketType)
  }

  canRemoveTicketPackage (showing, itemPackage) {
    return !!this.findPackageTicketOrderItem(showing, itemPackage)
  }

  findAllItemsForPackage (itemPackage, excludeRedemptionItems = false) {
    const orderItems = this.orderItems
    const foundIds = []
    return _.map(itemPackage.itemPackageItems.data, function (item) {
      if (item.itemVariation) {
        const foundItem = _.findLast(orderItems.data, (oi) => oi.type === 'item' && oi.itemPackageId === itemPackage.id && oi.itemOrderItem.itemVariation.item.id === item.itemVariation.item.id && (!excludeRedemptionItems || !(oi.voucherType?.id || oi.voucher?.id)) && !_.includes(foundIds, oi.id))
        foundIds.push(foundItem?.id)
        return foundItem
      } else if (item.ticketType) {
        const foundTicket = _.findLast(orderItems.data, (oi) => oi.type === 'ticket' && oi.itemPackageId === itemPackage.id && oi.ticket.ticketType.id === item.ticketType.id && (!excludeRedemptionItems || !(oi.voucherType?.id || oi.voucher?.id)) && !_.includes(foundIds, oi.id))
        foundIds.push(foundTicket?.id)
        return foundTicket
      }
    })
  }

  async addDayPass (showing) {
    this.orderItems.add({
      type: 'day-pass'
    })

    if (!this.user) {
      const dayPassUser = new User({
        firstName: 'Day Pass',
        lastName: 'Holder',
        userClassId: this.configuration.userClasses.data[0].id,
        siteId: this.siteId,
        circuitId: this.circuitId,
        membershipTypeId: this.configuration.defaultMembershipType.id,
        dayPassUser: true
      })

      await dayPassUser.save()

      this.setRelated('user', dayPassUser)
    }

    if (showing) {
      const orderItems = this.initializeDataOrderForShowing(this.user, showing)
      _.each(orderItems[0], oi => this.orderItems.add(oi))
    }

    Notify.create({
      message: 'Day Pass Added to Cart',
      type: 'positive',
      position: 'top'
    })
  }

  get dayPassUser () {
    if (!this.purchasingDayPass) return null

    return this.dayPassOrderItems[0].user
  }

  countSeatsToSelect (showing, ticketTypeIds = false) {
    if (!showing.reservedSeating) {
      return 0
    }

    return this.showingTicketOrderItems(showing, ticketTypeIds).length
  }

  currentSelectedSeats (showing, ticketTypeIds = false) {
    if (!showing.reservedSeating) {
      return []
    }

    return _(this.showingTicketOrderItems(showing, ticketTypeIds)).map('ticket.seatName').compact().value()
  }

  remainingSeatsToSelect (showing, ticketTypeIds = false) {
    return this.countSeatsToSelect(showing, ticketTypeIds) - this.currentSelectedSeats(showing, ticketTypeIds).length
  }

  draftSelectedSeats (showingId, ticketTypeIds = false) {
    return _(this.draftShowingTicketOrderItems(showingId, ticketTypeIds)).map('ticket.seatName').compact().value()
  }

  setSeatSelection (showing, seatNames, ticketTypeIds = false) {
    const ticketOrderItems = this.showingTicketOrderItems(showing, ticketTypeIds)

    if (ticketOrderItems.length !== seatNames.length) {
      const message = 'Seat selection count does not match ticket count'
      throw new Error(message)
    }

    for (let index = 0; index < seatNames.length; index += 1) {
      ticketOrderItems[index].ticket.seatName = seatNames[index]
      _.each(ticketOrderItems[index].ticket.tickets?.data, marathonTicket => {
        marathonTicket.seatName = seatNames[index]
      })
    }
  }

  clearSeatSelection (showing, ticketTypeIds = false) {
    const ticketOrderItems = this.showingTicketOrderItems(showing, ticketTypeIds)

    _.each(ticketOrderItems, ticketOrderItem => {
      ticketOrderItem.ticket.seatName = null
      _.each(ticketOrderItem.ticket.tickets.data, marathonTicket => {
        marathonTicket.seatName = null
      })
    })
  }

  firstOrderItemNeedingSeatSelection () {
    return _.find(
      this.operativeOrderItems,
      orderItem =>
        orderItem.type === 'ticket' &&
        orderItem.ticket.showing.reservedSeating &&
        !orderItem.ticket.seatName
    )
  }

  lastOrderItemWithSeatSelected () {
    return _.findLast(
      this.operativeOrderItems,
      orderItem =>
        orderItem.type === 'ticket' &&
        orderItem.ticket.showing.reservedSeating &&
        orderItem.ticket.seatName
    )
  }

  allSelectedSeats (showing, ticketTypeIds = false) {
    if (!showing.reservedSeating) {
      return []
    }

    return _(this.allShowingTicketOrderItems(showing, ticketTypeIds)).map('ticket.seatName').compact().value()
  }

  ticketForShowing (showingId) {
    if (!showingId) return
    return _.find(
      this.orderItems.data,
      orderItem =>
        orderItem.type === 'ticket' &&
        orderItem.ticket.showing.id.toString() === showingId.toString()
    )
  }

  processPackageWorkerApplyPackage (message) {
    const { packageId, orderItemAssignIds, orderItemUnassignIds } = message
    const packageIdentifier = `${packageId}-${this.packageCounter++}`
    const orderItemsToUnassign = _.map(orderItemUnassignIds, id => _.find(this.orderItems.data, orderItem => orderItem.id === id))
    const orderItemsToAssign = _.map(orderItemAssignIds, id => _.find(this.orderItems.data, orderItem => orderItem.id === id))
    const returnOrderItemsToAssign = []
    const returnOrderItemsToUnassign = []
    const getOrderItemTypeId = (orderItem) => {
      if (orderItem.type === ORDER_ITEM_TYPES.TICKET) {
        return orderItem.ticket.ticketType.id
      }
      return orderItem.itemOrderItem.itemVariation.id
    }

    _.each(orderItemsToUnassign, orderItem => {
      orderItem.itemPackageId = null
      orderItem.packageGroupIdentifier = null
      returnOrderItemsToUnassign.push({ id: orderItem.id, type: orderItem.type, typeId: getOrderItemTypeId(orderItem), excludedPackageIds: [...orderItem.excludedItemPackageIds], assignedPackageId: orderItem.itemPackageId, itemPackageIdentifier: packageIdentifier })
    })

    _.each(orderItemsToAssign, orderItem => {
      orderItem.itemPackageId = packageId
      orderItem.packageGroupIdentifier = packageIdentifier
      returnOrderItemsToAssign.push({ id: orderItem.id, type: orderItem.type, typeId: getOrderItemTypeId(orderItem), excludedPackageIds: [...orderItem.excludedItemPackageIds], assignedPackageId: orderItem.itemPackageId, itemPackageIdentifier: packageIdentifier })
    })

    this.packageWorker.postMessage({
      messageType: CONSUMER_MESSAGE_TYPES.UPDATE_PACKAGE_ASSIGNMENTS,
      orderItemsToUnassign: returnOrderItemsToUnassign,
      orderItemsToAssign: returnOrderItemsToAssign
    })
  }

  processPackageWorkerApplyDiscount (message) {
    const { addedPackageIds, removedPackageIds } = message

    const packageById = this.configuration?.currentSite?.packageById || {}

    _.each(addedPackageIds, packageId => {
      const itemPackage = packageById[packageId]
      if (!_.isNil(itemPackage)) {
        this.independentDiscounts.data = [...this.independentDiscounts.data, ...itemPackage.discountsForOrder]
      }
    })

    _.each(removedPackageIds, packageId => {
      const itemPackage = packageById[packageId]
      if (!_.isNil(itemPackage)) {
        // As package is removed, we need to remove the discounts that were added for it
        this.independentDiscounts.data = _.difference(this.independentDiscounts.data, itemPackage.discountsForOrder)
      }
    })

    this.applyRedemptions()
  }

  processPackageWorkerMessage (message) {
    if (message.messageType === PRODUCER_MESSAGE_TYPES.APPLY_PACKAGE) {
      this.processPackageWorkerApplyPackage(message)
    } else if (message.messageType === PRODUCER_MESSAGE_TYPES.APPLY_DISCOUNT) {
      this.processPackageWorkerApplyDiscount(message)
    }
  }

  // False means all, null means any ticket type without seat restrictions
  showingTicketOrderItems (showing, ticketTypeIds = false) {
    return _.filter(this.orderItems.data, orderItem => {
      if (
        orderItem.type !== 'ticket' ||
        orderItem.state !== 'draft' ||
        (showing && orderItem.ticket?.showing?.id !== showing.id)
      ) return false

      // False means all
      if (ticketTypeIds === false) return true

      // False means any ticket type without seat restrictions
      if (ticketTypeIds === null) return !orderItem.ticket?.ticketType?.appliesToSpecificSeats

      // An array means we need to match
      return _.includes(ticketTypeIds, orderItem.ticket?.ticketType?.id)
    })
  }

  // False means all, null means any ticket type without seat restrictions
  allShowingTicketOrderItems (showing, ticketTypeIds = false) {
    return _.filter(this.orderItems.data, orderItem => {
      if (
        orderItem.type !== 'ticket' ||
        orderItem.state === 'voided' ||
        (showing && orderItem.ticket?.showing?.id !== showing.id)
      ) return false

      // False means all
      if (ticketTypeIds === false) return true

      // False means any ticket type without seat restrictions
      if (ticketTypeIds === null) return !orderItem.ticket?.ticketType?.appliesToSpecificSeats

      // An array means we need to match
      return _.includes(ticketTypeIds, orderItem.ticket?.ticketType?.id)
    })
  }

  // False means all, null means any ticket type without seat restrictions
  draftShowingTicketOrderItems (showingId, ticketTypeIds = false) {
    return _.filter(this.orderItems.data, orderItem => {
      if (
        orderItem.state !== 'draft' ||
        (orderItem.ticket?.showing?.id !== showingId)
      ) return false

      // False means all
      if (ticketTypeIds === false) return true

      // False means any ticket type without seat restrictions
      if (ticketTypeIds === null) return !orderItem.ticket?.ticketType?.appliesToSpecificSeats

      // An array means we need to match
      return _.includes(ticketTypeIds, orderItem.ticket?.ticketType?.id)
    })
  }

  formatDueTime (dueTime) {
    const today = formatDate(new Date(), 'YYYY-MM-DD')
    const tomorrow = formatDate(addToDate(new Date(), { days: 1 }), 'YYYY-MM-DD')

    if (!dueTime) return 'None Selected'

    const siteDueTime = this.configuration.currentSite.inSiteTime(dueTime)

    const dueDate = formatDate(siteDueTime, 'YYYY-MM-DD')

    const time = formatDate(siteDueTime, 'h:mm a')

    if (dueDate === today) return time

    let date = ''

    if (dueDate === tomorrow) {
      date = this.i18n.t('time.tomorrow')
    } else {
      date = formatDate(siteDueTime, 'MMM Do')
    }

    return `${date} at ${time}`
  }

  get allEffectiveDates () {
    return _([this.orderItems.data, this.payments.data])
      .flatten()
      .map(item => [item.paymentDate, item.refundDate])
      .flatten()
      .compact()
      .sort()
      .uniqBy(date => date.getTime())
      .value()
  }

  get countOfMismatchedEffectiveDates () {
    let count = 0
    _.each(this.allEffectiveDates, effectiveDate => {
      let balance = 0

      _.each(this.orderItems.data, orderItem => {
        if (orderItem.paidAt && orderItem.paymentDate.getTime() === effectiveDate.getTime()) {
          balance += orderItem.reconciliationDisplayTotal
        }

        if (orderItem.refundDate && orderItem.refundDate.getTime() === effectiveDate.getTime()) {
          balance -= orderItem.reconciliationDisplayTotal
        }
      })

      _.each(this.payments.data, payment => {
        if (payment.paymentDate && payment.paymentDate.getTime() === effectiveDate.getTime()) {
          balance -= payment.amount
        }
      })

      balance = Math.abs(parseFloat(balance.toFixed(2)))

      if (balance !== 0) {
        count++
      }
    })

    return count
  }

  async place (placeType = 'full', errorHandler) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    // Set the action we want the server to execute
    if (placeType === ORDER_PLACE_TYPES.TAB_ORDER) {
      this.action = ORDER_ACTIONS.PLACE_TAB_ORDER
    } else if (placeType === ORDER_PLACE_TYPES.TAB_ORDER_AND_PAYMENTS) {
      this.action = ORDER_ACTIONS.PLACE_TAB_ORDER_PROCESS_PAYMENTS
    } else if (placeType === ORDER_PLACE_TYPES.PREP_ORDER) {
      this.action = ORDER_ACTIONS.PLACE_PREP_ORDER
    } else {
      this.action = ORDER_ACTIONS.PLACE_ORDER
    }

    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    this.addCashOutItems()

    _(this.orderItems.data).filter({ state: 'draft' }).each(orderItem => orderItem.place())
    _(this.payments.data).filter({ state: 'draft', type: 'cash' }).each(payment => { payment.registerStation = this.currentRegisterStation })

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }
    if (!errors && (this.state === 'draft' || this.state === 'voided' || this.state === 'canceled')) {
      errors = ['Order was not completed successfully']
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async cancel (errorHandler) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    this.action = ORDER_ACTIONS.CANCEL_ORDER
    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }
    if (!errors && this.state !== 'canceled') {
      errors = ['Order was not canceled successfully']
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async abandon (errorHandler) {
    if (this.transient) return

    if (this.isLocal) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    this.action = ORDER_ACTIONS.ABANDON_ORDER
    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async undoStartSwap (errorHandler) {
    if (this.transient) return

    if (this.isLocal) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.error(errors)
        this.$mh.showErrors(errors)
      }
    }

    this.action = 'undo_start_swap'

    // If we have the 'must select seat' or 'no date and/or time selected' error, we can ignore it.
    // It should not prevent us from undoing the swap
    let errors = _.filter(this.errors, error => error !== 'Must select seat' && error !== 'No date and/or time selected')

    if (errors && errors.length) {
      errorHandler(errors)
      this.resetField('action')
      return false
    }

    const result = await this.save()

    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    if (errors && errors.length) {
      errorHandler(errors)
      return false
    }

    return true
  }

  async collect (collectOrderItemIds, errorHandler) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    this.action = ORDER_ACTIONS.COLLECT_ORDER
    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    this.collectOrderItemIds = collectOrderItemIds

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async admitTickets (ticketIds, errorHandler) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    this.action = ORDER_ACTIONS.ADMIT_TICKETS
    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    this.admitTicketIds = ticketIds

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async excuseTickets (ticketIds, errorHandler) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    this.action = ORDER_ACTIONS.EXCUSE_TICKETS
    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    this.excuseTicketIds = ticketIds

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async void (voidOrderItemIds, payments, errorHandler, startSwap = false) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    this.action = startSwap ? ORDER_ACTIONS.START_SWAP : ORDER_ACTIONS.VOID
    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    this.voidOrderItemIds = voidOrderItemIds

    if (!startSwap) {
      _.each(payments, payment => {
        if (payment.type === 'credit') {
          const existingCreditPayment = _.find(this.payments.data, existingPayment => existingPayment.type === 'credit' && existingPayment.state === 'draft')
          if (existingCreditPayment) {
            existingCreditPayment.amount = -payment.amount
            return
          }
        }
        this.payments.add({
          amount: -payment.amount,
          type: payment.type,
          subtype: payment.subtype,
          stripePaymentIntentId: payment.stripePaymentIntentId,
          giftCard: payment.giftCard,
          lastFour: payment.lastFour,
          cardType: payment.cardType,
          nameOnCard: payment.nameOnCard,
          paymentTypeId: payment.paymentTypeId,
          paymentTypeReasonText: payment.paymentTypeReasonText,
          refundFromPaymentId: payment.refundFromPaymentId || payment.id,
          paymentConnectorVoid: payment.paymentConnectorVoid
        })
      })
      _(this.payments.data).filter({ state: PAYMENT_STATES.DRAFT, type: PAYMENT_TYPES.CASH }).each(payment => { payment.registerStation = this.currentRegisterStation })
    }

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    const paymentConnectorRefundInProgress = _.some(this.payments?.data, payment => payment.draft && payment.nonStripeCardPayment)

    if (!errors && !paymentConnectorRefundInProgress && !_.includes(ORDER_STATES.ALL_VOIDED_STATES, this.state)) {
      errors = ['There was a problem refunding the order']
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async claimRefund (payments, errorHandler) {
    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
        this.$mh.showErrors(errors)
      }
    }

    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      this.resetField('action')
      return
    }

    this.action = ORDER_ACTIONS.CLAIM_REFUND

    _.each(payments, payment => {
      if (payment.type === PAYMENT_TYPES.CREDIT) {
        const existingCreditPayment = _.find(this.payments.data, existingPayment => existingPayment.type === PAYMENT_TYPES.CREDIT && existingPayment.state === PAYMENT_STATES.DRAFT)
        if (existingCreditPayment) {
          existingCreditPayment.amount = -payment.amount
          return
        }
      }
      this.payments.add({
        amount: -payment.amount,
        type: payment.type,
        stripePaymentIntentId: payment.stripePaymentIntentId,
        giftCard: payment.giftCard,
        lastFour: payment.lastFour,
        cardType: payment.cardType,
        nameOnCard: payment.nameOnCard,
        paymentTypeId: payment.paymentTypeId,
        paymentTypeReasonText: payment.paymentTypeReasonText,
        refundFromPaymentId: payment.id
      })
    })
    _(this.payments.data).filter({ state: PAYMENT_STATES.DRAFT, type: PAYMENT_TYPES.CASH }).each(payment => { payment.registerStation = this.currentRegisterStation })

    const result = await this.save()
    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }
    if (!errors && !_.includes(ORDER_STATES.ALL_VOIDED_STATES, this.state)) {
      errors = ['There was a problem claiming the refund']
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return true
  }

  async open () {
    if (this.transient) return

    this.action = ORDER_ACTIONS.OPEN_TAB

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async close () {
    if (this.transient) return

    this.action = ORDER_ACTIONS.CLOSE_TAB

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async requestTabTransfer (employee) {
    if (this.transient || !employee) return
    if (this.employee?.id === employee.id) return
    if (!this.isRemote) return
    if (!this.canTransferTab) return

    const props = {
      id: this.id,
      employeeId: employee.id,
      action: TAB_TRANSFER_ACTIONS.REQUEST
    }

    const results = await executeMutation('tabTransferUpdate', props, { success: true, incomingEmployeeId: true })

    return results
  }

  async acceptTransfer () {
    if (this.transient) return
    if (this.incomingEmployee.id !== this.context.currentUser?.id) return

    const props = {
      id: this.id,
      action: TAB_TRANSFER_ACTIONS.ACCEPT
    }
    const results = await executeMutation('tabTransferUpdate', props, { success: true, employeeId: true, incomingEmployeeId: true })

    return results
  }

  async rejectTransfer () {
    if (this.transient) return
    if (this.incomingEmployee.id !== this.context.currentUser?.id) return

    const props = {
      id: this.id,
      action: TAB_TRANSFER_ACTIONS.REJECT
    }
    const results = await executeMutation('tabTransferUpdate', props, { success: true, incomingEmployeeId: true })

    return results
  }

  async transferTab (employee) {
    if (this.transient) return
    if (!employee) return
    if (!this.canTransferTab) return

    const props = {
      id: this.id,
      employeeId: employee.id,
      action: TAB_TRANSFER_ACTIONS.TRANSFER
    }
    const results = await executeMutation('tabTransferUpdate', props, { success: true, employeeId: true, incomingEmployeeId: true })

    return results
  }

  async cancelTabTransfer () {
    if (this.transient) return
    if (this.employee?.id !== this.context.currentUser.id) return

    const props = {
      id: this.id,
      action: TAB_TRANSFER_ACTIONS.CANCEL
    }
    const results = await executeMutation('tabTransferUpdate', props, { success: true, incomingEmployeeId: true })

    return results
  }

  async runCorrection (correction) {
    if (this.transient) return

    let errors = []
    const errorHandler = errors => {
      console.log(errors)
      this.$mh.showErrors(errors)
    }

    this.action = 'run_correction_' + correction

    let result
    try {
      result = await this.save()
    } catch (error) {
      errors.push(error)
      console.log(error)
    } finally {
      this.resetField('action')
    }

    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    if (errors && errors.length) {
      errorHandler(errors)
    }
  }

  async createPaymentIntent (amount, terminal = false, preAuth = false, errorHandler) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
      }
    }

    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      return
    }

    // Set the action we want the server to execute
    if (terminal && preAuth) {
      this.action = ORDER_ACTIONS.CREATE_TERMINAL_PRE_AUTH_PAYMENT_INTENT
    } else if (terminal) {
      this.action = ORDER_ACTIONS.CREATE_TERMINAL_PAYMENT_INTENT
    } else {
      this.action = ORDER_ACTIONS.CREATE_PAYMENT_INTENT
    }

    this.stripePaymentAmount = amount
    this.fullAmount = amount === this.remainingOwed

    try {
      const result = await this.save()
      if (result.errors.length) {
        errors = result.errors
      }
      this.resetField('action')
    } catch (error) {
      errors = [error]
    }

    this.resetField('action')

    if (errors) {
      errorHandler(errors)
      return
    }

    return this.stripePaymentIntentClientSecret
  }

  async runAuditChecks () {
    if (this.transient) return

    let errors = []
    const errorHandler = errors => {
      console.log(errors)
      this.$mh.showErrors(errors)
    }

    this.action = ORDER_ACTIONS.RUN_FULL_AUDIT_CHECKS

    let result
    try {
      result = await this.save()
    } catch (error) {
      errors.push(error)
      console.log(error)
    } finally {
      this.resetField('action')
    }

    this.resetField('action')
    if (result.errors.length) {
      errors = result.errors
    }

    if (errors && errors.length) {
      errorHandler(errors)
    }
  }

  async printOrderReceipt () {
    if (this.transient) return

    const results = await executeMutation('printOrderReceipt', { id: this.id }, { success: true })

    return results
  }

  async printBulkOrdersReceipt (orderIds, printMerchantCopy = false) {
    const results = await executeMutation('printBulkOrdersReceipt', { ids: orderIds, printMerchantCopy }, { success: true })

    return results
  }

  async addInteracRefundToOrder (stripeRefundId) {
    const results = await executeMutation('addInteracRefundToOrder', { id: this.id, stripeRefundId }, { success: true, error: true })

    return results
  }

  async updatePreAuthAmounts (orderIds) {
    const results = await executeMutation('updatePreAuthAmounts', { ids: orderIds }, { success: true, errorsByOrderId: true })

    return results
  }

  async placeOrdersAndProcessPayments (orderIds) {
    const results = await executeMutation('placeOrdersAndProcessPayments', { ids: orderIds }, { success: true, errorsByOrderId: true })

    return results
  }

  async claimQrOrder () {
    if (this.transient) return

    const results = await executeMutation('claimQrOrder', { id: this.id }, { success: true })

    return results
  }

  async saveTipAdjustments (tipByOrderId) {
    const results = await executeMutation('saveTipAdjustments', { tipByOrderId }, { success: true, errorsByOrderId: true })

    return results
  }

  async printOrderInvoice () {
    const results = await executeMutation('printOrderInvoice', { id: this.id }, { success: true })

    return results
  }

  async printKioskOrder () {
    if (this.transient) return

    const results = await executeMutation('printKioskOrder', { id: this.id }, { success: true })

    return results
  }

  async printOrderCustomTemplate () {
    if (this.transient) return

    const results = await executeMutation('printOrderCustomTemplate', { id: this.id, printTemplateId: this.printTemplateId }, { success: true })

    return results
  }

  async splitTabOrder (orderItemIds, orderTo = null, autoSplit = false) {
    if (this.transient) return

    const results = await executeMutation('splitTabOrder', { id: orderTo?.id || -1, orderItemIds, splitByPosition: autoSplit }, { success: true, id: true, error: true })

    return results
  }

  async emailOrderReceipt (email) {
    if (this.transient) return

    const results = await executeMutation('emailOrderReceipt', { id: this.id, email }, { success: true })

    return results
  }

  async emailOrderInvoice (email) {
    const results = await executeMutation('emailOrderInvoice', { id: this.id, email }, { success: true })

    return results
  }

  async emailOrderCustomTemplate (email) {
    const results = await executeMutation('emailOrderCustomTemplate', { id: this.id, printTemplateId: this.printTemplateId, email }, { success: true })

    return results
  }

  async printItems (orderItemIds = []) {
    if (this.transient) return

    const results = await executeMutation('printOrderItems', { id: this.id, orderItemIds, groupTicket: this.groupTicket }, { success: true })

    return results
  }

  async emailItems (email = null, orderItemIds = []) {
    if (this.transient) return

    const results = await executeMutation('emailOrderItems', { id: this.id, email, orderItemIds }, { success: true })

    return results
  }

  async sendSmsOrderReceipt (deliveryPhone, deliveryPhoneCountryCode) {
    if (this.transient) return

    const results = await executeMutation('sendSmsOrderReceipt', { id: this.id, deliveryPhone, deliveryPhoneCountryCode }, { success: true })

    return results
  }

  get label () {
    const label = [_.startCase(this.state)]

    if (this.user) {
      label.push(this.user.name)
    } else if (this.nameForOrder) {
      label.push(this.nameForOrder)
    } else if (this.deliveryFirstName && this.deliveryLastName) {
      label.push(this.deliveryFirstName + ' ' + this.deliveryLastName)
    }
    label.push(this.formatDueTime(this.dueTime || this.createdAt))

    return label.join(' - ')
  }

  get description () {
    if (!this.orderItems) return null

    return _.map(this.orderItems.data, 'description').join(', ')
  }

  get currentOrders () {
    this.setTimes()
    return _.filter(this.data, order => {
      if (order.state === 'draft') return

      if (order.hasDueTime) {
        return order.dueTime > this.currentTimeThreshold
      } else if (order.showing) {
        return order.showing.time > this.currentTimeThreshold && order.showing.time < this.upcomingTimeThreshold
      } else {
        return false
      }
    })
  }

  get pastOrders () {
    this.setTimes()
    return _(this.data).without(...this.currentOrders).filter(order => {
      if (order.state === 'draft') return null

      if (order.hasDueTime) {
        return order.dueTime < this.currentTimeThreshold
      } else if (order.showing) {
        return order.showing.time < this.currentTimeThreshold
      } else {
        return true
      }
    }).compact().value()
  }

  get upcomingOrders () {
    this.setTimes()
    return _(this.data).without(...[...this.currentOrders, ...this.pastOrders]).filter(order => order.state !== 'draft').value()
  }

  get currentAndUpcomingOrders () {
    return [...this.currentOrders, ...this.upcomingOrders]
  }

  get userPointsEarned () {
    if (!this.userId) return 0

    // Filter user points earned for other users (e.g. visits)
    return _(this.orderItems.data)
      .map(orderItem => orderItem.userPoints.data)
      .flatten()
      .filter(userPoint => userPoint.userId === this.userId)
      .sumBy('amountEarned')
  }

  get userPointsUsed () {
    if (!this.userId) return 0

    return _.sumBy(this.orderItems.data, 'pointsUsed')
  }

  get userPoints () {
    return this.userPointsEarned - this.userPointsUsed
  }

  get potentialOrderPoints () {
    if (this.user?.isRemote) return 0 // Points are only potential for orders without a user
    return _.sumBy(this.orderItems.data, 'pointsEarned')
  }

  get userPointsExpiresAt () {
    if (!this.userId) return null

    const expiresAts = _(this.orderItems.data)
      .map(orderItem => orderItem.userPoints.data)
      .flatten()
      .filter(userPoint => userPoint.userId === this.userId)
      .map(userPoint => userPoint.expiresAt)
      .compact().value()

    if (!expiresAts) return null

    return new IsoDateTime(Math.max(...expiresAts))
  }

  get effectiveDeliveryLocationModel () {
    return _.find(this.configuration?.deliveryLocations?.data, { type: this.deliveryType, name: this.deliveryLocation })
  }

  setTimes () {
    this.currentTimeThreshold = addToDate(new Date(), { hours: -1 })
    this.upcomingTimeThreshold = addToDate(new Date(), { hours: 12 })

    if (!this.updateTimeInterval) {
      this.updateTimeInterval = setInterval(() => {
        this.setTimes()
      }, 1 * 60 * 60 * 1000) // Every hour
    }
  }

  async createSetupIntent (terminal = false, errorHandler) {
    if (this.transient) return

    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.error(errors)
      }
    }

    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      return
    }

    // Set the action we want the server to execute
    let action
    if (terminal) {
      action = ORDER_ACTIONS.CREATE_TERMINAL_SETUP_INTENT
    } else {
      action = ORDER_ACTIONS.CREATE_SETUP_INTENT
    }

    const intentOrder = new Order({
      id: this.id,
      user: { id: this.user?.id },
      action
    }, {
      skipWatchers: true,
      skipComputed: true,
      fields: ['id', 'stripeSetupIntentClientSecret']
    })

    try {
      const result = await intentOrder.save()
      if (result.errors.length) {
        errors = result.errors
      }
    } catch (error) {
      errors = [error]
    }

    if (this.isLocal && intentOrder.isRemote) {
      this.id = intentOrder.id
    }

    intentOrder.destroy() // Destroy to ensure we can run garbage collection

    if (errors) {
      errorHandler(errors)
      return
    }

    return intentOrder.stripeSetupIntentClientSecret
  }

  async updateSavedPaymentMethodFromStripe () {
    if (this.transient) return

    this.action = ORDER_ACTIONS.UPDATE_SAVED_PAYMENT_METHOD_FROM_STRIPE

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      delete this.data.action
    }
  }

  async removeSavedPaymentMethod () {
    if (this.transient) return

    this.action = ORDER_ACTIONS.REMOVE_SAVED_PAYMENT_METHOD

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      delete this.data.action
    }
  }

  async attachPaymentMethod (paymentMethodId) {
    if (this.transient) return

    this.attachPaymentMethodId = paymentMethodId

    this.action = ORDER_ACTIONS.ATTACH_PAYMENT_METHOD

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      delete this.data.action
    }
  }

  // We need to know the name and have a form of payment
  get canPlaceTab () {
    if (
      this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE &&
      !this.orderItems.data.length &&
      !this.needsPaymentMethod
    ) {
      return true
    }

    if (!this.deliveryInformationProvided) return false

    if (!this.nameForOrder && !this.user && !this.identifyWithDeliveryInfo) return false

    if (this.needsPaymentMethod) return false

    if (this.hasIncompleteActivityBookings) return false

    return true
  }

  get needsPaymentMethod () {
    return (
      this.configuration.currentSite.requireCreditCardForTabOrders &&
      !this.hasSavedPaymentMethod
    )
  }

  get hasSavedPaymentMethod () {
    return !!(this.savedPaymentMethodId || this.orderUserDefaultPaymentMethodId || this.user?.defaultPaymentMethodId || !!this.preAuthPayment)
  }

  get timeAwareShowingState () {
    if (this.state !== 'completed') {
      return 'invalid-state'
    } else if (this.showing && this.showing.time < addToDate(new Date(), { hours: -2 })) {
      return 'invalid-past'
    } else if (this.showing && this.showing.time > addToDate(new Date(), { hours: 2 })) {
      return 'invalid-future'
    } else if (!this.showing) {
      return 'invalid-no-showing'
    } else {
      return 'valid'
    }
  }

  /**
   * Returns an object with order information commonly requested for analytics
   * event payloads. This may be embedded in the DOM as a JSON string, or
   * used to map field values to a data layer or as a payload for an analytics event.
   */
  get analyticsData () {
    const ticketQuantities = _.countBy(this.ticketOrderItems, orderItem => {
      return orderItem.ticket.showing.movie.name
    })
    ticketQuantities.total = this.ticketOrderItems.length

    return {
      id: this.id,
      total: this.total,
      subtotal: this.subtotal,
      tax: this.taxAmount,
      discount: this.discount,
      bookingFee: _(this.operativeOrderItems).filter({ type: ORDER_ITEM_TYPES.BOOKING_FEE }).sumBy('subtotal'),
      items: _.map(this.orderItems.data, 'analyticsData'),
      ticketQuantities,
      siteName: this.configuration.currentSite.displayName
    }
  }

  setupCurrentMemberDiscounts () {
    if (!this.currentMembershipDiscounts?.data) return

    const availableMembershipDiscounts = _.filter((this.user && this.user.membershipDiscounts) || [], membershipDiscount => {
      let isAvailable = true

      if (isAvailable && membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.DAY_OF_WEEK]) {
        const dayNumber = _.first(this.futureShowings)?.dayNumber || new Date().getDay()
        const isDayOfWeekMatch = _.includes(membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.DAY_OF_WEEK], dayNumber)
        isAvailable = isAvailable && membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK] !== isDayOfWeekMatch
      }

      if (isAvailable && membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.DATE_RANGE]) {
        const time = _.first(this.futureShowings)?.time ? new Date(_.first(this.futureShowings)?.time).getTime() : new Date().getTime()
        const startDate = new Date(membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_START]).getTime()
        const isAfterStartDate = time > startDate
        isAvailable = isAvailable && membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE] !== isAfterStartDate

        if (membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END]) {
          const endDate = addToDate(new Date(membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.DATE_RANGE_END]), { days: 1 }).getTime()
          const isBeforeEndDate = time < endDate
          isAvailable = isAvailable && membershipDiscount.conditionsRequirements[CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE] !== isBeforeEndDate
        }
      }

      return isAvailable
    })

    const updatedMembershipDiscounts = _.map(availableMembershipDiscounts, membershipDiscount => {
      const discount = membershipDiscount.discount
      discount.user = this.user
      discount.membershipDiscount = membershipDiscount
      return discount
    })

    const addedMembershipDiscounts = _.differenceBy(updatedMembershipDiscounts, this.currentMembershipDiscounts.data, 'id')
    const removedMembershipDiscounts = _.differenceBy(this.currentMembershipDiscounts.data, updatedMembershipDiscounts, 'id')

    this.currentMembershipDiscounts.removeAll(removedMembershipDiscounts)
    this.currentMembershipDiscounts.addAll(addedMembershipDiscounts)
  }

  toggleUsualOrder (itemPreferences) {
    if (this.usualOrderAdded) {
      this.orderItems.filter(orderItem => {
        return orderItem.id < this.usualOrderItemIdEnd || orderItem.id > this.usualOrderItemIdStart
      })
    } else {
      this.usualOrderItemIdStart = this.nextLocalId()
      _.each(itemPreferences, itemPreference => this.addFavorite(itemPreference))
      this.usualOrderItemIdEnd = this.nextLocalId()
    }
    this.usualOrderAdded = !this.usualOrderAdded
  }

  addFavorite (favoriteItem) {
    _.times(favoriteItem.qty, () => {
      const orderItem = this.getFavoriteOrderItem(favoriteItem)

      this.orderItems.add(orderItem)
    })
  }

  getFavoriteOrderItem (favoriteItem) {
    const orderItem = new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: favoriteItem.item_id,
        itemVariationId: favoriteItem.item_variation_id,
        itemModifiers: _.map(favoriteItem.modifiers, (modifier) => {
          return new ItemOrderItemModifier({ modifierId: modifier.modifier_id, modifierSetId: modifier.modifier_set_id })
        })
      },
      note: favoriteItem.note,
      voucher: null,
      voucherType: null,
      discount: null
    })
    this.generatedOrderItems.push(orderItem)
    return orderItem
  }

  addCopy (orderItem) {
    const orderItemCopy = new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: orderItem.itemOrderItem.itemId,
        itemVariationId: orderItem.itemOrderItem.itemVariationId,
        itemModifiers: _.map(orderItem.itemOrderItem.itemModifiers.data, (itemModifier) => {
          return new ItemOrderItemModifier({ modifierId: itemModifier.modifierId, modifierSetId: itemModifier.modifierSetId })
        })
      },
      note: orderItem.note,
      hasDueTime: null,
      dueTime: null,
      voucher: null,
      voucherType: null,
      discount: null
    })

    if (orderItem.state === 'draft') {
      orderItemCopy.hasDueTime = orderItem.hasDueTime
      orderItemCopy.dueTime = orderItem.dueTime
    }

    this.orderItems.add(orderItemCopy)
  }

  // Return an order item for the passed in item
  itemOrderItemForItem (item, { itemVariation = null, modifiersAndSets = null, adminApproved = false } = {}) {
    const restrictOutOfStock = this.inventoryStrategy?.inventoryRestrictionType === SALES_ITEM_RESTRICTION_TYPES.RESTRICT

    let defaultItemVariation

    if (
      itemVariation &&
      itemVariation.isActive &&
      !itemVariation.salesDisabled &&
      (
        !restrictOutOfStock ||
        !isOutOfStock(
          itemVariation,
          { order: this }
        )
      )
    ) {
      defaultItemVariation = itemVariation
    } else {
      defaultItemVariation = _(item.activeItemVariations).filter(activeItemVariation => {
        return !activeItemVariation.salesDisabled && (
          !restrictOutOfStock ||
          !isOutOfStock(activeItemVariation, { order: this })
        )
      }).first()
    }

    if (!defaultItemVariation) {
      return null
    }

    const outOfStockModifierIds = restrictOutOfStock
      ? _(item.itemModifierSets.data)
        .map('modifierSet.activeModifiers')
        .flatten()
        .filter(modifier => isOutOfStock(modifier, { item, order: this }))
        .map('id')
        .value()
      : null

    const defaultItemModifiers = []
    const modifierSetIdsToIgnore = []

    if (modifiersAndSets) {
      _.each(modifiersAndSets, modifierAndSet => {
        const { modifier, modifierSet } = modifierAndSet
        if (!modifier.isActive) return

        if (modifier.salesDisabled) return

        if (restrictOutOfStock && _.includes(outOfStockModifierIds, modifier.id)) return

        // Find all modifier set IDs that contain this modifier
        const modifierSetIds = _(item.itemModifierSets.data)
          .filter(ims => _.some(ims.modifierSet.activeModifiers, { id: modifier.id }))
          .map('modifierSetId')
          .value()

        modifierSetIdsToIgnore.push(...modifierSetIds)
        defaultItemModifiers.push(new ItemOrderItemModifier({ modifierId: modifier.id, modifierSetId: modifierSet.id }))
      })
    }

    _(item.itemModifierSets.data)
      .filter(itemModifierSet => !_.includes(modifierSetIdsToIgnore, itemModifierSet.modifierSetId))
      .flatMap(itemModifierSet => itemModifierSet.defaultModifiers.data.map(modifier => ({ modifier, modifierSetId: itemModifierSet.modifierSetId })))
      .each(({ modifier, modifierSetId }) => {
        if (!modifier.isActive) return

        if (modifier.salesDisabled) return

        if (restrictOutOfStock && _.includes(outOfStockModifierIds, modifier.id)) return

        defaultItemModifiers.push(new ItemOrderItemModifier({ modifierId: modifier.id, modifierSetId }))
      })

    const orderItem = new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: item.id,
        ...(defaultItemVariation ? { itemVariationId: defaultItemVariation.id } : {}),
        ...(defaultItemModifiers ? { itemModifiers: defaultItemModifiers } : {}),
        adminApproved
      },
      voucher: null,
      voucherType: null,
      discount: null
    })
    this.generatedOrderItems.push(orderItem)
    return orderItem
  }

  classesForRow () {
    if (this.state === ORDER_STATES.CANCELED) {
      return 'bg-red-1 text-strike'
    } else if (this.state === ORDER_STATES.DRAFT) {
      return 'bg-grey-3 text-grey-6'
    } else if (this.state === ORDER_STATES.PENDING) {
      return 'bg-amber-2'
    } else if (this.state === ORDER_STATES.OPEN_TAB) {
      return 'bg-green-1'
    } else if (this.state === ORDER_STATES.PARTIALLY_VOIDED || this.state === ORDER_STATES.PARTIALLY_REFUNDED) {
      return 'bg-yellow-1'
    } else if (this.state === ORDER_STATES.VOIDED || this.state === ORDER_STATES.REFUNDED) {
      return 'bg-red-1'
    } else {
      return ''
    }
  }

  removeUser () {
    this.user?.destroy()
    this.user = null
    _.each(this.appliedRewards?.data || [], reward => {
      this.appliedRewards.remove(reward)
    })
    this.resetRedemptions()
    this.orderItems.filter(orderItem => {
      const ticketForSpecificUser = orderItem.ticket?.user
      const membershipItem = orderItem.type === ORDER_ITEM_TYPES.MEMBERSHIP_INTERVAL
      const dayPassItem = orderItem.type === ORDER_ITEM_TYPES.DAY_PASS
      const ticketItemIfMembershipRequired = this.configuration.currentSite.membershipRequired && orderItem.ticket

      const reject = ticketForSpecificUser || membershipItem || dayPassItem || ticketItemIfMembershipRequired

      return !reject
    })

    this.payments.filter(payment => payment.type !== 'draft' && payment.type !== 'credit' && payment.stripePaymentMethodId !== this.user?.defaultPaymentMethodId)
  }

  beforeUnmount () {
    this.updateTimeInterval && clearInterval(this.updateTimeInterval)
  }
}
